import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, ExternalLink, UserPlus, Clock, FileText, MessageSquare, Music, Calendar, ClipboardList, Printer, Edit, Trash2, Check, X, Save, Search, UserPlus2, DollarSign, CreditCard, CheckSquare, File, Upload, Eye, Download, AlertTriangle, CalendarPlus, Percent, Tag, MapPin, Send, Mail, ChevronRight, AlertCircle, LayoutGrid, List, ChevronDown, Lock, Unlock } from 'lucide-react';
import EmailComposeDialog from '@/components/EmailComposeDialog';
import CommunicationsLog from '@/components/CommunicationsLog';
import { 
  TimelineSkeleton, PlaylistsSkeleton, TasksSkeleton, PaymentsSkeleton, 
  MessagesSkeleton, ContractsSkeleton, DocumentsSkeleton, FormsSkeleton, GenericTabSkeleton 
} from '@/components/TabSkeletons';
import { 
  BookingCard, 
  EmployeeAssignmentDialog, 
  CancelBookingDialog, 
  CounterSignDialog,
  TimelineTab,
  TasksTab,
  DocumentsTab,
  MessagesTab,
  ActivityNotesTab,
  ContractsTab,
  PaymentsTab
} from '@/components/bookings';
import SeatingChartEditor from '@/components/SeatingChartEditor';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const { formatDate, formatTime, settings } = useSettings();
  
  // Get branding colors from settings
  const primaryColor = settings?.primary_color || '#06b6d4';
  const secondaryColor = settings?.secondary_color || '#8b5cf6';
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [bookingEmployees, setBookingEmployees] = useState([]);
  const [editingBooking, setEditingBooking] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [showAvailabilityWarning, setShowAvailabilityWarning] = useState(false);
  const [availabilityWarningData, setAvailabilityWarningData] = useState(null);
  const [initialEventTab, setInitialEventTab] = useState('timeline');
  const [bookingsTab, setBookingsTab] = useState('upcoming');
  const [viewMode, setViewMode] = useState('compact');
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  const [loadedTabs, setLoadedTabs] = useState({}); // Track which tabs have been loaded
  const [loadingTab, setLoadingTab] = useState(null); // Currently loading tab
  
  // Cancellation dialog state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancellingBooking, setCancellingBooking] = useState(null);
  const [cancellationReason, setCancellationReason] = useState('');
  
  // Email dialog state
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState(null);
  const [communicationsRefresh, setCommunicationsRefresh] = useState(0);
  
  // Bookings search state
  const [searchQuery, setSearchQuery] = useState('');
  
  // Client search state
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [newClientData, setNewClientData] = useState({ name: '', email: '', phone: '' });
  const clientSearchRef = useRef(null);
  
  // Venue search state
  const [venues, setVenues] = useState([]);
  const [venueSearchQuery, setVenueSearchQuery] = useState('');
  const [showVenueDropdown, setShowVenueDropdown] = useState(false);
  const [showAddVenueDialog, setShowAddVenueDialog] = useState(false);
  const [newVenueData, setNewVenueData] = useState({ 
    name: '', 
    address: '', 
    city: '', 
    state: '',
    zip_code: '',
    venue_type: 'Other',
    capacity: '',
    contact_name: '',
    contact_phone: '',
    contact_email: '',
    parking_info: '',
    setup_notes: '',
    restrictions: '',
    image: ''
  });
  const [venueImagePreview, setVenueImagePreview] = useState('');
  const venueSearchRef = useRef(null);
  
  // Event details management state
  const [showEventDetailsDialog, setShowEventDetailsDialog] = useState(false);
  const [eventDetailsBooking, setEventDetailsBooking] = useState(null);
  const [eventPlaylists, setEventPlaylists] = useState([]);
  const [missingSongs, setMissingSongs] = useState({}); // Track songs not in DJ's library
  const [eventTimeline, setEventTimeline] = useState(null);
  const [eventForms, setEventForms] = useState([]);
  const [eventFormResponses, setEventFormResponses] = useState([]);
  const [eventTasks, setEventTasks] = useState([]);
  const [eventDocuments, setEventDocuments] = useState([]);
  const [eventContracts, setEventContracts] = useState([]);
  const [loadingEventDetails, setLoadingEventDetails] = useState(false);
  const [lockoutOverrides, setLockoutOverrides] = useState({
    forms_locked_override: null,
    timeline_locked_override: null,
    playlists_locked_override: null
  });
  
  // Form removal confirmation state
  const [showRemoveFormDialog, setShowRemoveFormDialog] = useState(false);
  const [formToRemove, setFormToRemove] = useState(null);
  const [formHasData, setFormHasData] = useState(false);
  
  // Contract signing state
  const [showCounterSignDialog, setShowCounterSignDialog] = useState(false);
  const [selectedContractForSigning, setSelectedContractForSigning] = useState(null);
  const [counterSignerName, setCounterSignerName] = useState('');
  const [counterSigning, setCounterSigning] = useState(false);
  
  // Playlist editing state
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [showPlaylistDialog, setShowPlaylistDialog] = useState(false);
  const [playlistName, setPlaylistName] = useState('');
  const [playlistSongs, setPlaylistSongs] = useState([{ title: '', artist: '', designation: 'must_play', comment: '' }]);
  
  // Add Song modal state
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [newSong, setNewSong] = useState({ title: '', artist: '', designation: '', comment: '' });
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [modalSearchResults, setModalSearchResults] = useState([]);
  const [modalSearching, setModalSearching] = useState(false);
  const [showModalSearchResults, setShowModalSearchResults] = useState(false);
  const [spotifyConfigured, setSpotifyConfigured] = useState(false);
  const [searchSource, setSearchSource] = useState('musicbrainz'); // 'spotify' or 'musicbrainz'
  
  // Spotify import state
  const [showSpotifyDialog, setShowSpotifyDialog] = useState(false);
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [spotifyPreview, setSpotifyPreview] = useState(null);
  const [spotifyImportMode, setSpotifyImportMode] = useState('new'); // 'new' or 'existing'
  const [selectedPlaylistForSpotify, setSelectedPlaylistForSpotify] = useState('');
  const [importingSpotify, setImportingSpotify] = useState(false);
  
  // PDF import state
  const [showPdfImportDialog, setShowPdfImportDialog] = useState(false);
  const [pdfImportResult, setPdfImportResult] = useState(null);
  const [importingPdf, setImportingPdf] = useState(false);
  const pdfInputRef = useRef(null);
  
  // Timeline editing state
  const [showTimelineDialog, setShowTimelineDialog] = useState(false);
  const [editingTimelineEvent, setEditingTimelineEvent] = useState(null);
  const [timelineEventData, setTimelineEventData] = useState({ time: '', title: '', description: '' });
  
  // Expenses/Payouts state (private - not visible to clients)
  const [eventExpenses, setEventExpenses] = useState([]);
  const [expenseSummary, setExpenseSummary] = useState({ total_expenses: 0, total_revenue: 0, profit: 0 });
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [expenseData, setExpenseData] = useState({ category: 'Employee Pay', description: '', amount: '', date: '' });
  const EXPENSE_CATEGORIES = ['Employee Pay', 'Equipment Rental', 'Travel', 'Subcontractor', 'Supplies', 'Other'];
  
  // Manual Forms state
  const [allForms, setAllForms] = useState([]);
  const [showAddFormDialog, setShowAddFormDialog] = useState(false);
  const [selectedFormToAdd, setSelectedFormToAdd] = useState('');
  
  // Payment management state
  const [eventPayments, setEventPayments] = useState([]);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [newPayment, setNewPayment] = useState({
    amount: '',
    payment_method: 'Cash',
    payment_date: new Date().toISOString().split('T')[0],
    notes: '',
    reference_number: ''
  });
  
  // Communications state
  const [eventMessages, setEventMessages] = useState([]);
  
  // Add-on requests state
  const [addonRequests, setAddonRequests] = useState([]);
  const [processingAddonRequest, setProcessingAddonRequest] = useState(null);
  const [addonApprovalPrice, setAddonApprovalPrice] = useState('');
  const [addonApprovalNotes, setAddonApprovalNotes] = useState('');
  
  // Venue change requests state
  const [venueChangeRequests, setVenueChangeRequests] = useState([]);
  const [processingVenueChange, setProcessingVenueChange] = useState(null);
  const [venueChangeNotes, setVenueChangeNotes] = useState('');
  
  // Form editing state
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [editingForm, setEditingForm] = useState(null);
  const [formResponses, setFormResponses] = useState({});
  const [savingForm, setSavingForm] = useState(false);
  
  // Document state
  const [showDocUploadDialog, setShowDocUploadDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docNote, setDocNote] = useState('');
  const [selectedDocFile, setSelectedDocFile] = useState(null);
  const docFileInputRef = useRef(null);
  
  // Activity Notes state
  const [eventActivityLog, setEventActivityLog] = useState([]);
  
  // Contract generation state
  const [contractTemplates, setContractTemplates] = useState([]);
  const [showGenerateContractDialog, setShowGenerateContractDialog] = useState(false);
  const [selectedContractTemplate, setSelectedContractTemplate] = useState('');
  const [generatingContract, setGeneratingContract] = useState(false);
  
  const printRef = useRef(null);
  
  const [newBooking, setNewBooking] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    event_date: '',
    start_time: '',
    end_time: '',
    event_type: '',
    venue: '',
    guest_count: '',
    package: '',
    package_id: '',
    selected_addons: [],
    total_amount: '',
    discount_amount: 0,
    applied_discounts: [],
    retainer_amount: '',
    retainer_due_date: '',
    payment_schedule: [], // Array of { amount: '', due_date: '', description: '' }
    notes: ''
  });
  
  // Discounts state
  const [discounts, setDiscounts] = useState([]);
  const [selectedDiscounts, setSelectedDiscounts] = useState([]);

  // Invoice Preview state
  const [showInvoicePreview, setShowInvoicePreview] = useState(false);
  const [invoicePreviewData, setInvoicePreviewData] = useState(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [editableInvoice, setEditableInvoice] = useState({
    description: '',
    notes: '',
    line_items: [],
    due_date: ''
  });

  // Event Invoices state (for Manage Event dialog)
  const [eventInvoices, setEventInvoices] = useState([]);
  const [selectedEventInvoice, setSelectedEventInvoice] = useState(null);
  const [showEditInvoiceDialog, setShowEditInvoiceDialog] = useState(false);
  const [showEmailInvoiceDialog, setShowEmailInvoiceDialog] = useState(false);
  const [invoiceEmailAddress, setInvoiceEmailAddress] = useState('');
  const [sendingInvoiceEmail, setSendingInvoiceEmail] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(event.target)) {
        setShowClientDropdown(false);
      }
      if (venueSearchRef.current && !venueSearchRef.current.contains(event.target)) {
        setShowVenueDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchBookings();
    fetchEmployees();
    fetchPackages();
    fetchAddons();
    fetchDiscounts();
    fetchEventTypes();
    fetchPaymentTypes();
    fetchVenues();
    fetchContractTemplates();
    checkSpotifyStatus();
  }, []);

  // Check if Spotify is configured
  const checkSpotifyStatus = async () => {
    try {
      const response = await axios.get(`${API}/spotify/status`);
      setSpotifyConfigured(response.data.configured);
      if (response.data.configured) {
        setSearchSource('spotify');
      }
    } catch {
      setSpotifyConfigured(false);
    }
  };

  // Handle URL parameters to auto-open a specific booking's dialog
  useEffect(() => {
    const openBookingId = searchParams.get('open') || searchParams.get('booking');
    const targetTab = searchParams.get('tab');
    
    if (openBookingId && bookings.length > 0 && !showEventDetailsDialog) {
      const bookingToOpen = bookings.find(b => b.id === openBookingId);
      if (bookingToOpen) {
        // Open the event details dialog with the specified tab
        openEventDetailsDialog(bookingToOpen, targetTab || 'timeline');
        // Clear the URL parameters to prevent re-opening on refresh
        setSearchParams({});
      }
    }
  }, [bookings, searchParams]);

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      console.error('Failed to fetch packages:', error);
    }
  };

  const fetchAddons = async () => {
    try {
      const response = await axios.get(`${API}/addons`);
      setAddons(response.data);
    } catch (error) {
      console.error('Failed to fetch add-ons:', error);
    }
  };

  const fetchDiscounts = async () => {
    try {
      const response = await axios.get(`${API}/discounts`);
      setDiscounts(response.data.filter(d => d.is_active));
    } catch (error) {
      console.error('Failed to fetch discounts:', error);
    }
  };

  const fetchContractTemplates = async () => {
    try {
      const response = await axios.get(`${API}/contract-templates`);
      setContractTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch contract templates:', error);
    }
  };

  const handleGenerateContract = async () => {
    if (!selectedContractTemplate) {
      toast.error('Please select a contract template');
      return;
    }
    
    setGeneratingContract(true);
    try {
      await axios.post(`${API}/contracts`, {
        booking_id: eventDetailsBooking.id,
        template_id: selectedContractTemplate
      });
      toast.success('Contract generated successfully');
      setShowGenerateContractDialog(false);
      setSelectedContractTemplate('');
      // Refresh contracts list
      const contractsRes = await axios.get(`${API}/contracts/booking/${eventDetailsBooking.id}`);
      setEventContracts(contractsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate contract');
    } finally {
      setGeneratingContract(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await axios.get(`${API}/event-types`);
      // Sort by sort_order to match Event Types settings
      const sorted = response.data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      setEventTypes(sorted);
      // Set default event type if available
      if (sorted.length > 0 && !newBooking.event_type) {
        setNewBooking(prev => ({ ...prev, event_type: sorted[0].name }));
      }
    } catch (error) {
      console.error('Failed to fetch event types:', error);
    }
  };

  const fetchPaymentTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      const types = response.data.payment_types || ['Cash', 'Check', 'Credit Card', 'Venmo', 'PayPal', 'Zelle', 'Bank Transfer'];
      setPaymentTypes(types);
      if (types.length > 0) {
        setNewPayment(prev => ({ ...prev, payment_method: types[0] }));
      }
      // Store payment processor settings
      setPaymentSettings({
        processor: response.data.payment_processor,
        stripe_configured: !!response.data.stripe_secret_key,
        square_configured: !!(response.data.square_access_token && response.data.square_location_id)
      });
    } catch (error) {
      console.error('Failed to fetch payment types:', error);
      setPaymentTypes(['Cash', 'Check', 'Credit Card', 'Venmo', 'PayPal', 'Zelle', 'Bank Transfer']);
    }
  };

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venues`);
      setVenues(response.data);
    } catch (error) {
      console.error('Failed to fetch venues:', error);
    }
  };

  // Client search functionality
  const searchClients = async (query) => {
    if (query.length < 2) {
      setClientSearchResults([]);
      setShowClientDropdown(false);
      return;
    }
    
    setIsSearching(true);
    try {
      const response = await axios.get(`${API}/clients/search?q=${encodeURIComponent(query)}`);
      setClientSearchResults(response.data);
      setShowClientDropdown(true);
    } catch (error) {
      console.error('Failed to search clients:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (clientSearchQuery) {
        searchClients(clientSearchQuery);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [clientSearchQuery]);

  const handleClientSearchChange = (e) => {
    const value = e.target.value;
    setClientSearchQuery(value);
    setNewBooking(prev => ({ ...prev, client_name: value }));
    if (!value) {
      setShowClientDropdown(false);
      setClientSearchResults([]);
    }
  };

  const selectClient = (client) => {
    setNewBooking(prev => ({
      ...prev,
      client_name: client.name,
      client_email: client.email || '',
      client_phone: client.phone || ''
    }));
    setClientSearchQuery(client.name);
    setShowClientDropdown(false);
    toast.success(`Selected client: ${client.name}`);
  };

  const openAddClientDialog = () => {
    setNewClientData({
      name: clientSearchQuery,
      email: '',
      phone: ''
    });
    setShowAddClientDialog(true);
    setShowClientDropdown(false);
  };

  const saveNewClient = async () => {
    if (!newClientData.name.trim() || !newClientData.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    
    try {
      const response = await axios.post(`${API}/clients`, newClientData);
      toast.success('New client added!');
      
      // Auto-select the new client
      setNewBooking(prev => ({
        ...prev,
        client_name: newClientData.name,
        client_email: newClientData.email,
        client_phone: newClientData.phone || ''
      }));
      setClientSearchQuery(newClientData.name);
      setShowAddClientDialog(false);
    } catch (error) {
      toast.error('Failed to create client');
    }
  };

  // Venue search functionality
  const handleVenueSearchChange = (e) => {
    const value = e.target.value;
    setVenueSearchQuery(value);
    setNewBooking(prev => ({ ...prev, venue: value }));
    setShowVenueDropdown(value.length > 0);
  };

  const selectVenue = (venue) => {
    setNewBooking(prev => ({
      ...prev,
      venue: venue.name
    }));
    setVenueSearchQuery(venue.name);
    setShowVenueDropdown(false);
    toast.success(`Selected venue: ${venue.name}`);
  };

  const openAddVenueDialog = () => {
    setNewVenueData({
      name: venueSearchQuery,
      address: '',
      city: '',
      state: '',
      zip_code: '',
      venue_type: 'Other',
      capacity: '',
      contact_name: '',
      contact_phone: '',
      contact_email: '',
      parking_info: '',
      setup_notes: '',
      restrictions: '',
      image: ''
    });
    setVenueImagePreview('');
    setShowAddVenueDialog(true);
    setShowVenueDropdown(false);
  };

  const handleVenueImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be less than 2MB');
      return;
    }
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setVenueImagePreview(reader.result);
      setNewVenueData(prev => ({ ...prev, image: reader.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeVenueImage = () => {
    setVenueImagePreview('');
    setNewVenueData(prev => ({ ...prev, image: '' }));
  };

  const saveNewVenue = async () => {
    if (!newVenueData.name.trim()) {
      toast.error('Venue name is required');
      return;
    }
    
    try {
      const venuePayload = {
        name: newVenueData.name,
        venue_type: newVenueData.venue_type || 'Other',
        address: newVenueData.address || null,
        city: newVenueData.city || null,
        state: newVenueData.state || null,
        zip_code: newVenueData.zip_code || null,
        contact_name: newVenueData.contact_name || null,
        contact_phone: newVenueData.contact_phone || null,
        contact_email: newVenueData.contact_email || null,
        capacity: newVenueData.capacity ? parseInt(newVenueData.capacity) : null,
        parking_info: newVenueData.parking_info || null,
        setup_notes: newVenueData.setup_notes || null,
        restrictions: newVenueData.restrictions || null,
        image: newVenueData.image || null
      };
      
      await axios.post(`${API}/venues`, venuePayload);
      toast.success('New venue added!');
      
      // Auto-select the new venue
      setNewBooking(prev => ({
        ...prev,
        venue: newVenueData.name
      }));
      setVenueSearchQuery(newVenueData.name);
      setShowAddVenueDialog(false);
      fetchVenues(); // Refresh venues list
    } catch (error) {
      toast.error('Failed to create venue');
    }
  };

  // Get filtered venues based on search
  const filteredVenues = venues.filter(v => 
    v.name.toLowerCase().includes(venueSearchQuery.toLowerCase())
  );

  const handlePackageSelect = (packageId) => {
    const selectedPkg = packages.find(p => p.id === packageId);
    if (selectedPkg) {
      setNewBooking({
        ...newBooking,
        package_id: packageId,
        package: selectedPkg.name,
        total_amount: selectedPkg.base_price,
        selected_addons: [],
      });
    }
  };

  const handleAddonToggle = (addonId) => {
    const selected = newBooking.selected_addons.includes(addonId)
      ? newBooking.selected_addons.filter(id => id !== addonId)
      : [...newBooking.selected_addons, addonId];
    
    const selectedPkg = packages.find(p => p.id === newBooking.package_id);
    const basePrice = selectedPkg ? selectedPkg.base_price : 0;
    const addonsTotal = addons
      .filter(addon => selected.includes(addon.id))
      .reduce((sum, addon) => sum + addon.price, 0);
    
    setNewBooking({
      ...newBooking,
      selected_addons: selected,
      total_amount: basePrice + addonsTotal,
    });
  };

  const resetBookingForm = () => {
    setNewBooking({
      client_name: '',
      client_email: '',
      client_phone: '',
      event_date: '',
      start_time: '',
      end_time: '',
      event_type: eventTypes.length > 0 ? eventTypes[0].name : 'wedding',
      venue: '',
      guest_count: '',
      package: '',
      package_id: '',
      selected_addons: [],
      total_amount: '',
      retainer_amount: '',
      retainer_due_date: '',
      payment_schedule: [],
      notes: ''
    });
    setClientSearchQuery('');
    setClientSearchResults([]);
    setEditingBooking(null);
  };

  const openEditBookingDialog = (booking) => {
    setEditingBooking(booking);
    // Calculate the original amount (before discounts)
    // If original_amount exists, use it. Otherwise, calculate it from total + discounts
    let baseAmount = booking.original_amount;
    if (!baseAmount && booking.applied_discounts?.length > 0) {
      // Calculate what the original amount was by adding back all discount amounts
      const totalDiscounts = booking.applied_discounts.reduce((sum, d) => sum + (d.amount || 0), 0);
      baseAmount = (booking.total_amount || 0) + totalDiscounts;
    } else if (!baseAmount) {
      baseAmount = booking.total_amount || '';
    }
    
    setNewBooking({
      client_name: booking.client_name || '',
      client_email: booking.client_email || '',
      client_phone: booking.client_phone || '',
      event_date: booking.event_date || '',
      start_time: booking.start_time || '',
      end_time: booking.end_time || '',
      event_type: booking.event_type || '',
      venue: booking.venue || '',
      guest_count: booking.guest_count || '',
      package: booking.package || '',
      package_id: booking.package_id || '',
      selected_addons: booking.selected_addons || [],
      total_amount: baseAmount,
      retainer_amount: booking.retainer_amount || booking.deposit_amount || '',
      retainer_due_date: booking.retainer_due_date || '',
      payment_schedule: booking.payment_schedule || [],
      notes: booking.notes || ''
    });
    // Store the calculated original amount in editingBooking for handleCreate to use
    setEditingBooking({ ...booking, original_amount: baseAmount });
    setSelectedDiscounts(booking.applied_discounts || []);
    setClientSearchQuery(booking.client_name || '');
    setShowDialog(true);
  };

  const checkDateAvailability = async (date, excludeBookingId = null) => {
    try {
      const response = await axios.post(`${API}/availability/check`, {
        date: date,
        exclude_booking_id: excludeBookingId
      });
      return response.data;
    } catch (error) {
      console.error('Failed to check availability:', error);
      return { available: true };
    }
  };

  const handleCreate = async (forceCreate = false) => {
    try {
      // For editing, use the original amount stored in the booking, not the discounted total
      const subtotal = editingBooking 
        ? (editingBooking.original_amount || parseFloat(newBooking.total_amount) || 0)
        : (newBooking.total_amount ? parseFloat(newBooking.total_amount) : 0);
      
      // Recalculate discounts with cascading logic (each discount applies to already-discounted amount)
      let runningTotal = subtotal;
      const recalculatedDiscounts = selectedDiscounts.map(d => {
        let discountAmount = 0;
        if (d.type === 'percentage' || d.discount_type === 'percentage') {
          // Percentage discount applies to current running total
          discountAmount = (runningTotal * (d.value || 0)) / 100;
        } else {
          // Fixed amount discount
          discountAmount = d.value || d.amount || 0;
        }
        runningTotal = Math.max(0, runningTotal - discountAmount);
        return {
          ...d,
          amount: discountAmount
        };
      });
      
      const totalDiscountAmount = recalculatedDiscounts.reduce((sum, d) => sum + d.amount, 0);
      const finalTotal = Math.max(0, subtotal - totalDiscountAmount);
      
      // Convert numeric fields properly
      const bookingData = { 
        ...newBooking,
        guest_count: newBooking.guest_count ? parseInt(newBooking.guest_count) : null,
        total_amount: finalTotal,
        original_amount: subtotal, // Store original amount before discounts
        discount_amount: totalDiscountAmount,
        applied_discounts: recalculatedDiscounts,
        retainer_amount: newBooking.retainer_amount ? parseFloat(newBooking.retainer_amount) : 0,
        deposit_amount: newBooking.retainer_amount ? parseFloat(newBooking.retainer_amount) : 0, // Keep deposit_amount for backward compatibility
        retainer_due_date: newBooking.retainer_due_date || null,
        payment_schedule: newBooking.payment_schedule || []
      };
      
      // Check date availability if date changed (unless forcing creation)
      if (!forceCreate) {
        if (editingBooking && bookingData.event_date && bookingData.event_date !== editingBooking.event_date) {
          setCheckingAvailability(true);
          const availabilityResult = await checkDateAvailability(bookingData.event_date, editingBooking.id);
          setCheckingAvailability(false);
          
          if (!availabilityResult.available) {
            setAvailabilityWarningData({
              date: bookingData.event_date,
              reason: availabilityResult.reason,
              isEdit: true
            });
            setShowAvailabilityWarning(true);
            return;
          }
        } else if (!editingBooking && bookingData.event_date) {
          // Check for new bookings too
          setCheckingAvailability(true);
          const availabilityResult = await checkDateAvailability(bookingData.event_date);
          setCheckingAvailability(false);
          
          if (!availabilityResult.available) {
            setAvailabilityWarningData({
              date: bookingData.event_date,
              reason: availabilityResult.reason,
              isEdit: false
            });
            setShowAvailabilityWarning(true);
            return;
          }
        }
      }
      
      if (editingBooking) {
        // Update existing booking
        await axios.put(`${API}/bookings/${editingBooking.id}`, bookingData);
        toast.success('Booking updated successfully');
        
        // If booking has a lead_id and date changed, update the lead too
        if (editingBooking.lead_id && bookingData.event_date !== editingBooking.event_date) {
          try {
            await axios.put(`${API}/leads/${editingBooking.lead_id}`, { event_date: bookingData.event_date });
            toast.success('Associated lead date also updated');
          } catch (error) {
            console.error('Failed to update lead date:', error);
          }
        }
      } else {
        // Create new booking
        const response = await axios.post(`${API}/bookings`, bookingData);
        toast.success('Booking created successfully');
        
        // Check if auto-applied discounts were applied
        const createdBooking = response.data;
        if (createdBooking.applied_discounts && createdBooking.applied_discounts.length > 0) {
          const autoDiscounts = createdBooking.applied_discounts.filter(d => d.auto_applied);
          if (autoDiscounts.length > 0) {
            const discountNames = autoDiscounts.map(d => d.name).join(', ');
            const totalSaved = autoDiscounts.reduce((sum, d) => sum + (d.amount || 0), 0);
            toast.success(`Auto-applied discount${autoDiscounts.length > 1 ? 's' : ''}: ${discountNames} (-$${totalSaved.toFixed(2)})`, {
              duration: 5000
            });
          }
        }
      }
      
      setShowDialog(false);
      resetBookingForm();
      fetchBookings();
      setSelectedDiscounts([]);
    } catch (error) {
      toast.error(editingBooking ? 'Failed to update booking' : 'Failed to create booking');
    }
  };

  const getPortalUrl = (token) => {
    if (settings?.custom_domain) {
      return `https://${settings.custom_domain}/portal/${token}`;
    }
    return `${window.location.origin}/portal/${token}`;
  };

  const copyPortalLink = (token) => {
    navigator.clipboard.writeText(getPortalUrl(token));
    toast.success('Portal link copied to clipboard!');
  };

  const handleStatusChange = async (bookingId, newStatus, booking = null) => {
    // If cancelling, show confirmation dialog
    if (newStatus === 'cancelled') {
      setCancellingBooking(booking || bookings.find(b => b.id === bookingId));
      setCancellationReason('');
      setShowCancelDialog(true);
      return;
    }
    
    try {
      await axios.put(`${API}/bookings/${bookingId}/status`, { status: newStatus });
      const statusLabel = newStatus === 'confirmed' ? 'booked' : newStatus === 'completed' ? 'marked as past' : 'updated';
      toast.success(`Event ${statusLabel} successfully`);
      fetchBookings();
      // Update the eventDetailsBooking if it's the same booking
      if (eventDetailsBooking?.id === bookingId) {
        setEventDetailsBooking(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update event status');
    }
  };

  const confirmCancellation = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Please provide a reason for cancellation');
      return;
    }
    
    try {
      await axios.put(`${API}/bookings/${cancellingBooking.id}/status`, { 
        status: 'cancelled',
        cancellation_reason: cancellationReason.trim()
      });
      toast.success('Event cancelled successfully');
      setShowCancelDialog(false);
      // Update the eventDetailsBooking if it's the same booking
      if (eventDetailsBooking?.id === cancellingBooking.id) {
        setEventDetailsBooking(prev => ({ 
          ...prev, 
          status: 'cancelled',
          cancellation_reason: cancellationReason.trim()
        }));
      }
      setCancellingBooking(null);
      setCancellationReason('');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to cancel event');
    }
  };

  // Discount management functions for event details dialog
  const addDiscountToBooking = async (discount) => {
    if (!eventDetailsBooking) return;
    
    const currentDiscounts = eventDetailsBooking.applied_discounts || [];
    if (currentDiscounts.find(d => d.id === discount.id)) {
      toast.error('This discount is already applied');
      return;
    }
    
    // Calculate discount amount using cascading logic
    // Start with original amount and apply all existing discounts to get running total
    const originalAmount = eventDetailsBooking.original_amount || eventDetailsBooking.total_amount || 0;
    let runningTotal = originalAmount;
    
    // Apply all existing discounts first to get current running total
    currentDiscounts.forEach(d => {
      if (d.discount_type === 'percentage') {
        runningTotal -= (runningTotal * (d.value || 0)) / 100;
      } else {
        runningTotal -= (d.value || d.amount || 0);
      }
      runningTotal = Math.max(0, runningTotal);
    });
    
    // Now calculate this discount against the running total
    let discountAmount = 0;
    if (discount.discount_type === 'percentage') {
      discountAmount = (runningTotal * discount.value) / 100;
    } else {
      discountAmount = discount.value;
    }
    
    const newDiscount = {
      id: discount.id,
      name: discount.name,
      discount_type: discount.discount_type,
      value: discount.value,
      amount: discountAmount,
      tags: discount.tags || [],
      auto_applied: false
    };
    
    const updatedDiscounts = [...currentDiscounts, newDiscount];
    
    // Recalculate total with cascading discounts
    runningTotal = originalAmount;
    updatedDiscounts.forEach(d => {
      if (d.discount_type === 'percentage') {
        runningTotal -= (runningTotal * (d.value || 0)) / 100;
      } else {
        runningTotal -= (d.value || d.amount || 0);
      }
      runningTotal = Math.max(0, runningTotal);
    });
    const newTotal = runningTotal;
    
    try {
      await axios.put(`${API}/bookings/${eventDetailsBooking.id}/discounts`, {
        applied_discounts: updatedDiscounts,
        total_amount: newTotal,
        original_amount: originalAmount
      });
      
      // Update local state
      setEventDetailsBooking(prev => ({
        ...prev,
        applied_discounts: updatedDiscounts,
        total_amount: newTotal,
        original_amount: originalAmount
      }));
      
      toast.success(`Discount "${discount.name}" applied`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to apply discount');
    }
  };

  const removeDiscountFromBooking = async (discountId) => {
    if (!eventDetailsBooking) return;
    
    const currentDiscounts = eventDetailsBooking.applied_discounts || [];
    const discountToRemove = currentDiscounts.find(d => d.id === discountId);
    if (!discountToRemove) return;
    
    const updatedDiscounts = currentDiscounts.filter(d => d.id !== discountId);
    const originalAmount = eventDetailsBooking.original_amount || eventDetailsBooking.total_amount + discountToRemove.amount;
    
    // Recalculate with cascading discounts
    let runningTotal = originalAmount;
    const recalculatedDiscounts = updatedDiscounts.map(d => {
      let discountAmount = 0;
      if (d.discount_type === 'percentage') {
        discountAmount = (runningTotal * (d.value || 0)) / 100;
      } else {
        discountAmount = d.value || d.amount || 0;
      }
      runningTotal = Math.max(0, runningTotal - discountAmount);
      return { ...d, amount: discountAmount };
    });
    const newTotal = runningTotal;
    
    try {
      await axios.put(`${API}/bookings/${eventDetailsBooking.id}/discounts`, {
        applied_discounts: recalculatedDiscounts,
        total_amount: newTotal,
        original_amount: originalAmount
      });
      
      // Update local state
      setEventDetailsBooking(prev => ({
        ...prev,
        applied_discounts: recalculatedDiscounts,
        total_amount: newTotal,
        original_amount: originalAmount
      }));
      
      toast.success(`Discount "${discountToRemove.name}" removed`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to remove discount');
    }
  };

  const openEmployeeDialog = async (booking) => {
    setSelectedBooking(booking);
    try {
      const response = await axios.get(`${API}/bookings/${booking.id}/employees`);
      setBookingEmployees(response.data);
      setShowEmployeeDialog(true);
    } catch (error) {
      toast.error('Failed to load employees');
    }
  };

  const handleAssignEmployee = async (employeeId) => {
    if (!selectedBooking) return;
    
    try {
      await axios.post(`${API}/bookings/${selectedBooking.id}/assign-employee?employee_id=${employeeId}`);
      toast.success('Employee assigned');
      const response = await axios.get(`${API}/bookings/${selectedBooking.id}/employees`);
      setBookingEmployees(response.data);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to assign employee');
    }
  };

  const handleUnassignEmployee = async (employeeId) => {
    if (!selectedBooking) return;
    
    try {
      await axios.delete(`${API}/bookings/${selectedBooking.id}/unassign-employee/${employeeId}`);
      toast.success('Employee unassigned');
      const response = await axios.get(`${API}/bookings/${selectedBooking.id}/employees`);
      setBookingEmployees(response.data);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to unassign employee');
    }
  };

  const isEmployeeAssigned = (employeeId) => {
    return bookingEmployees.some(e => e.id === employeeId);
  };

  const handleDeleteBooking = async (booking) => {
    if (!window.confirm(`Are you sure you want to delete the booking for ${booking.client_name}? This will also delete all associated contracts, invoices, payments, playlists, and other data. This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/bookings/${booking.id}`);
      toast.success('Booking deleted successfully');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete booking');
    }
  };

  // Calendar Export Functions
  const exportBookingToCalendar = async (booking) => {
    try {
      const response = await axios.get(`${API}/bookings/${booking.id}/calendar`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `booking_${booking.client_name.replace(/\s+/g, '_')}_${booking.event_date}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Calendar event downloaded');
    } catch (error) {
      toast.error('Failed to export to calendar');
    }
  };

  const exportAllBookingsToCalendar = async () => {
    try {
      let url = `${API}/bookings/export/calendar`;
      const params = new URLSearchParams();
      
      if (activeTab !== 'all') {
        params.append('status', activeTab);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url, {
        responseType: 'blob'
      });
      
      const downloadUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `dj_crm_bookings_${new Date().toISOString().split('T')[0]}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success('All bookings exported to calendar');
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'No bookings to export' : 'Failed to export bookings');
    }
  };

  const handleGenerateInvoice = async (booking) => {
    try {
      // Fetch preview data instead of directly generating
      const response = await axios.post(`${API}/invoices/preview-from-booking/${booking.id}`);
      const { preview, template } = response.data;
      
      setInvoicePreviewData(preview);
      setInvoiceTemplate(template);
      setEditableInvoice({
        booking_id: booking.id,
        description: preview.description,
        notes: preview.notes || '',
        line_items: preview.line_items,
        due_date: preview.due_date,
        amount: preview.amount,
        template_id: template?.id || null
      });
      setShowInvoicePreview(true);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Booking not found');
      } else {
        toast.error('Failed to load invoice preview');
      }
    }
  };

  const handleSendInvoice = async () => {
    try {
      const response = await axios.post(`${API}/invoices/create-with-customization`, {
        booking_id: editableInvoice.booking_id,
        amount: editableInvoice.amount,
        due_date: editableInvoice.due_date,
        description: editableInvoice.description,
        line_items: editableInvoice.line_items,
        notes: editableInvoice.notes,
        template_id: editableInvoice.template_id,
        custom_content: editableInvoice.notes ? `Notes: ${editableInvoice.notes}` : null
      });
      
      if (response.data.success) {
        toast.success(`Invoice ${response.data.invoice.invoice_number} created successfully!`);
        setShowInvoicePreview(false);
        setInvoicePreviewData(null);
      }
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const updateLineItem = (index, field, value) => {
    setEditableInvoice(prev => {
      const newItems = [...prev.line_items];
      newItems[index] = { ...newItems[index], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
      const newAmount = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      return { ...prev, line_items: newItems, amount: newAmount };
    });
  };

  const addLineItem = () => {
    setEditableInvoice(prev => ({
      ...prev,
      line_items: [...prev.line_items, { description: '', amount: 0, type: 'custom' }]
    }));
  };

  const removeLineItem = (index) => {
    setEditableInvoice(prev => {
      const newItems = prev.line_items.filter((_, i) => i !== index);
      const newAmount = newItems.reduce((sum, item) => sum + (item.amount || 0), 0);
      return { ...prev, line_items: newItems, amount: newAmount };
    });
  };

  // Event Invoice Management Functions (for Manage Event dialog)
  const openEditEventInvoice = (invoice) => {
    setSelectedEventInvoice(invoice);
    setEditableInvoice({
      id: invoice.id,
      booking_id: invoice.booking_id,
      description: invoice.description || '',
      notes: invoice.notes || '',
      line_items: invoice.line_items || [],
      due_date: invoice.due_date || '',
      amount: invoice.amount || 0,
      invoice_number: invoice.invoice_number
    });
    setShowEditInvoiceDialog(true);
  };

  const saveEventInvoice = async () => {
    try {
      await axios.put(`${API}/invoices/${editableInvoice.id}`, {
        description: editableInvoice.description,
        notes: editableInvoice.notes,
        line_items: editableInvoice.line_items,
        due_date: editableInvoice.due_date,
        amount: editableInvoice.amount
      });
      
      toast.success('Invoice updated successfully');
      setShowEditInvoiceDialog(false);
      
      // Refresh invoices
      const invoicesRes = await axios.get(`${API}/invoices/booking/${eventDetailsBooking.id}`);
      setEventInvoices(invoicesRes.data || []);
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const openEmailEventInvoice = (invoice) => {
    setSelectedEventInvoice(invoice);
    setInvoiceEmailAddress(eventDetailsBooking?.client_email || '');
    setShowEmailInvoiceDialog(true);
  };

  const sendEventInvoiceEmail = async () => {
    if (!invoiceEmailAddress.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    
    setSendingInvoiceEmail(true);
    try {
      await axios.post(`${API}/invoices/${selectedEventInvoice.id}/email`, {
        email: invoiceEmailAddress
      });
      toast.success(`Invoice will be sent to ${invoiceEmailAddress}`);
      setShowEmailInvoiceDialog(false);
    } catch (error) {
      toast.error('Failed to send invoice email');
    } finally {
      setSendingInvoiceEmail(false);
    }
  };

  const downloadEventInvoicePdf = async (invoice) => {
    try {
      const response = await axios.get(`${API}/invoices/${invoice.id}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Invoice downloaded!');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  // Contract counter-signing functions
  const openCounterSignDialog = (contract) => {
    setSelectedContractForSigning(contract);
    setCounterSignerName(user?.name || user?.email || '');
    setShowCounterSignDialog(true);
  };

  const handleCounterSign = async () => {
    if (!counterSignerName.trim()) {
      toast.error('Please enter your name to sign');
      return;
    }
    
    setCounterSigning(true);
    try {
      await axios.put(`${API}/contracts/${selectedContractForSigning.id}/counter-sign`, {
        signer_name: counterSignerName.trim()
      });
      toast.success('Contract counter-signed successfully!');
      setShowCounterSignDialog(false);
      
      // Refresh contracts
      const contractsRes = await axios.get(`${API}/contracts/booking/${eventDetailsBooking.id}`);
      setEventContracts(contractsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to counter-sign contract');
    } finally {
      setCounterSigning(false);
    }
  };

  const downloadContractPdf = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.client_name || contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Contract downloaded!');
    } catch {
      toast.error('Failed to download contract');
    }
  };

  const handleDeleteContract = async (contract) => {
    if (contract.status === 'fully_signed') {
      toast.error('Cannot delete a fully signed contract');
      return;
    }
    
    const statusText = contract.status === 'client_signed' ? 'client-signed' : 
                       contract.status === 'signed' ? 'signed' : 'unsigned';
    
    if (!window.confirm(`Are you sure you want to delete this ${statusText} contract? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/contracts/${contract.id}`);
      toast.success('Contract deleted successfully');
      
      // Refresh contracts list
      const contractsRes = await axios.get(`${API}/contracts/booking/${eventDetailsBooking.id}`);
      setEventContracts(contractsRes.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete contract');
    }
  };

  // Event Details Management Functions
  const openEventDetailsDialog = async (booking, targetTab = 'timeline') => {
    setEventDetailsBooking(booking);
    setLoadingEventDetails(true);
    setShowEventDetailsDialog(true);
    setLoadedTabs({}); // Reset loaded tabs for new booking
    setInitialEventTab(targetTab); // Use specified tab or default to timeline
    
    // Initialize lockout overrides from booking
    setLockoutOverrides({
      forms_locked_override: booking.forms_locked_override ?? null,
      timeline_locked_override: booking.timeline_locked_override ?? null,
      playlists_locked_override: booking.playlists_locked_override ?? null
    });
    
    try {
      // Only fetch essential booking data initially - lazy load the rest
      const freshBookingRes = await axios.get(`${API}/bookings/${booking.id}`);
      const freshBooking = freshBookingRes.data;
      setEventDetailsBooking(freshBooking);
      
      // Update lockout overrides with fresh data
      setLockoutOverrides({
        forms_locked_override: freshBooking.forms_locked_override ?? null,
        timeline_locked_override: freshBooking.timeline_locked_override ?? null,
        playlists_locked_override: freshBooking.playlists_locked_override ?? null
      });
      
      // Load activity log from the booking (needed for badge count)
      setEventActivityLog(freshBooking.activity_log || []);
      
      // Mark loaded tabs and trigger timeline load
      setLoadedTabs(prev => ({ ...prev, booking: true }));
    } catch (error) {
      console.error('Failed to load event details:', error);
      toast.error('Failed to load event details');
    } finally {
      setLoadingEventDetails(false);
    }
  };

  // Toggle lockout override for a specific field
  const toggleLockout = async (field) => {
    if (!eventDetailsBooking?.id) return;
    
    const current = lockoutOverrides[field];
    // Cycle: null -> true -> false -> null
    const next = current === null ? true : current === true ? false : null;
    
    try {
      const response = await axios.put(`${API}/bookings/${eventDetailsBooking.id}/lockout-override`, {
        [field]: next
      });
      
      if (response.data.success) {
        setLockoutOverrides(prev => ({ ...prev, [field]: next }));
        const label = field.replace('_locked_override', '').replace('_', ' ');
        const stateLabel = next === true ? 'Locked' : next === false ? 'Open' : 'Default';
        toast.success(`${label.charAt(0).toUpperCase() + label.slice(1)} set to ${stateLabel}`);
      }
    } catch (error) {
      console.error('Failed to update lockout override:', error);
      toast.error('Failed to update lockout setting');
    }
  };

  // Lazy load tab data function
  const loadTabData = async (tabId, bookingId) => {
    if (!bookingId || loadedTabs[tabId]) return;
    
    setLoadingTab(tabId);
    
    try {
      switch (tabId) {
        case 'timeline': {
          const timelineRes = await axios.get(`${API}/timelines/booking/${bookingId}`);
          setEventTimeline(timelineRes.data);
          break;
        }
        case 'playlists': {
          const playlistsRes = await axios.get(`${API}/playlists/booking/${bookingId}`);
          const playlists = playlistsRes.data || [];
          setEventPlaylists(playlists);
          
          // Check which songs are in the DJ's library
          try {
            const allSongs = playlists.flatMap(p => p.songs || []).filter(s => s.title);
            if (allSongs.length > 0) {
              const checkRes = await axios.post(`${API}/music-library/check`, allSongs);
              const missingMap = {};
              checkRes.data.forEach(result => {
                const key = `${result.title?.toLowerCase()}-${result.artist?.toLowerCase()}`;
                if (!result.in_library) {
                  missingMap[key] = true;
                }
              });
              setMissingSongs(missingMap);
            } else {
              setMissingSongs({});
            }
          } catch (err) {
            console.error('Failed to check music library:', err);
            setMissingSongs({});
          }
          break;
        }
        case 'forms': {
          const [formsRes, eventTypesRes, addonsRes] = await Promise.all([
            axios.get(`${API}/planning-forms`),
            axios.get(`${API}/event-types`),
            axios.get(`${API}/addons`)
          ]);
          
          const freshBooking = eventDetailsBooking;
          const assignedFormIds = new Set();
          
          // Get forms from event type
          if (freshBooking.event_type) {
            const eventType = eventTypesRes.data.find(et => 
              et.name.toLowerCase() === freshBooking.event_type.toLowerCase()
            );
            if (eventType?.assigned_forms) {
              eventType.assigned_forms.forEach(id => assignedFormIds.add(id));
            }
          }
          
          // Get forms from selected add-ons
          if (freshBooking.selected_addons?.length > 0) {
            freshBooking.selected_addons.forEach(addonId => {
              const addon = addonsRes.data.find(a => a.id === addonId);
              if (addon?.assigned_forms) {
                addon.assigned_forms.forEach(id => assignedFormIds.add(id));
              }
            });
          }
          
          const allAvailableForms = formsRes.data || [];
          setAllForms(allAvailableForms);
          
          allAvailableForms.forEach(form => {
            if (form.assigned_packages?.includes(freshBooking.package_id)) {
              assignedFormIds.add(form.id);
            }
          });
          
          const manualFormIds = freshBooking.manual_form_ids || [];
          const excludedFormIds = freshBooking.excluded_form_ids || [];
          
          const bookingForms = allAvailableForms.filter(f => 
            ((assignedFormIds.has(f.id) || manualFormIds.includes(f.id)) && 
             f.form_type !== 'contact' && 
             !excludedFormIds.includes(f.id))
          ).map(f => ({
            ...f,
            is_manual: manualFormIds.includes(f.id) && !assignedFormIds.has(f.id)
          }));
          setEventForms(bookingForms);
          
          // Fetch form responses
          const responsesRes = await axios.get(`${API}/form-responses/booking/${bookingId}`);
          setEventFormResponses(responsesRes.data || []);
          break;
        }
        case 'tasks': {
          const tasksRes = await axios.get(`${API}/bookings/${bookingId}/tasks`);
          setEventTasks(tasksRes.data || []);
          break;
        }
        case 'payments': {
          const paymentsRes = await axios.get(`${API}/payments/booking/${bookingId}`);
          setEventPayments(paymentsRes.data || []);
          break;
        }
        case 'invoices': {
          const invoicesRes = await axios.get(`${API}/invoices/booking/${bookingId}`);
          setEventInvoices(invoicesRes.data || []);
          break;
        }
        case 'contracts': {
          const contractsRes = await axios.get(`${API}/contracts/booking/${bookingId}`);
          setEventContracts(contractsRes.data || []);
          break;
        }
        case 'documents': {
          const documentsRes = await axios.get(`${API}/bookings/${bookingId}/documents`);
          setEventDocuments(documentsRes.data || []);
          break;
        }
        case 'messages': {
          const messagesRes = await axios.get(`${API}/bookings/${bookingId}/messages`);
          setEventMessages(messagesRes.data || []);
          // Mark messages as read
          await axios.put(`${API}/bookings/${bookingId}/messages/mark-read`);
          break;
        }
        case 'expenses': {
          const expensesRes = await axios.get(`${API}/bookings/${bookingId}/expenses`);
          setEventExpenses(expensesRes.data?.expenses || []);
          setExpenseSummary({
            total_expenses: expensesRes.data?.total_expenses || 0,
            total_revenue: expensesRes.data?.total_revenue || 0,
            profit: expensesRes.data?.profit || 0
          });
          break;
        }
        case 'addon-requests': {
          const addonRequestsRes = await axios.get(`${API}/addon-requests?status=pending`).catch(() => ({ data: [] }));
          setAddonRequests((addonRequestsRes.data || []).filter(r => r.booking_id === bookingId));
          break;
        }
        case 'venue-changes': {
          const venueChangeRequestsRes = await axios.get(`${API}/venue-change-requests/pending`).catch(() => ({ data: [] }));
          setVenueChangeRequests((venueChangeRequestsRes.data || []).filter(r => r.booking_id === bookingId));
          break;
        }
        // These tabs don't need additional data fetching
        case 'seating':
        case 'discounts':
        case 'communications':
        case 'notes':
          break;
        default:
          break;
      }
      
      setLoadedTabs(prev => ({ ...prev, [tabId]: true }));
    } catch (error) {
      console.error(`Failed to load ${tabId} data:`, error);
      toast.error(`Failed to load ${tabId}`);
    } finally {
      setLoadingTab(null);
    }
  };

  // Effect to lazy load tab data when tab changes
  useEffect(() => {
    if (showEventDetailsDialog && eventDetailsBooking?.id && initialEventTab) {
      loadTabData(initialEventTab, eventDetailsBooking.id);
    }
  }, [initialEventTab, showEventDetailsDialog, eventDetailsBooking?.id]);

  // Task Management
  const handleGenerateTasks = async () => {
    if (!eventDetailsBooking) return;
    try {
      const response = await axios.post(`${API}/bookings/${eventDetailsBooking.id}/generate-tasks`);
      toast.success(`${response.data.tasks_created} tasks generated`);
      // Refresh tasks
      refreshTasks();
    } catch (error) {
      toast.error('Failed to generate tasks');
    }
  };

  // Playlist Management
  const openPlaylistEditor = (playlist = null) => {
    if (playlist) {
      setEditingPlaylist(playlist);
      setPlaylistName(playlist.name);
      // Ensure songs have designation and comment fields
      const songsWithFields = (playlist.songs?.length > 0 ? [...playlist.songs] : [{ title: '', artist: '', designation: 'must_play', comment: '' }])
        .map(s => ({ ...s, designation: s.designation || 'must_play', comment: s.comment || '' }));
      setPlaylistSongs(songsWithFields);
    } else {
      setEditingPlaylist(null);
      setPlaylistName('');
      setPlaylistSongs([{ title: '', artist: '', designation: 'must_play', comment: '' }]);
    }
    setShowPlaylistDialog(true);
  };

  const addPlaylistSong = () => {
    setPlaylistSongs([...playlistSongs, { title: '', artist: '', designation: 'must_play', comment: '' }]);
  };

  const updatePlaylistSong = (index, field, value) => {
    const updated = [...playlistSongs];
    updated[index][field] = value;
    setPlaylistSongs(updated);
  };

  const removePlaylistSong = (index) => {
    if (playlistSongs.length > 1) {
      setPlaylistSongs(playlistSongs.filter((_, i) => i !== index));
    }
  };

  // Add Song Modal handlers
  const openAddSongModal = () => {
    setNewSong({ title: '', artist: '', designation: '', comment: '' });
    setModalSearchQuery('');
    setModalSearchResults([]);
    setShowModalSearchResults(false);
    setShowAddSongModal(true);
  };

  // Search songs for the Add Song modal
  const searchSongsForModal = async (query) => {
    setModalSearchQuery(query);
    if (!query || query.length < 2) {
      setModalSearchResults([]);
      setShowModalSearchResults(false);
      return;
    }
    
    setModalSearching(true);
    setShowModalSearchResults(true);
    try {
      let response;
      if (spotifyConfigured && searchSource === 'spotify') {
        // Try Spotify first
        response = await axios.get(`${API}/spotify/search`, { params: { query, limit: 8 } });
      } else {
        // Fall back to MusicBrainz
        response = await axios.get(`${API}/music/search`, { params: { query, limit: 8 } });
      }
      setModalSearchResults(response.data.results || []);
    } catch {
      // If Spotify fails, try MusicBrainz as fallback
      if (spotifyConfigured && searchSource === 'spotify') {
        try {
          const fallbackResponse = await axios.get(`${API}/music/search`, { params: { query, limit: 8 } });
          setModalSearchResults(fallbackResponse.data.results || []);
        } catch {
          setModalSearchResults([]);
        }
      } else {
        setModalSearchResults([]);
      }
    } finally {
      setModalSearching(false);
    }
  };

  const selectSongFromModalSearch = (result) => {
    setNewSong({
      ...newSong,
      title: result.title,
      artist: result.artist
    });
    setModalSearchQuery('');
    setModalSearchResults([]);
    setShowModalSearchResults(false);
  };

  const handleAddSongFromModal = () => {
    if (!newSong.title.trim()) {
      toast.error('Please enter a song title');
      return;
    }
    if (!newSong.designation) {
      toast.error('Please select a designation (Must Play, Play if Possible, or Do Not Play)');
      return;
    }
    
    // Add the new song to the playlist
    const songToAdd = {
      title: newSong.title.trim(),
      artist: newSong.artist.trim(),
      designation: newSong.designation,
      comment: newSong.comment.trim()
    };
    
    setPlaylistSongs([...playlistSongs, songToAdd]);
    setShowAddSongModal(false);
    setNewSong({ title: '', artist: '', designation: '', comment: '' });
    toast.success('Song added!');
  };

  const savePlaylist = async () => {
    if (!playlistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    const validSongs = playlistSongs.filter(s => s.title.trim() || s.artist.trim());
    
    try {
      if (editingPlaylist) {
        await axios.put(`${API}/playlists/${editingPlaylist.id}`, {
          name: playlistName,
          songs: validSongs
        });
        toast.success('Playlist updated');
      } else {
        await axios.post(`${API}/playlists`, {
          booking_id: eventDetailsBooking.id,
          name: playlistName,
          songs: validSongs
        });
        toast.success('Playlist created');
      }
      setShowPlaylistDialog(false);
      // Refresh playlists
      const res = await axios.get(`${API}/playlists/booking/${eventDetailsBooking.id}`);
      setEventPlaylists(res.data || []);
    } catch (error) {
      toast.error('Failed to save playlist');
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Are you sure you want to delete this playlist?')) return;
    
    try {
      await axios.delete(`${API}/playlists/${playlistId}`);
      toast.success('Playlist deleted');
      const res = await axios.get(`${API}/playlists/booking/${eventDetailsBooking.id}`);
      setEventPlaylists(res.data || []);
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  // Spotify Import Functions
  const openSpotifyImport = () => {
    setSpotifyUrl('');
    setSpotifyPreview(null);
    setSpotifyImportMode('new');
    setSelectedPlaylistForSpotify('');
    setShowSpotifyDialog(true);
  };

  const previewSpotifyPlaylist = async () => {
    if (!spotifyUrl.trim()) {
      toast.error('Please enter a Spotify playlist URL');
      return;
    }

    setImportingSpotify(true);
    try {
      const response = await axios.get(`${API}/spotify/preview`, {
        params: { spotify_url: spotifyUrl }
      });
      setSpotifyPreview(response.data);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to fetch playlist. Check the URL and your Spotify settings.';
      toast.error(message);
      setSpotifyPreview(null);
    } finally {
      setImportingSpotify(false);
    }
  };

  const importSpotifyPlaylist = async () => {
    if (!spotifyPreview) {
      toast.error('Please preview the playlist first');
      return;
    }

    setImportingSpotify(true);
    try {
      const payload = {
        spotify_url: spotifyUrl,
        booking_id: eventDetailsBooking.id
      };

      if (spotifyImportMode === 'existing' && selectedPlaylistForSpotify) {
        payload.playlist_id = selectedPlaylistForSpotify;
      } else {
        payload.playlist_name = spotifyPreview.name;
      }

      const response = await axios.post(`${API}/spotify/import`, payload);
      
      toast.success(`Imported ${response.data.songs_imported} songs from "${response.data.spotify_name}"`);
      setShowSpotifyDialog(false);
      
      // Refresh playlists
      const res = await axios.get(`${API}/playlists/booking/${eventDetailsBooking.id}`);
      setEventPlaylists(res.data || []);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to import playlist';
      toast.error(message);
    } finally {
      setImportingSpotify(false);
    }
  };

  // PDF Import Functions
  const openPdfImport = () => {
    setPdfImportResult(null);
    setShowPdfImportDialog(true);
  };

  const handlePdfFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please select a PDF file');
      return;
    }
    
    setImportingPdf(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/playlists/import-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setPdfImportResult(response.data);
      toast.success(`Found ${response.data.counts.total} songs in PDF`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to parse PDF';
      toast.error(message);
      setPdfImportResult(null);
    } finally {
      setImportingPdf(false);
      // Reset the file input
      if (pdfInputRef.current) {
        pdfInputRef.current.value = '';
      }
    }
  };

  const importPdfPlaylist = async () => {
    if (!pdfImportResult?.songs) {
      toast.error('No songs to import');
      return;
    }
    
    // Combine all songs from the PDF
    const allSongs = [
      ...pdfImportResult.songs.must_play,
      ...pdfImportResult.songs.play_if_possible,
      ...pdfImportResult.songs.do_not_play
    ];
    
    if (allSongs.length === 0) {
      toast.error('No songs found in PDF');
      return;
    }
    
    try {
      // Create a new playlist with all the imported songs
      const playlistData = {
        booking_id: eventDetailsBooking.id,
        name: `Imported Playlist (${new Date().toLocaleDateString()})`,
        songs: allSongs
      };
      
      await axios.post(`${API}/playlists`, playlistData);
      toast.success(`Created playlist with ${allSongs.length} songs`);
      setShowPdfImportDialog(false);
      setPdfImportResult(null);
      
      // Refresh playlists
      const res = await axios.get(`${API}/playlists/booking/${eventDetailsBooking.id}`);
      setEventPlaylists(res.data || []);
    } catch (error) {
      toast.error('Failed to create playlist');
    }
  };

  // ==================== EXPENSE MANAGEMENT (Private) ====================
  const openExpenseDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setExpenseData({
        category: expense.category,
        description: expense.description,
        amount: expense.amount.toString(),
        date: expense.date || ''
      });
    } else {
      setEditingExpense(null);
      setExpenseData({ category: 'Employee Pay', description: '', amount: '', date: '' });
    }
    setShowExpenseDialog(true);
  };

  const saveExpense = async () => {
    if (!expenseData.description.trim() || !expenseData.amount) {
      toast.error('Please fill in description and amount');
      return;
    }
    
    const amount = parseFloat(expenseData.amount);
    if (isNaN(amount) || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      const payload = {
        category: expenseData.category,
        description: expenseData.description,
        amount: amount,
        date: expenseData.date || null
      };

      if (editingExpense) {
        await axios.put(`${API}/bookings/${eventDetailsBooking.id}/expenses/${editingExpense.id}`, payload);
        toast.success('Expense updated');
      } else {
        await axios.post(`${API}/bookings/${eventDetailsBooking.id}/expenses`, payload);
        toast.success('Expense added');
      }

      setShowExpenseDialog(false);
      
      // Refresh expenses
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/expenses`);
      setEventExpenses(res.data?.expenses || []);
      setExpenseSummary({
        total_expenses: res.data?.total_expenses || 0,
        total_revenue: res.data?.total_revenue || 0,
        profit: res.data?.profit || 0
      });
    } catch (error) {
      toast.error('Failed to save expense');
    }
  };

  const deleteExpense = async (expenseId) => {
    if (!window.confirm('Are you sure you want to delete this expense?')) return;
    
    try {
      await axios.delete(`${API}/bookings/${eventDetailsBooking.id}/expenses/${expenseId}`);
      toast.success('Expense deleted');
      
      // Refresh expenses
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/expenses`);
      setEventExpenses(res.data?.expenses || []);
      setExpenseSummary({
        total_expenses: res.data?.total_expenses || 0,
        total_revenue: res.data?.total_revenue || 0,
        profit: res.data?.profit || 0
      });
    } catch (error) {
      toast.error('Failed to delete expense');
    }
  };

  // ==================== MANUAL FORMS MANAGEMENT ====================
  const addManualForm = async () => {
    if (!selectedFormToAdd || !eventDetailsBooking) return;
    
    try {
      await axios.post(`${API}/bookings/${eventDetailsBooking.id}/manual-forms/${selectedFormToAdd}`);
      toast.success('Form added to booking');
      setShowAddFormDialog(false);
      setSelectedFormToAdd('');
      
      // Refresh the booking details to get updated forms
      openEventDetailsDialog(eventDetailsBooking);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add form');
    }
  };

  // Initiate form removal with confirmation
  const initiateRemoveForm = (form) => {
    const response = getFormResponse(form.id);
    setFormToRemove(form);
    setFormHasData(!!response);
    setShowRemoveFormDialog(true);
  };

  // Actually remove the form after confirmation
  const confirmRemoveForm = async () => {
    if (!formToRemove) return;
    
    try {
      // For manual forms, use the manual-forms endpoint
      if (formToRemove.is_manual) {
        await axios.delete(`${API}/bookings/${eventDetailsBooking.id}/manual-forms/${formToRemove.id}`);
      } else {
        // For auto-assigned forms, we need to detach them
        await axios.delete(`${API}/bookings/${eventDetailsBooking.id}/forms/${formToRemove.id}`);
      }
      
      toast.success('Form removed from booking');
      
      // Update local state
      setEventForms(prev => prev.filter(f => f.id !== formToRemove.id));
      
      // Also remove any form response
      setEventFormResponses(prev => prev.filter(r => r.form_id !== formToRemove.id));
      
    } catch (error) {
      console.error('Failed to remove form:', error);
      toast.error('Failed to remove form');
    } finally {
      setShowRemoveFormDialog(false);
      setFormToRemove(null);
      setFormHasData(false);
    }
  };

  // Legacy function for backwards compatibility
  const removeManualForm = async (formId) => {
    const form = eventForms.find(f => f.id === formId);
    if (form) {
      initiateRemoveForm(form);
    }
  };

  // Get forms that can be added (not already attached)
  const getAvailableFormsToAdd = () => {
    const attachedFormIds = eventForms.map(f => f.id);
    return allForms.filter(f => 
      !attachedFormIds.includes(f.id) && f.form_type !== 'contact'
    );
  };

  // Timeline Management
  // Helper function to convert time string to minutes for sorting
  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    // Handle HH:MM format (24-hour from time picker)
    if (timeStr.includes(':') && !timeStr.includes(' ')) {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    }
    // Handle legacy "H:MM AM/PM" format
    const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (!match) return 0;
    let hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const period = match[3]?.toUpperCase();
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return hours * 60 + minutes;
  };

  // Sort timeline events by time
  const sortedTimelineEvents = (events) => {
    if (!events || !Array.isArray(events)) return [];
    return [...events].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
  };

  const openTimelineEditor = (event = null) => {
    if (event) {
      setEditingTimelineEvent(event);
      setTimelineEventData({
        time: event.time || '',
        title: event.title || '',
        description: event.description || ''
      });
    } else {
      setEditingTimelineEvent(null);
      setTimelineEventData({ time: '', title: '', description: '' });
    }
    setShowTimelineDialog(true);
  };

  const saveTimelineEvent = async () => {
    if (!timelineEventData.time.trim() || !timelineEventData.title.trim()) {
      toast.error('Please enter time and title');
      return;
    }
    
    try {
      if (eventTimeline) {
        // Update existing timeline
        let events = [...(eventTimeline.events || [])];
        
        if (editingTimelineEvent) {
          // Update existing event
          events = events.map(e => 
            e.id === editingTimelineEvent.id 
              ? { ...e, ...timelineEventData }
              : e
          );
        } else {
          // Add new event
          events.push({
            id: `dj-${Date.now()}`,
            ...timelineEventData,
            is_client_request: false
          });
        }
        
        await axios.put(`${API}/timelines/${eventTimeline.id}`, { events });
      } else {
        // Create new timeline
        await axios.post(`${API}/timelines`, {
          booking_id: eventDetailsBooking.id,
          events: [{
            id: `dj-${Date.now()}`,
            ...timelineEventData,
            is_client_request: false
          }]
        });
      }
      
      toast.success(editingTimelineEvent ? 'Event updated' : 'Event added');
      setShowTimelineDialog(false);
      
      // Refresh timeline
      const res = await axios.get(`${API}/timelines/booking/${eventDetailsBooking.id}`);
      setEventTimeline(res.data);
    } catch (error) {
      toast.error('Failed to save timeline event');
    }
  };

  // Refresh timeline function for child components
  const refreshTimeline = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/timelines/booking/${eventDetailsBooking.id}`);
      setEventTimeline(res.data);
    } catch (error) {
      console.error('Failed to refresh timeline:', error);
    }
  };

  // Refresh tasks function for child components
  const refreshTasks = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/tasks`);
      setEventTasks(res.data || []);
    } catch (error) {
      console.error('Failed to refresh tasks:', error);
    }
  };

  // Refresh documents function for child components
  const refreshDocuments = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/documents`);
      setEventDocuments(res.data || []);
    } catch (error) {
      console.error('Failed to refresh documents:', error);
    }
  };

  // Refresh messages function for child components
  const refreshMessages = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/messages`);
      setEventMessages(res.data || []);
      // Mark messages as read
      await axios.put(`${API}/bookings/${eventDetailsBooking.id}/messages/mark-read`);
    } catch (error) {
      console.error('Failed to refresh messages:', error);
    }
  };

  // Refresh contracts function for child components
  const refreshContracts = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/contracts/booking/${eventDetailsBooking.id}`);
      setEventContracts(res.data || []);
    } catch (error) {
      console.error('Failed to refresh contracts:', error);
    }
  };

  // Refresh activity notes function for child components
  const refreshActivityNotes = async () => {
    if (!eventDetailsBooking?.id) return;
    try {
      const res = await axios.get(`${API}/bookings/${eventDetailsBooking.id}`);
      setEventActivityLog(res.data?.activity_log || []);
    } catch (error) {
      console.error('Failed to refresh activity notes:', error);
    }
  };

  const deleteTimelineEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const events = (eventTimeline.events || []).filter(e => e.id !== eventId);
      await axios.put(`${API}/timelines/${eventTimeline.id}`, { events });
      toast.success('Event deleted');
      
      const res = await axios.get(`${API}/timelines/booking/${eventDetailsBooking.id}`);
      setEventTimeline(res.data);
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const approveTimelineEvent = async (eventId, approved) => {
    try {
      const events = (eventTimeline.events || []).map(e => 
        e.id === eventId ? { ...e, status: approved ? 'approved' : 'declined' } : e
      );
      await axios.put(`${API}/timelines/${eventTimeline.id}`, { events });
      toast.success(`Event ${approved ? 'approved' : 'declined'}`);
      
      const res = await axios.get(`${API}/timelines/booking/${eventDetailsBooking.id}`);
      setEventTimeline(res.data);
    } catch (error) {
      toast.error('Failed to update event status');
    }
  };

  // Payment Management Functions
  const fetchEventPayments = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/payments/booking/${bookingId}`);
      setEventPayments(response.data || []);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
      setEventPayments([]);
    }
  };

  const openAddPaymentDialog = () => {
    setNewPayment({
      amount: '',
      payment_method: paymentTypes[0] || 'Cash',
      payment_date: new Date().toISOString().split('T')[0],
      notes: '',
      reference_number: ''
    });
    setShowPaymentDialog(true);
  };

  const handleAddPayment = async () => {
    if (!newPayment.amount || parseFloat(newPayment.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    try {
      await axios.post(`${API}/payments`, {
        booking_id: eventDetailsBooking.id,
        amount: parseFloat(newPayment.amount),
        payment_method: newPayment.payment_method,
        payment_date: newPayment.payment_date || null,
        notes: newPayment.notes || null,
        reference_number: newPayment.reference_number || null
      });
      toast.success('Payment added successfully');
      setShowPaymentDialog(false);
      
      // Refresh payments and bookings to update totals
      await fetchEventPayments(eventDetailsBooking.id);
      await fetchBookings();
      
      // Update the local eventDetailsBooking paid_amount
      setEventDetailsBooking(prev => ({
        ...prev,
        paid_amount: (prev.paid_amount || 0) + parseFloat(newPayment.amount)
      }));
    } catch (error) {
      toast.error('Failed to add payment');
    }
  };

  const handleDeletePayment = async (paymentId, paymentAmount) => {
    if (!window.confirm('Are you sure you want to delete this payment?')) return;

    try {
      await axios.delete(`${API}/payments/${paymentId}`);
      toast.success('Payment deleted');
      
      // Refresh payments and bookings to update totals
      await fetchEventPayments(eventDetailsBooking.id);
      await fetchBookings();
      
      // Update the local eventDetailsBooking paid_amount
      setEventDetailsBooking(prev => ({
        ...prev,
        paid_amount: Math.max(0, (prev.paid_amount || 0) - paymentAmount)
      }));
    } catch (error) {
      toast.error('Failed to delete payment');
    }
  };

  // Print Functions
  const handlePrint = (section) => {
    const printContent = document.getElementById(`print-${section}`);
    if (!printContent) return;
    
    const formattedDate = formatDate(eventDetailsBooking?.event_date);
    const formattedStartTime = eventDetailsBooking?.start_time ? formatTime(eventDetailsBooking.start_time) : '--:--';
    const formattedEndTime = eventDetailsBooking?.end_time ? formatTime(eventDetailsBooking.end_time) : '--:--';
    
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${eventDetailsBooking?.client_name} - ${section}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            h1 { color: #333; margin-bottom: 10px; }
            h2 { color: #666; margin-top: 20px; }
            .event-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .item { padding: 10px; border-bottom: 1px solid #eee; }
            .item:last-child { border-bottom: none; }
            .time { font-weight: bold; color: #0088cc; }
            .status { padding: 2px 8px; border-radius: 12px; font-size: 12px; }
            .status.pending { background: #fff3cd; color: #856404; }
            .status.approved { background: #d4edda; color: #155724; }
            .status.declined { background: #f8d7da; color: #721c24; }
            .song-number { display: inline-block; width: 24px; height: 24px; background: #0088cc; color: white; border-radius: 50%; text-align: center; line-height: 24px; margin-right: 10px; }
            table { width: 100%; border-collapse: collapse; margin-top: 10px; }
            th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
            th { background: #f5f5f5; }
            /* Designation badges for print */
            .print-designation, [class*="bg-green-500"], [class*="bg-amber-500"], [class*="bg-red-500"] {
              display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 500;
            }
            .bg-green-500\\/20 { background: #22c55e33 !important; color: #22c55e !important; }
            .bg-amber-500\\/20 { background: #f59e0b33 !important; color: #f59e0b !important; }
            .bg-red-500\\/20 { background: #ef444433 !important; color: #ef4444 !important; }
            @media print { body { padding: 15px; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>${eventDetailsBooking?.client_name}</h1>
          <div class="event-info">
            <strong>Date:</strong> ${formattedDate}<br/>
            <strong>Venue:</strong> ${eventDetailsBooking?.venue}<br/>
            <strong>Time:</strong> ${formattedStartTime} - ${formattedEndTime}
          </div>
          ${printContent.innerHTML}
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes with PDF)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printHtml);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  // Print single form with its responses
  const handlePrintSingleForm = (form) => {
    const response = getFormResponse(form.id);
    const formattedDate = formatDate(eventDetailsBooking?.event_date);
    
    let formContentHtml = `<h2 style="color: #333; border-bottom: 2px solid #0088cc; padding-bottom: 10px;">${form.name}</h2>`;
    
    if (form.fields && form.fields.length > 0) {
      formContentHtml += '<div class="form-fields">';
      form.fields.forEach(field => {
        if (field.type === 'section') {
          formContentHtml += `<div class="section-header"><h3>${field.label || field.name}</h3></div>`;
        } else if (field.type === 'comment') {
          formContentHtml += `<div class="comment"><em>${field.label || ''}</em></div>`;
        } else {
          const value = response?.responses?.[field.name];
          let displayValue = value || '<span style="color: #999;">Not provided</span>';
          
          if (field.type === 'repeater' || field.type === 'repeater3') {
            if (Array.isArray(value) && value.length > 0) {
              displayValue = '<table><tbody>';
              value.forEach((row, idx) => {
                const cols = Object.values(row).filter(v => v).join(' | ');
                displayValue += `<tr><td>${idx + 1}.</td><td>${cols || 'Empty'}</td></tr>`;
              });
              displayValue += '</tbody></table>';
            }
          } else if (field.type === 'columns2' || field.type === 'columns3') {
            if (value && typeof value === 'object') {
              displayValue = Object.values(value).filter(v => v).join(' | ') || '<span style="color: #999;">Not provided</span>';
            }
          } else if (field.type === 'textarea') {
            displayValue = value ? value.replace(/\n/g, '<br/>') : '<span style="color: #999;">Not provided</span>';
          }
          
          formContentHtml += `
            <div class="field-row">
              <div class="field-label">${field.label || field.name}</div>
              <div class="field-value">${displayValue}</div>
            </div>
          `;
        }
      });
      formContentHtml += '</div>';
    }
    
    if (!response) {
      formContentHtml += '<p style="color: #856404; background: #fff3cd; padding: 10px; border-radius: 5px; margin-top: 15px;">This form has not been submitted yet.</p>';
    }
    
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${eventDetailsBooking?.client_name} - ${form.name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; margin-bottom: 10px; }
            h2 { color: #333; margin-top: 30px; }
            h3 { color: #0088cc; margin: 15px 0 10px 0; }
            .event-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .form-fields { margin-top: 15px; }
            .field-row { display: flex; padding: 10px 0; border-bottom: 1px solid #eee; }
            .field-label { font-weight: bold; width: 40%; color: #555; }
            .field-value { width: 60%; }
            .section-header { background: #f0f0f0; padding: 10px; margin: 20px 0 10px 0; border-radius: 5px; }
            .section-header h3 { margin: 0; color: #333; }
            .comment { color: #666; font-style: italic; padding: 5px 0; }
            table { width: 100%; border-collapse: collapse; }
            td { padding: 5px; border-bottom: 1px solid #eee; }
            @media print { body { padding: 0; } @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h1>${eventDetailsBooking?.client_name}</h1>
          <div class="event-info">
            <strong>Event:</strong> ${eventDetailsBooking?.event_type || 'Event'}<br/>
            <strong>Date:</strong> ${formattedDate}<br/>
            <strong>Venue:</strong> ${eventDetailsBooking?.venue || 'TBD'}
          </div>
          ${formContentHtml}
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printHtml);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  // Print all forms with their responses
  const handlePrintAllForms = () => {
    if (!eventForms || eventForms.length === 0) {
      toast.error('No forms to print');
      return;
    }
    
    const formattedDate = formatDate(eventDetailsBooking?.event_date);
    
    let allFormsHtml = '';
    eventForms.forEach((form, formIndex) => {
      const response = getFormResponse(form.id);
      
      allFormsHtml += `<div class="form-section" style="${formIndex > 0 ? 'page-break-before: always;' : ''}">`;
      allFormsHtml += `<h2>${form.name}</h2>`;
      
      if (response) {
        allFormsHtml += '<span class="status-badge submitted">Submitted</span>';
      } else {
        allFormsHtml += '<span class="status-badge pending">Pending</span>';
      }
      
      if (form.fields && form.fields.length > 0) {
        allFormsHtml += '<div class="form-fields">';
        form.fields.forEach(field => {
          if (field.type === 'section') {
            allFormsHtml += `<div class="section-header"><h3>${field.label || field.name}</h3></div>`;
          } else if (field.type === 'comment') {
            allFormsHtml += `<div class="comment"><em>${field.label || ''}</em></div>`;
          } else {
            const value = response?.responses?.[field.name];
            let displayValue = value || '<span style="color: #999;">Not provided</span>';
            
            if (field.type === 'repeater' || field.type === 'repeater3') {
              if (Array.isArray(value) && value.length > 0) {
                displayValue = '<table><tbody>';
                value.forEach((row, idx) => {
                  const cols = Object.values(row).filter(v => v).join(' | ');
                  displayValue += `<tr><td>${idx + 1}.</td><td>${cols || 'Empty'}</td></tr>`;
                });
                displayValue += '</tbody></table>';
              }
            } else if (field.type === 'columns2' || field.type === 'columns3') {
              if (value && typeof value === 'object') {
                displayValue = Object.values(value).filter(v => v).join(' | ') || '<span style="color: #999;">Not provided</span>';
              }
            } else if (field.type === 'textarea') {
              displayValue = value ? value.replace(/\n/g, '<br/>') : '<span style="color: #999;">Not provided</span>';
            }
            
            allFormsHtml += `
              <div class="field-row">
                <div class="field-label">${field.label || field.name}</div>
                <div class="field-value">${displayValue}</div>
              </div>
            `;
          }
        });
        allFormsHtml += '</div>';
      }
      
      allFormsHtml += '</div>';
    });
    
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${eventDetailsBooking?.client_name} - All Forms</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; max-width: 800px; margin: 0 auto; }
            h1 { color: #333; margin-bottom: 10px; }
            h2 { color: #333; margin-top: 20px; border-bottom: 2px solid #0088cc; padding-bottom: 10px; }
            h3 { color: #0088cc; margin: 15px 0 10px 0; }
            .event-info { background: #f5f5f5; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
            .form-section { margin-bottom: 30px; }
            .form-fields { margin-top: 15px; }
            .field-row { display: flex; padding: 10px 0; border-bottom: 1px solid #eee; }
            .field-label { font-weight: bold; width: 40%; color: #555; }
            .field-value { width: 60%; }
            .section-header { background: #f0f0f0; padding: 10px; margin: 20px 0 10px 0; border-radius: 5px; }
            .section-header h3 { margin: 0; color: #333; }
            .comment { color: #666; font-style: italic; padding: 5px 0; }
            .status-badge { display: inline-block; padding: 3px 10px; border-radius: 12px; font-size: 12px; margin-left: 10px; }
            .status-badge.submitted { background: #d4edda; color: #155724; }
            .status-badge.pending { background: #fff3cd; color: #856404; }
            table { width: 100%; border-collapse: collapse; }
            td { padding: 5px; border-bottom: 1px solid #eee; }
            @media print { 
              body { padding: 0; }
              .form-section { page-break-inside: avoid; }
              @page { margin: 1cm; }
            }
          </style>
        </head>
        <body>
          <h1>${eventDetailsBooking?.client_name} - All Forms</h1>
          <div class="event-info">
            <strong>Event:</strong> ${eventDetailsBooking?.event_type || 'Event'}<br/>
            <strong>Date:</strong> ${formattedDate}<br/>
            <strong>Venue:</strong> ${eventDetailsBooking?.venue || 'TBD'}<br/>
            <strong>Total Forms:</strong> ${eventForms.length}
          </div>
          ${allFormsHtml}
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printHtml);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  const getFormResponse = (formId) => {
    return eventFormResponses.find(r => r.form_id === formId);
  };

  // Form editing functions
  const openFormEditor = (form) => {
    const existingResponse = getFormResponse(form.id);
    setEditingForm(form);
    // Initialize form responses with existing data or empty values
    const initialResponses = {};
    form.fields?.forEach(field => {
      if (existingResponse?.responses?.[field.name] !== undefined) {
        initialResponses[field.name] = existingResponse.responses[field.name];
      } else if (field.type === 'repeater') {
        initialResponses[field.name] = [{ col1: '', col2: '' }];
      } else if (field.type === 'repeater3') {
        initialResponses[field.name] = [{ col1: '', col2: '', col3: '' }];
      } else if (field.type === 'columns2') {
        initialResponses[field.name] = { col1: '', col2: '' };
      } else if (field.type === 'columns3') {
        initialResponses[field.name] = { col1: '', col2: '', col3: '' };
      } else if (field.type === 'section' || field.type === 'comment') {
        // Section and comment fields don't need user input
      } else {
        initialResponses[field.name] = '';
      }
    });
    setFormResponses(initialResponses);
    setShowFormDialog(true);
  };

  const handleFormFieldChange = (fieldName, value) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Handler for column layout fields (columns2, columns3)
  const handleColumnFieldChange = (fieldName, column, value) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: { ...(prev[fieldName] || {}), [column]: value }
    }));
  };

  const handleRepeaterChange = (fieldName, index, subFieldName, value) => {
    setFormResponses(prev => {
      const rows = [...(prev[fieldName] || [{}])];
      rows[index] = { ...rows[index], [subFieldName]: value };
      return { ...prev, [fieldName]: rows };
    });
  };

  const addRepeaterRow = (fieldName, is3Col = false) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: [...(prev[fieldName] || []), is3Col ? { col1: '', col2: '', col3: '' } : { col1: '', col2: '' }]
    }));
  };

  const removeRepeaterRow = (fieldName, index) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldName]: (prev[fieldName] || []).filter((_, i) => i !== index)
    }));
  };

  // Helper function to render typed column inputs for columns2/columns3 fields
  const renderColumnInput = (field, colKey, colType, colOptions, colValue) => {
    switch (colType) {
      case 'select':
        return (
          <Select 
            value={colValue || ''} 
            onValueChange={(v) => handleColumnFieldChange(field.name, colKey, v)}
          >
            <SelectTrigger className="bg-background" style={{ borderColor: primaryColor + '40' }}>
              <SelectValue placeholder="Select..." />
            </SelectTrigger>
            <SelectContent>
              {colOptions?.map((opt) => (
                <SelectItem key={opt} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'date':
        return (
          <Input
            type="date"
            value={colValue || ''}
            onChange={(e) => handleColumnFieldChange(field.name, colKey, e.target.value)}
            className="bg-background"
            style={{ borderColor: primaryColor + '40' }}
          />
        );
      case 'time':
        return (
          <Input
            type="time"
            value={colValue || ''}
            onChange={(e) => handleColumnFieldChange(field.name, colKey, e.target.value)}
            className="bg-background"
            style={{ borderColor: primaryColor + '40' }}
          />
        );
      case 'number':
        return (
          <Input
            type="number"
            value={colValue || ''}
            onChange={(e) => handleColumnFieldChange(field.name, colKey, e.target.value)}
            className="bg-background"
            style={{ borderColor: primaryColor + '40' }}
          />
        );
      default:
        return (
          <Input
            type="text"
            value={colValue || ''}
            onChange={(e) => handleColumnFieldChange(field.name, colKey, e.target.value)}
            className="bg-background"
            style={{ borderColor: primaryColor + '40' }}
          />
        );
    }
  };

  const saveFormResponse = async () => {
    if (!editingForm || !eventDetailsBooking) return;
    
    setSavingForm(true);
    try {
      const existingResponse = getFormResponse(editingForm.id);
      
      if (existingResponse) {
        // Update existing response
        await axios.put(`${API}/form-responses/${existingResponse.id}`, {
          responses: formResponses
        });
        toast.success('Form updated successfully');
      } else {
        // Create new response
        await axios.post(`${API}/form-responses`, {
          form_id: editingForm.id,
          booking_id: eventDetailsBooking.id,
          responses: formResponses
        });
        toast.success('Form saved successfully');
      }
      
      // Refresh form responses
      const responsesRes = await axios.get(`${API}/form-responses/booking/${eventDetailsBooking.id}`);
      setEventFormResponses(responsesRes.data || []);
      setShowFormDialog(false);
      setEditingForm(null);
    } catch (error) {
      console.error('Failed to save form:', error);
      toast.error('Failed to save form');
    } finally {
      setSavingForm(false);
    }
  };

  // Document handling functions
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDocFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedDocFile(file);
    }
  };

  const handleUploadDocument = async () => {
    if (!selectedDocFile || !eventDetailsBooking) return;
    
    setUploadingDoc(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        await axios.post(`${API}/bookings/${eventDetailsBooking.id}/documents`, {
          booking_id: eventDetailsBooking.id,
          filename: `${Date.now()}_${selectedDocFile.name}`,
          original_filename: selectedDocFile.name,
          file_type: selectedDocFile.type,
          file_size: selectedDocFile.size,
          file_url: base64Data,
          note: docNote || null
        });
        
        toast.success('Document uploaded successfully');
        setShowDocUploadDialog(false);
        setSelectedDocFile(null);
        setDocNote('');
        
        // Refresh documents
        const docsRes = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/documents`);
        setEventDocuments(docsRes.data || []);
      };
      reader.readAsDataURL(selectedDocFile);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API}/bookings/${eventDetailsBooking.id}/documents/${documentId}`);
      toast.success('Document deleted');
      const docsRes = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/documents`);
      setEventDocuments(docsRes.data || []);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete document');
    }
  };

  // Add-on request handlers
  const handleApproveAddonRequest = async (request) => {
    try {
      const price = parseFloat(addonApprovalPrice) || request.default_price;
      await axios.put(`${API}/addon-requests/${request.id}/approve`, {
        approved_price: price,
        dj_notes: addonApprovalNotes
      });
      toast.success('Add-on request approved! Client has been notified.');
      setProcessingAddonRequest(null);
      setAddonApprovalPrice('');
      setAddonApprovalNotes('');
      // Refresh addon requests and booking data
      const [requestsRes, bookingRes] = await Promise.all([
        axios.get(`${API}/addon-requests?status=pending`),
        axios.get(`${API}/bookings/${eventDetailsBooking.id}`)
      ]);
      setAddonRequests((requestsRes.data || []).filter(r => r.booking_id === eventDetailsBooking.id));
      setEventDetailsBooking(bookingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleDeclineAddonRequest = async (request) => {
    try {
      await axios.put(`${API}/addon-requests/${request.id}/decline`, {
        dj_notes: addonApprovalNotes
      });
      toast.success('Add-on request declined. Client has been notified.');
      setProcessingAddonRequest(null);
      setAddonApprovalNotes('');
      // Refresh addon requests
      const requestsRes = await axios.get(`${API}/addon-requests?status=pending`);
      setAddonRequests((requestsRes.data || []).filter(r => r.booking_id === eventDetailsBooking.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline request');
    }
  };

  // Venue change request handlers
  const handleApproveVenueChange = async (request) => {
    try {
      await axios.post(`${API}/venue-change-requests/${request.id}/approve`, {
        dj_notes: venueChangeNotes
      });
      toast.success('Venue change approved! Client has been notified.');
      setProcessingVenueChange(null);
      setVenueChangeNotes('');
      // Refresh venue change requests and booking data
      const [requestsRes, bookingRes] = await Promise.all([
        axios.get(`${API}/venue-change-requests/pending`),
        axios.get(`${API}/bookings/${eventDetailsBooking.id}`)
      ]);
      setVenueChangeRequests((requestsRes.data || []).filter(r => r.booking_id === eventDetailsBooking.id));
      setEventDetailsBooking(bookingRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleDeclineVenueChange = async (request) => {
    try {
      await axios.post(`${API}/venue-change-requests/${request.id}/decline`, {
        dj_notes: venueChangeNotes
      });
      toast.success('Venue change request declined. Client has been notified.');
      setProcessingVenueChange(null);
      setVenueChangeNotes('');
      // Refresh venue change requests
      const requestsRes = await axios.get(`${API}/venue-change-requests/pending`);
      setVenueChangeRequests((requestsRes.data || []).filter(r => r.booking_id === eventDetailsBooking.id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline request');
    }
  };

  const handlePrintDocuments = () => {
    const printContent = eventDocuments.map(doc => `
      <div style="margin-bottom: 20px; padding: 10px; border: 1px solid #ccc;">
        <h3>${doc.original_filename}</h3>
        ${doc.note ? `<p><em>${doc.note}</em></p>` : ''}
        <p>Uploaded by: ${doc.uploaded_by_name}</p>
        <p>Size: ${formatFileSize(doc.file_size)}</p>
      </div>
    `).join('');
    
    const printHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Documents - ${eventDetailsBooking?.client_name}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; }
            h2 { color: #333; }
            @media print { @page { margin: 1cm; } }
          </style>
        </head>
        <body>
          <h2>Documents for ${eventDetailsBooking?.client_name}</h2>
          ${printContent}
        </body>
      </html>
    `;
    
    // Use iframe for safer printing (prevents browser crashes)
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    document.body.appendChild(printFrame);
    
    printFrame.contentDocument.write(printHtml);
    printFrame.contentDocument.close();
    
    printFrame.onload = () => {
      setTimeout(() => {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
        setTimeout(() => document.body.removeChild(printFrame), 1000);
      }, 250);
    };
  };

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="bookings-page">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2" data-testid="bookings-heading">Bookings</h1>
            <p className="text-muted-foreground text-sm sm:text-base">Manage your events and bookings</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Dialog open={showDialog} onOpenChange={(open) => {
              setShowDialog(open);
              if (open) {
                setClientSearchQuery('');
                setClientSearchResults([]);
                setShowClientDropdown(false);
                setVenueSearchQuery('');
                setShowVenueDropdown(false);
                if (!open) resetBookingForm();
              }
            }}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black flex-1 sm:flex-none" data-testid="create-booking-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">New Booking</span>
                  <span className="sm:hidden">New</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-booking-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">
                    {editingBooking ? 'Edit Booking' : 'Create New Booking'}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2" ref={clientSearchRef}>
                      <Label className="text-foreground">Client Name *</Label>
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={clientSearchQuery || newBooking.client_name}
                            onChange={handleClientSearchChange}
                            onFocus={() => clientSearchQuery.length >= 2 && setShowClientDropdown(true)}
                            placeholder="Search existing clients..."
                            className="bg-background border-border pl-9 text-foreground"
                            data-testid="booking-client-name-input"
                          />
                          {isSearching && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2">
                              <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                        </div>
                        
                        {/* Dropdown for search results */}
                        {showClientDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto">
                            {clientSearchResults.length > 0 ? (
                              <>
                                {clientSearchResults.map((client, idx) => (
                                  <div
                                    key={idx}
                                    onClick={() => selectClient(client)}
                                    className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 text-foreground"
                                    data-testid={`client-result-${idx}`}
                                  >
                                    <div className="font-medium">{client.name}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                      {client.email}
                                      <span className="text-xs px-1.5 py-0.5 rounded bg-muted">
                                        {client.source}
                                      </span>
                                    </div>
                                  </div>
                                ))}
                                <div
                                  onClick={openAddClientDialog}
                                  className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary border-t border-border"
                                  data-testid="add-new-client-option"
                                >
                                  <UserPlus2 className="w-4 h-4" />
                                  Add "{clientSearchQuery}" as new client
                                </div>
                              </>
                            ) : (
                              <div
                                onClick={openAddClientDialog}
                                className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary"
                                data-testid="add-new-client-option"
                              >
                                <UserPlus2 className="w-4 h-4" />
                                No clients found - Add "{clientSearchQuery}" as new client
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Email *</Label>
                      <Input
                        type="email"
                        value={newBooking.client_email}
                        onChange={(e) => setNewBooking({ ...newBooking, client_email: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-email-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Phone</Label>
                      <Input
                        value={newBooking.client_phone}
                        onChange={(e) => setNewBooking({ ...newBooking, client_phone: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-phone-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Event Date *</Label>
                      <Input
                        type="date"
                        value={newBooking.event_date}
                        onChange={(e) => setNewBooking({ ...newBooking, event_date: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-event-date-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Start Time</Label>
                      <Input
                        type="time"
                        value={newBooking.start_time}
                        onChange={(e) => setNewBooking({ ...newBooking, start_time: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-start-time-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">End Time</Label>
                      <Input
                        type="time"
                        value={newBooking.end_time}
                        onChange={(e) => setNewBooking({ ...newBooking, end_time: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-end-time-input"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Event Type *</Label>
                      <Select value={newBooking.event_type} onValueChange={(v) => setNewBooking({ ...newBooking, event_type: v })}>
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="booking-event-type-select">
                          <SelectValue placeholder="Select event type..." />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {eventTypes.length === 0 ? (
                            <SelectItem value="other">Other</SelectItem>
                          ) : (
                            eventTypes.filter(et => et.is_active).map((eventType) => (
                              <SelectItem key={eventType.id} value={eventType.name}>
                                <div className="flex items-center gap-2">
                                  <div 
                                    className="w-3 h-3 rounded-full" 
                                    style={{ backgroundColor: eventType.color }}
                                  />
                                  {eventType.name}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2" ref={venueSearchRef}>
                      <Label className="text-foreground">Venue *</Label>
                      <div className="relative">
                        <div className="relative">
                          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            value={venueSearchQuery || newBooking.venue}
                            onChange={handleVenueSearchChange}
                            onFocus={() => setShowVenueDropdown(true)}
                            placeholder="Search existing venues..."
                            className="bg-background border-border pl-9 text-foreground"
                            data-testid="booking-venue-input"
                          />
                        </div>
                        
                        {/* Dropdown for venue results */}
                        {showVenueDropdown && (
                          <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-xl shadow-xl max-h-60 overflow-y-auto">
                            {filteredVenues.length > 0 ? (
                              <>
                                {filteredVenues.map((venue, idx) => (
                                  <div
                                    key={venue.id || idx}
                                    onClick={() => selectVenue(venue)}
                                    className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 text-foreground"
                                    data-testid={`venue-result-${idx}`}
                                  >
                                    <div className="font-medium">{venue.name}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                      {venue.venue_type}
                                      {venue.city && <span>• {venue.city}</span>}
                                    </div>
                                  </div>
                                ))}
                                <div
                                  onClick={openAddVenueDialog}
                                  className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary border-t border-border"
                                  data-testid="add-new-venue-option"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add "{venueSearchQuery || newBooking.venue}" as new venue
                                </div>
                              </>
                            ) : venueSearchQuery ? (
                              <div
                                onClick={openAddVenueDialog}
                                className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary"
                                data-testid="add-new-venue-option"
                              >
                                <Plus className="w-4 h-4" />
                                No venues found - Add "{venueSearchQuery}" as new venue
                              </div>
                            ) : (
                              venues.length > 0 ? (
                                venues.slice(0, 5).map((venue, idx) => (
                                  <div
                                    key={venue.id || idx}
                                    onClick={() => selectVenue(venue)}
                                    className="px-4 py-3 hover:bg-muted cursor-pointer border-b border-border last:border-b-0 text-foreground"
                                    data-testid={`venue-result-${idx}`}
                                  >
                                    <div className="font-medium">{venue.name}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-2">
                                      {venue.venue_type}
                                      {venue.city && <span>• {venue.city}</span>}
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div
                                  onClick={openAddVenueDialog}
                                  className="px-4 py-3 hover:bg-primary/10 cursor-pointer flex items-center gap-2 text-primary"
                                  data-testid="add-new-venue-option"
                                >
                                  <Plus className="w-4 h-4" />
                                  Add new venue
                                </div>
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Package *</Label>
                      <Select value={newBooking.package_id} onValueChange={handlePackageSelect}>
                        <SelectTrigger className="bg-background border-border text-foreground" data-testid="booking-package-select">
                          <SelectValue placeholder="Select a package..." />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {packages.map((pkg) => (
                            <SelectItem key={pkg.id} value={pkg.id}>
                              {pkg.name} - ${pkg.base_price}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-foreground">Guest Count</Label>
                      <Input
                        type="number"
                        value={newBooking.guest_count}
                        onChange={(e) => setNewBooking({ ...newBooking, guest_count: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-guest-count-input"
                      />
                    </div>
                  </div>
                  {newBooking.package_id && addons.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-foreground">Add-Ons (Optional)</Label>
                      <div className="grid grid-cols-2 gap-2 max-h-[150px] overflow-y-auto">
                        {addons.map((addon) => (
                          <div
                            key={addon.id}
                            className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg cursor-pointer hover:bg-muted/50 text-foreground"
                            onClick={() => handleAddonToggle(addon.id)}
                          >
                            <Checkbox
                              checked={newBooking.selected_addons.includes(addon.id)}
                              onCheckedChange={() => handleAddonToggle(addon.id)}
                            />
                            <div className="flex-1">
                              <div className="text-sm font-medium">{addon.name}</div>
                              <div className="text-xs text-primary">+${addon.price}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-foreground">Total Amount ($)</Label>
                      <Input
                        type="number"
                        value={newBooking.total_amount}
                        onChange={(e) => setNewBooking({ ...newBooking, total_amount: e.target.value })}
                        className="bg-background border-border text-foreground"
                        data-testid="booking-total-amount-input"
                        readOnly={!!newBooking.package_id}
                      />
                    </div>
                  </div>

                  {/* Payment Schedule Section */}
                  <div className="space-y-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                    <div className="flex items-center justify-between">
                      <Label className="text-green-400 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Payment Schedule
                      </Label>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setNewBooking({
                            ...newBooking,
                            payment_schedule: [
                              ...(newBooking.payment_schedule || []),
                              { amount: '', due_date: '', description: '', payment_type: 'regular', allow_partial: true }
                            ]
                          });
                        }}
                        className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                        data-testid="add-payment-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Add Payment
                      </Button>
                    </div>
                    
                    {newBooking.payment_schedule && newBooking.payment_schedule.length > 0 ? (
                      <div className="space-y-3">
                        {newBooking.payment_schedule.map((payment, index) => (
                          <div key={index} className={`p-3 rounded-lg border space-y-2 ${
                            payment.payment_type === 'retainer' ? 'bg-blue-500/10 border-blue-500/30' :
                            payment.payment_type === 'pay_in_full' ? 'bg-purple-500/10 border-purple-500/30' :
                            'bg-background/50 border-border'
                          }`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-foreground">Payment {index + 1}</span>
                                {payment.payment_type === 'retainer' && (
                                  <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-0.5 rounded">Minimum Required</span>
                                )}
                                {payment.payment_type === 'pay_in_full' && (
                                  <span className="text-xs bg-purple-500/20 text-purple-400 px-2 py-0.5 rounded">Discount Option</span>
                                )}
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  const updated = [...newBooking.payment_schedule];
                                  updated.splice(index, 1);
                                  setNewBooking({ ...newBooking, payment_schedule: updated });
                                }}
                                className="text-red-500 hover:text-red-400 h-6 w-6 p-0"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            {/* Payment Type Selection */}
                            <div className="grid grid-cols-4 gap-2">
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Type</Label>
                                <Select 
                                  value={payment.payment_type || 'regular'} 
                                  onValueChange={(v) => {
                                    const updated = [...newBooking.payment_schedule];
                                    updated[index].payment_type = v;
                                    // Set defaults based on type
                                    if (v === 'retainer') {
                                      updated[index].description = 'Retainer';
                                      updated[index].allow_partial = false;
                                    } else if (v === 'pay_in_full') {
                                      updated[index].description = 'Pay in Full (Save ' + (updated[index].discount_percent || 10) + '%)';
                                      updated[index].allow_partial = false;
                                    } else {
                                      updated[index].allow_partial = true;
                                    }
                                    setNewBooking({ ...newBooking, payment_schedule: updated });
                                  }}
                                >
                                  <SelectTrigger className="bg-background border-border text-foreground h-8">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent className="bg-card border-border">
                                    <SelectItem value="retainer">Retainer</SelectItem>
                                    <SelectItem value="regular">Regular</SelectItem>
                                    <SelectItem value="pay_in_full">Pay in Full (Discount)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">
                                  {payment.payment_type === 'pay_in_full' ? 'Discount %' : 'Amount ($)'}
                                </Label>
                                {payment.payment_type === 'pay_in_full' ? (
                                  <Input
                                    type="number"
                                    value={payment.discount_percent || 10}
                                    onChange={(e) => {
                                      const updated = [...newBooking.payment_schedule];
                                      updated[index].discount_percent = e.target.value;
                                      updated[index].description = 'Pay in Full (Save ' + e.target.value + '%)';
                                      // Calculate discounted amount
                                      const discount = parseFloat(e.target.value) || 0;
                                      const total = parseFloat(newBooking.total_amount) || 0;
                                      updated[index].amount = (total * (1 - discount / 100)).toFixed(2);
                                      setNewBooking({ ...newBooking, payment_schedule: updated });
                                    }}
                                    className="bg-background border-border text-foreground h-8"
                                    placeholder="10"
                                    min="0"
                                    max="100"
                                    data-testid={`payment-discount-${index}`}
                                  />
                                ) : (
                                  <Input
                                    type="number"
                                    value={payment.amount}
                                    onChange={(e) => {
                                      const updated = [...newBooking.payment_schedule];
                                      updated[index].amount = e.target.value;
                                      setNewBooking({ ...newBooking, payment_schedule: updated });
                                    }}
                                    className="bg-background border-border text-foreground h-8"
                                    placeholder="0.00"
                                    data-testid={`payment-amount-${index}`}
                                  />
                                )}
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Due Date</Label>
                                <Input
                                  type="date"
                                  value={payment.due_date}
                                  onChange={(e) => {
                                    const updated = [...newBooking.payment_schedule];
                                    updated[index].due_date = e.target.value;
                                    setNewBooking({ ...newBooking, payment_schedule: updated });
                                  }}
                                  className="bg-background border-border text-foreground h-8"
                                  data-testid={`payment-due-date-${index}`}
                                />
                              </div>
                              <div className="space-y-1">
                                <Label className="text-xs text-muted-foreground">Description</Label>
                                <Input
                                  type="text"
                                  value={payment.description}
                                  onChange={(e) => {
                                    const updated = [...newBooking.payment_schedule];
                                    updated[index].description = e.target.value;
                                    setNewBooking({ ...newBooking, payment_schedule: updated });
                                  }}
                                  className="bg-background border-border text-foreground h-8"
                                  placeholder={payment.payment_type === 'retainer' ? 'Retainer' : 'e.g., Final Payment'}
                                  data-testid={`payment-description-${index}`}
                                />
                              </div>
                            </div>
                            
                            {/* Show calculated amount for pay in full */}
                            {payment.payment_type === 'pay_in_full' && (
                              <div className="text-xs text-purple-400 mt-1">
                                Client pays: ${parseFloat(payment.amount || 0).toFixed(2)} (saves ${((parseFloat(newBooking.total_amount) || 0) - parseFloat(payment.amount || 0)).toFixed(2)})
                              </div>
                            )}
                          </div>
                        ))}
                        
                        {/* Payment Summary */}
                        <div className="pt-2 border-t border-green-500/30">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Retainer (minimum):</span>
                            <span className="text-foreground">
                              ${newBooking.payment_schedule
                                .filter(p => p.payment_type === 'retainer')
                                .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Scheduled Payments:</span>
                            <span className="text-foreground">
                              ${newBooking.payment_schedule
                                .filter(p => p.payment_type === 'regular' || !p.payment_type)
                                .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}
                            </span>
                          </div>
                          {newBooking.payment_schedule.some(p => p.payment_type === 'pay_in_full') && (
                            <div className="flex justify-between text-sm text-purple-400">
                              <span>Pay in Full Option:</span>
                              <span>
                                ${newBooking.payment_schedule
                                  .filter(p => p.payment_type === 'pay_in_full')
                                  .reduce((sum, p) => sum + parseFloat(p.amount || 0), 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No payments scheduled. Click "Add Payment" to add a retainer or payment plan.
                      </p>
                    )}
                  </div>

                  {/* Discounts Section */}
                  {discounts.length > 0 && (
                    <div className="space-y-3 p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <Label className="text-purple-400 flex items-center gap-2">
                        <Percent className="w-4 h-4" />
                        Apply Discount
                      </Label>
                      <Select
                        value=""
                        onValueChange={(discountId) => {
                          const discount = discounts.find(d => d.id === discountId);
                          if (discount && !selectedDiscounts.find(d => d.id === discountId)) {
                            // Calculate cascading discount - apply to running total after previous discounts
                            const subtotal = parseFloat(newBooking.total_amount) || 0;
                            let runningTotal = subtotal;
                            // Apply all existing discounts first to get current running total
                            selectedDiscounts.forEach(d => {
                              if (d.type === 'percentage' || d.discount_type === 'percentage') {
                                runningTotal -= (runningTotal * (d.value || 0)) / 100;
                              } else {
                                runningTotal -= (d.value || d.amount || 0);
                              }
                              runningTotal = Math.max(0, runningTotal);
                            });
                            // Now calculate this discount against the running total
                            const discountAmount = discount.discount_type === 'percentage' 
                              ? (runningTotal * discount.value / 100) 
                              : discount.value;
                            const newSelectedDiscount = {
                              id: discount.id,
                              name: discount.name,
                              type: discount.discount_type,
                              value: discount.value,
                              amount: discountAmount,
                              tags: discount.tags || []
                            };
                            setSelectedDiscounts([...selectedDiscounts, newSelectedDiscount]);
                          }
                        }}
                      >
                        <SelectTrigger className="bg-background border-border">
                          <SelectValue placeholder="Select a discount to apply..." />
                        </SelectTrigger>
                        <SelectContent>
                          {discounts.filter(d => !selectedDiscounts.find(sd => sd.id === d.id)).map(discount => (
                            <SelectItem key={discount.id} value={discount.id}>
                              {discount.name} ({discount.discount_type === 'percentage' ? `${discount.value}%` : `$${discount.value}`})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      
                      {selectedDiscounts.length > 0 && (
                        <div className="space-y-2 mt-3">
                          {selectedDiscounts.map(discount => (
                            <div key={discount.id} className="flex items-center justify-between p-2 rounded bg-purple-500/20">
                              <div className="flex items-center gap-2">
                                <span className="text-sm">{discount.name}</span>
                                {discount.auto_applied && (
                                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/30 text-green-400 font-medium">AUTO</span>
                                )}
                                <span className="text-xs text-purple-400">
                                  -{discount.type === 'percentage' || discount.discount_type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-red-400">-${(discount.amount || 0).toFixed(2)}</span>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setSelectedDiscounts(selectedDiscounts.filter(d => d.id !== discount.id))}
                                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                  title={discount.auto_applied ? "Remove auto-applied discount" : "Remove discount"}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                          <div className="flex justify-between items-center pt-2 border-t border-purple-500/30">
                            <span className="font-semibold">Final Total:</span>
                            <span className="text-lg font-bold text-green-500">
                              ${(() => {
                                const subtotal = parseFloat(newBooking.total_amount) || 0;
                                let runningTotal = subtotal;
                                selectedDiscounts.forEach(d => {
                                  if (d.type === 'percentage' || d.discount_type === 'percentage') {
                                    runningTotal -= (runningTotal * (d.value || 0)) / 100;
                                  } else {
                                    runningTotal -= (d.value || d.amount || 0);
                                  }
                                  runningTotal = Math.max(0, runningTotal);
                                });
                                return runningTotal.toFixed(2);
                              })()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label className="text-foreground">Notes</Label>
                    <textarea
                      value={newBooking.notes}
                      onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })}
                      className="w-full min-h-[100px] bg-background border border-border rounded-lg p-3 text-foreground"
                      data-testid="booking-notes-input"
                    />
                  </div>
                  <Button
                    onClick={() => handleCreate()}
                    disabled={checkingAvailability}
                    className="w-full bg-primary hover:bg-primary/80 text-primary-foreground"
                    data-testid="booking-create-submit-btn"
                  >
                    {checkingAvailability ? 'Checking availability...' : (editingBooking ? 'Update Booking' : 'Create Booking')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Button 
              variant="outline" 
              onClick={exportAllBookingsToCalendar}
              className="border-border flex-1 sm:flex-none"
              data-testid="export-all-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Export to Calendar</span>
            </Button>
          </div>
        </div>

          {/* Search and Tabs */}
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            {/* Search Input */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, venue, event type..."
                className="pl-10 bg-muted/50 border-border"
                data-testid="booking-search-input"
              />
              {searchQuery && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSearchQuery('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>

            {/* View Toggle */}
            <div className="flex items-center gap-1 glass-card border-glass-border rounded-lg p-1">
              <Button
                variant={viewMode === 'compact' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('compact')}
                className="h-8 px-3"
                data-testid="view-compact-btn"
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className="h-8 px-3"
                data-testid="view-cards-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
            </div>
            
            {/* Tabs */}
            <Tabs value={bookingsTab} onValueChange={setBookingsTab}>
              <TabsList className="glass-card border-glass-border">
                <TabsTrigger value="upcoming" data-testid="tab-upcoming">
                  Upcoming
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-green-500/20 text-green-500">
                    {bookings.filter(b => b.status === 'confirmed' && new Date(b.event_date) >= new Date(new Date().toDateString())).length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="pending" data-testid="tab-pending">
                  Pending
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-yellow-500/20 text-yellow-500">
                    {bookings.filter(b => b.status === 'pending').length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="completed" data-testid="tab-completed">
                  Completed
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-gray-500/20 text-gray-400">
                    {bookings.filter(b => b.status === 'completed').length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="cancelled" data-testid="tab-cancelled">
                  Cancelled
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-red-500/20 text-red-500">
                    {bookings.filter(b => b.status === 'cancelled').length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="all" data-testid="tab-all">
                  All
                  <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-purple-500/20 text-purple-500">
                    {bookings.length}
                  </span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : bookings.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-bookings-message">
              <p className="text-muted-foreground">No bookings yet. Create your first booking!</p>
            </div>
          ) : bookings.filter(b => {
            // Apply filters to check if any results
            const today = new Date(new Date().toDateString());
            let matchesTab = true;
            if (bookingsTab === 'upcoming') {
              matchesTab = b.status === 'confirmed' && new Date(b.event_date) >= today;
            } else if (bookingsTab === 'pending') {
              matchesTab = b.status === 'pending';
            } else if (bookingsTab === 'completed') {
              matchesTab = b.status === 'completed';
            } else if (bookingsTab === 'cancelled') {
              matchesTab = b.status === 'cancelled';
            }
            
            if (!matchesTab) return false;
            
            // Search filter
            if (searchQuery.trim()) {
              const query = searchQuery.toLowerCase();
              return (
                b.client_name?.toLowerCase().includes(query) ||
                b.client_email?.toLowerCase().includes(query) ||
                b.venue?.toLowerCase().includes(query) ||
                b.event_type?.toLowerCase().includes(query)
              );
            }
            return true;
          }).length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl">
              <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground/50" />
              <p className="text-muted-foreground">
                {searchQuery ? `No bookings found matching "${searchQuery}"` : `No ${bookingsTab} bookings`}
              </p>
              {searchQuery && (
                <Button variant="link" onClick={() => setSearchQuery('')} className="mt-2">
                  Clear search
                </Button>
              )}
            </div>
          ) : (
            <div className={viewMode === 'compact' ? 'space-y-1' : 'grid gap-4'} data-testid="bookings-list">
              {bookings
                .filter(b => {
                  // Tab filter
                  const today = new Date(new Date().toDateString());
                  let matchesTab = true;
                  if (bookingsTab === 'upcoming') {
                    matchesTab = b.status === 'confirmed' && new Date(b.event_date) >= today;
                  } else if (bookingsTab === 'pending') {
                    matchesTab = b.status === 'pending';
                  } else if (bookingsTab === 'completed') {
                    matchesTab = b.status === 'completed';
                  } else if (bookingsTab === 'cancelled') {
                    matchesTab = b.status === 'cancelled';
                  }
                  // 'all' tab - matchesTab stays true
                  
                  if (!matchesTab) return false;
                  
                  // Search filter (applies to all tabs)
                  if (searchQuery.trim()) {
                    const query = searchQuery.toLowerCase();
                    return (
                      b.client_name?.toLowerCase().includes(query) ||
                      b.client_email?.toLowerCase().includes(query) ||
                      b.client_phone?.toLowerCase().includes(query) ||
                      b.venue?.toLowerCase().includes(query) ||
                      b.event_type?.toLowerCase().includes(query) ||
                      b.package?.toLowerCase().includes(query) ||
                      b.event_date?.includes(query) ||
                      b.notes?.toLowerCase().includes(query)
                    );
                  }
                  return true;
                })
                .sort((a, b) => {
                  // For completed/cancelled: sort by event_date descending (most recent first)
                  // For others: sort by event_date ascending (nearest first)
                  if (bookingsTab === 'completed' || bookingsTab === 'cancelled') {
                    return new Date(b.event_date) - new Date(a.event_date);
                  } else {
                    return new Date(a.event_date) - new Date(b.event_date);
                  }
                })
                .map((booking) => viewMode === 'compact' ? (
                  <div key={booking.id} data-testid={`compact-booking-${booking.id}`}>
                    <button
                      onClick={() => setExpandedBookingId(expandedBookingId === booking.id ? null : booking.id)}
                      className="w-full flex items-center gap-4 px-4 py-3 rounded-xl text-left transition-colors hover:bg-white/[0.04] group"
                      style={{ background: expandedBookingId === booking.id ? 'rgba(255,255,255,0.04)' : 'transparent' }}
                    >
                      <div className={`w-2 h-2 rounded-full shrink-0 ${
                        booking.status === 'confirmed' ? 'bg-green-500' :
                        booking.status === 'pending' ? 'bg-yellow-500' :
                        booking.status === 'completed' ? 'bg-gray-400' :
                        'bg-red-500'
                      }`} />
                      <div className="flex-1 min-w-0 flex items-center gap-4">
                        <span className="font-medium text-sm w-48 truncate">{booking.client_name}</span>
                        <span className="text-sm text-muted-foreground w-28 shrink-0">{formatDate(booking.event_date)}</span>
                        <span className="text-xs text-muted-foreground hidden sm:block w-40 truncate">{booking.event_type}</span>
                        <span className="text-xs text-muted-foreground hidden md:block w-40 truncate">{booking.venue || '—'}</span>
                      </div>
                      <div className="flex items-center gap-4 shrink-0">
                        {(() => {
                          const balance = Math.max(0, (booking.total_amount || 0) - (booking.paid_amount || 0));
                          return balance > 0 ? (
                            <span className="text-sm font-medium text-amber-400 w-24 text-right">${balance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                          ) : (
                            <span className="text-sm text-green-500 w-24 text-right">Paid</span>
                          );
                        })()}
                        <ChevronDown className={`w-4 h-4 text-muted-foreground transition-transform ${expandedBookingId === booking.id ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                    {expandedBookingId === booking.id && (
                      <div className="pl-6 pr-2 pb-2 pt-1">
                        <BookingCard
                          booking={booking}
                          formatDate={formatDate}
                          formatTime={formatTime}
                          onStatusChange={handleStatusChange}
                          onManageEvent={(b) => {
                            setInitialEventTab('timeline');
                            openEventDetailsDialog(b);
                          }}
                          onEdit={openEditBookingDialog}
                          onCopyPortalLink={copyPortalLink}
                          onSendEmail={(b) => {
                            setEmailRecipient({
                              email: b.client_email,
                              name: b.client_name,
                              contact_id: b.contact_id,
                              booking_id: b.id
                            });
                            setShowEmailDialog(true);
                          }}
                          onExportCalendar={exportBookingToCalendar}
                          onAssignEmployees={openEmployeeDialog}
                          onGenerateInvoice={handleGenerateInvoice}
                          onViewPortal={(b) => window.open(`/portal/${b.client_portal_token}`, '_blank')}
                          onDelete={handleDeleteBooking}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                <BookingCard
                  key={booking.id}
                  booking={booking}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  onStatusChange={handleStatusChange}
                  onManageEvent={(b) => {
                    setInitialEventTab('timeline');
                    openEventDetailsDialog(b);
                  }}
                  onEdit={openEditBookingDialog}
                  onCopyPortalLink={copyPortalLink}
                  onSendEmail={(b) => {
                    setEmailRecipient({
                      email: b.client_email,
                      name: b.client_name,
                      contact_id: b.contact_id,
                      booking_id: b.id
                    });
                    setShowEmailDialog(true);
                  }}
                  onExportCalendar={exportBookingToCalendar}
                  onAssignEmployees={openEmployeeDialog}
                  onGenerateInvoice={handleGenerateInvoice}
                  onViewPortal={(b) => window.open(`/portal/${b.client_portal_token}`, '_blank')}
                  onDelete={handleDeleteBooking}
                />
              ))}
            </div>
          )}

          {/* Employee Assignment Dialog */}
          <EmployeeAssignmentDialog
            open={showEmployeeDialog}
            onOpenChange={setShowEmployeeDialog}
            booking={selectedBooking}
            employees={employees}
            assignedEmployeeIds={selectedBooking?.assigned_employees || []}
            onAssign={handleAssignEmployee}
            onUnassign={handleUnassignEmployee}
          />

          {/* Event Details Management Dialog */}
          <Dialog open={showEventDetailsDialog} onOpenChange={setShowEventDetailsDialog}>
            <DialogContent className="bg-card border border-border max-w-5xl max-h-[85vh] p-0 overflow-hidden" data-testid="event-details-dialog">
              <div className="flex h-full">
                {/* Left Sidebar Navigation */}
                <div className="w-56 bg-muted/30 border-r border-border flex flex-col">
                  {/* Header */}
                  <div className="p-4 border-b border-border">
                    <h2 className="font-heading text-lg font-semibold flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-neon-purple" />
                      Manage Event
                    </h2>
                    <p className="text-sm text-muted-foreground mt-1 truncate">{eventDetailsBooking?.client_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {eventDetailsBooking && formatDate(eventDetailsBooking.event_date)}
                      {eventDetailsBooking?.event_date && (
                        <span className="ml-1">
                          ({new Date(eventDetailsBooking.event_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short' })})
                        </span>
                      )}
                    </p>
                    
                    {/* Status Selector */}
                    <div className="mt-3">
                      <Select
                        value={eventDetailsBooking?.status || 'pending'}
                        onValueChange={(value) => {
                          if (value === 'cancelled') {
                            setCancellingBooking(eventDetailsBooking);
                            setCancellationReason('');
                            setShowCancelDialog(true);
                          } else {
                            handleStatusChange(eventDetailsBooking?.id, value, eventDetailsBooking);
                          }
                        }}
                      >
                        <SelectTrigger 
                          className={`w-full h-8 text-xs font-medium border-0 ${
                            eventDetailsBooking?.status === 'confirmed' ? 'bg-green-500/20 text-green-500' :
                            eventDetailsBooking?.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                            eventDetailsBooking?.status === 'completed' ? 'bg-blue-500/20 text-blue-400' :
                            eventDetailsBooking?.status === 'cancelled' ? 'bg-red-500/20 text-red-500' :
                            'bg-yellow-500/20 text-yellow-500'
                          }`}
                          data-testid="event-status-selector"
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                              Pending
                            </span>
                          </SelectItem>
                          <SelectItem value="confirmed">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-green-500"></span>
                              Booked
                            </span>
                          </SelectItem>
                          <SelectItem value="completed">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                              Past
                            </span>
                          </SelectItem>
                          <SelectItem value="cancelled">
                            <span className="flex items-center gap-2">
                              <span className="w-2 h-2 rounded-full bg-red-500"></span>
                              Cancelled
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Export PDF Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/bookings/${eventDetailsBooking.id}/event-summary-pdf`, {
                            responseType: 'blob'
                          });
                          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `event_summary_${eventDetailsBooking.client_name?.replace(/\s+/g, '_') || 'event'}.pdf`;
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                          toast.success('PDF downloaded!');
                        } catch (err) {
                          toast.error('Failed to generate PDF');
                        }
                      }}
                      className="w-full mt-3 border-glass-border text-xs"
                      data-testid="export-event-pdf-btn"
                    >
                      <FileText className="w-3.5 h-3.5 mr-1.5" />
                      Export Event PDF
                    </Button>
                  </div>
                  
                  {/* Portal Lockout Overrides */}
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-semibold">Portal Lockout</p>
                    <div className="space-y-1">
                      {[
                        { field: 'forms_locked_override', label: 'Forms', icon: ClipboardList },
                        { field: 'timeline_locked_override', label: 'Timeline', icon: Calendar },
                        { field: 'playlists_locked_override', label: 'Playlists', icon: Music }
                      ].map(({ field, label, icon: Icon }) => {
                        const val = lockoutOverrides[field];
                        const isLocked = val === true;
                        const isUnlocked = val === false;
                        return (
                          <button
                            key={field}
                            onClick={() => toggleLockout(field)}
                            data-testid={`lockout-toggle-${field}`}
                            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs transition-colors ${
                              isLocked ? 'bg-red-500/15 text-red-400' : 
                              isUnlocked ? 'bg-emerald-500/15 text-emerald-400' : 
                              'bg-muted/50 text-muted-foreground'
                            } hover:bg-muted`}
                            title={isLocked ? `${label}: Locked (click to unlock)` : isUnlocked ? `${label}: Unlocked (click for default)` : `${label}: Using default (click to lock)`}
                          >
                            {isLocked ? <Lock className="w-3 h-3" /> : isUnlocked ? <Unlock className="w-3 h-3" /> : <Icon className="w-3 h-3 opacity-50" />}
                            <span className="flex-1 text-left">{label}</span>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                              isLocked ? 'bg-red-500/20 text-red-300' : 
                              isUnlocked ? 'bg-emerald-500/20 text-emerald-300' : 
                              'bg-muted text-muted-foreground'
                            }`}>
                              {isLocked ? 'Locked' : isUnlocked ? 'Open' : 'Default'}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  
                  {/* Navigation Items */}
                  <nav className="flex-1 p-2 space-y-1">
                    {[
                      { id: 'timeline', label: 'Timeline', icon: Calendar },
                      { id: 'playlists', label: 'Playlists', icon: Music },
                      { id: 'seating', label: 'Seating Chart', icon: LayoutGrid },
                      { id: 'forms', label: 'Forms', icon: ClipboardList },
                      { id: 'tasks', label: 'Tasks', icon: CheckSquare, badge: eventTasks.filter(t => t.status !== 'completed').length, badgeColor: eventTasks.some(t => t.is_overdue) ? 'bg-red-500' : 'bg-orange-500' },
                      { id: 'discounts', label: 'Discounts', icon: Percent, badge: eventDetailsBooking?.applied_discounts?.length || 0, badgeColor: 'bg-neon-purple' },
                      { id: 'invoices', label: 'Invoices', icon: FileText, badge: eventInvoices.length, badgeColor: 'bg-neon-blue' },
                      { id: 'contracts', label: 'Contracts', icon: FileText, badge: eventContracts.filter(c => c.status === 'client_signed').length, badgeColor: 'bg-orange-500' },
                      { id: 'payments', label: 'Payments', icon: DollarSign },
                      { id: 'expenses', label: 'Expenses', icon: CreditCard, badge: eventExpenses.length, badgeColor: 'bg-purple-500' },
                      { id: 'documents', label: 'Documents', icon: File, badge: eventDocuments.length, badgeColor: 'bg-muted' },
                      { id: 'addon-requests', label: 'Add-on Requests', icon: Plus, badge: addonRequests.length, badgeColor: 'bg-neon-purple' },
                      { id: 'venue-changes', label: 'Venue Changes', icon: MapPin, badge: venueChangeRequests.length, badgeColor: 'bg-emerald-500' },
                      { id: 'communications', label: 'Communications', icon: Mail },
                      { id: 'messages', label: 'Messages', icon: MessageSquare, badge: eventMessages.filter(m => m.sender_type === 'client' && !m.is_read).length, badgeColor: 'bg-red-500' },
                      { id: 'notes', label: 'Activity Notes', icon: Clock, badge: eventActivityLog.length, badgeColor: 'bg-yellow-500' },
                    ].map((item) => {
                      const Icon = item.icon;
                      const isActive = initialEventTab === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => setInitialEventTab(item.id)}
                          className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-left ${
                            isActive 
                              ? 'bg-neon-blue/20 text-neon-blue' 
                              : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                          }`}
                          data-testid={`event-nav-${item.id}`}
                        >
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4" />
                            <span className="text-sm font-medium">{item.label}</span>
                          </div>
                          {item.badge > 0 && (
                            <span className={`min-w-[20px] h-5 px-1.5 text-xs rounded-full flex items-center justify-center ${
                              item.badgeColor === 'bg-muted' ? 'bg-muted text-muted-foreground' : `${item.badgeColor} text-white`
                            }`}>
                              {item.badge}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </nav>
                  
                  {/* Close Button */}
                  <div className="p-3 border-t border-border">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => setShowEventDetailsDialog(false)}
                      className="w-full border-border"
                      data-testid="close-event-dialog-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Close
                    </Button>
                  </div>
                </div>
                
                {/* Main Content Area */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {loadingEventDetails ? (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-blue mx-auto mb-2"></div>
                        <p className="text-muted-foreground">Loading event details...</p>
                      </div>
                    </div>
                  ) : loadingTab === initialEventTab ? (
                    <div className="flex-1 overflow-y-auto p-6">
                      {/* Show skeleton based on which tab is loading */}
                      {initialEventTab === 'timeline' && <TimelineSkeleton />}
                      {initialEventTab === 'playlists' && <PlaylistsSkeleton />}
                      {initialEventTab === 'tasks' && <TasksSkeleton />}
                      {initialEventTab === 'payments' && <PaymentsSkeleton />}
                      {initialEventTab === 'invoices' && <PaymentsSkeleton />}
                      {initialEventTab === 'messages' && <MessagesSkeleton />}
                      {initialEventTab === 'contracts' && <ContractsSkeleton />}
                      {initialEventTab === 'documents' && <DocumentsSkeleton />}
                      {initialEventTab === 'forms' && <FormsSkeleton />}
                      {!['timeline', 'playlists', 'tasks', 'payments', 'invoices', 'messages', 'contracts', 'documents', 'forms'].includes(initialEventTab) && <GenericTabSkeleton />}
                    </div>
                  ) : (
                    <div className="flex-1 overflow-y-auto p-6">
                      {/* Timeline Section */}
                      {initialEventTab === 'timeline' && (
                        <TimelineTab
                          booking={eventDetailsBooking}
                          timeline={eventTimeline}
                          onTimelineUpdate={refreshTimeline}
                          formatTime={formatTime}
                        />
                      )}

                      {/* Playlists Section */}
                      {initialEventTab === 'playlists' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">Music Playlists</h3>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => handlePrint('playlists')} className="border-border">
                                <Printer className="w-4 h-4 mr-1" />
                                Print
                              </Button>
                              <Button size="sm" variant="outline" onClick={openSpotifyImport} className="border-green-500/50 text-green-400 hover:bg-green-500/10">
                                <Music className="w-4 h-4 mr-1" />
                                Import Spotify
                              </Button>
                              <Button size="sm" variant="outline" onClick={openPdfImport} className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                                <FileText className="w-4 h-4 mr-1" />
                                Import PDF
                              </Button>
                              <Button size="sm" onClick={() => openPlaylistEditor()} className="bg-neon-blue text-black hover:bg-neon-blue/80">
                                <Plus className="w-4 h-4 mr-1" />
                                Add Playlist
                              </Button>
                            </div>
                          </div>
                          
                          <div id="print-playlists" className="grid md:grid-cols-2 gap-4">
                      {eventPlaylists.length > 0 ? (
                        eventPlaylists.map((playlist) => (
                          <div 
                            key={playlist.id}
                            onClick={() => openPlaylistEditor(playlist)}
                            className={`p-4 rounded-xl cursor-pointer hover:bg-muted/50 transition-all group ${
                              playlist.created_by === 'client' ? 'bg-purple-500/10 border border-purple-500/30' : 'bg-muted/30 border border-border'
                            }`}
                            data-testid={`playlist-card-${playlist.id}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <Music className="w-5 h-5 text-neon-blue" />
                                <h4 className="font-semibold">{playlist.name}</h4>
                                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                              <div className="flex items-center gap-1">
                                {playlist.created_by === 'client' && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-neon-purple/20 text-neon-purple mr-2">
                                    Client
                                  </span>
                                )}
                                {playlist.play_in_order && (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 mr-2 flex items-center gap-1" data-testid={`play-in-order-badge-${playlist.id}`}>
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                                    Play in Order
                                  </span>
                                )}
                                {playlist.spotify_url && (
                                  <a 
                                    href={playlist.spotify_url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 hover:bg-green-500/30 flex items-center gap-1 mr-2"
                                  >
                                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                                      <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                                    </svg>
                                  </a>
                                )}
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openPlaylistEditor(playlist); }} className="text-muted-foreground hover:text-foreground p-1" title="Edit">
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); deletePlaylist(playlist.id); }} className="text-red-500 hover:text-red-400 p-1" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                            <p className="text-muted-foreground text-sm ml-7">{playlist.songs?.length || 0} songs</p>
                            {/* Song designation breakdown */}
                            {playlist.songs?.length > 0 && (
                              <div className="flex gap-3 mt-2 ml-7 text-xs">
                                {playlist.songs.filter(s => (s.designation || 'must_play') === 'must_play').length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                    <span className="text-muted-foreground">{playlist.songs.filter(s => (s.designation || 'must_play') === 'must_play').length}</span>
                                  </span>
                                )}
                                {playlist.songs.filter(s => s.designation === 'play_if_possible').length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                                    <span className="text-muted-foreground">{playlist.songs.filter(s => s.designation === 'play_if_possible').length}</span>
                                  </span>
                                )}
                                {playlist.songs.filter(s => s.designation === 'do_not_play').length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                    <span className="text-muted-foreground">{playlist.songs.filter(s => s.designation === 'do_not_play').length}</span>
                                  </span>
                                )}
                                {/* Missing songs indicator (songs not in DJ's library) */}
                                {(() => {
                                  const missingCount = (playlist.songs || []).filter(s => {
                                    const key = `${s.title?.toLowerCase()}-${s.artist?.toLowerCase()}`;
                                    return missingSongs[key];
                                  }).length;
                                  return missingCount > 0 ? (
                                    <span className="flex items-center gap-1 ml-2 text-orange-400" title="Songs not in your music library">
                                      <AlertCircle className="w-3 h-3" />
                                      <span>{missingCount} missing</span>
                                    </span>
                                  ) : null;
                                })()}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="col-span-2 text-center py-8 text-muted-foreground">
                          <Music className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No playlists yet</p>
                          <p className="text-sm">Click "Add Playlist" to create song lists</p>
                        </div>
                      )}
                          </div>
                        </div>
                      )}

                      {/* Seating Chart Section */}
                      {initialEventTab === 'seating' && (
                        <div className="space-y-4" data-testid="seating-chart-section">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              <LayoutGrid className="w-5 h-5 text-purple-400" />
                              Seating Chart
                            </h3>
                          </div>
                          <div className="bg-gray-900/50 rounded-xl border border-gray-700 overflow-hidden" style={{ height: '600px' }}>
                            <SeatingChartEditor 
                              bookingId={eventDetailsBooking?.id}
                              isClientPortal={false}
                            />
                          </div>
                        </div>
                      )}

                      {/* Forms Section */}
                      {initialEventTab === 'forms' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">Client Forms</h3>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => setShowAddFormDialog(true)} className="border-border">
                                <Plus className="w-4 h-4 mr-1" />
                                Add Form
                              </Button>
                              <Button size="sm" variant="outline" onClick={handlePrintAllForms} className="border-border">
                                <Printer className="w-4 h-4 mr-1" />
                                Print All
                              </Button>
                            </div>
                          </div>
                          
                          <div id="print-forms" className="space-y-4">
                      {eventForms.length > 0 ? (
                        eventForms.map((form) => {
                          const response = getFormResponse(form.id);
                          const lastEditedBy = response?.last_edited_by;
                          const clientEdited = lastEditedBy === 'client';
                          
                          return (
                            <div 
                              key={form.id} 
                              className={`p-4 rounded-xl flex items-center justify-between ${
                                response 
                                  ? 'bg-green-500/10 border border-green-500/30' 
                                  : 'bg-orange-500/10 border border-orange-500/30'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <ClipboardList className={`w-5 h-5 ${response ? 'text-green-500' : 'text-orange-500'}`} />
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-semibold">{form.name}</h4>
                                    {form.is_manual && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                        Manual
                                      </span>
                                    )}
                                    {response && clientEdited && (
                                      <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400 flex items-center gap-1">
                                        <Edit className="w-3 h-3" />
                                        Client Edited
                                      </span>
                                    )}
                                  </div>
                                  {response ? (
                                    <span className="text-xs text-green-500">
                                      Submitted {response.last_edited_at ? `• Last edit: ${new Date(response.last_edited_at).toLocaleDateString()}` : ''}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-orange-500">Pending</span>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handlePrintSingleForm(form)}
                                  className="h-8 w-8 p-0"
                                  title="Print this form"
                                  data-testid={`print-form-${form.id}`}
                                >
                                  <Printer className="w-4 h-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant={response ? "outline" : "default"}
                                  onClick={() => openFormEditor(form)}
                                  className={response ? "border-border" : "bg-neon-blue hover:bg-neon-blue/80 text-black"}
                                  data-testid={`edit-form-${form.id}`}
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  {response ? 'View/Edit' : 'Fill Out'}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => initiateRemoveForm(form)}
                                  className="text-red-500 hover:text-red-400 h-8 w-8 p-0"
                                  title="Remove form from booking"
                                  data-testid={`remove-form-${form.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No forms assigned to this booking</p>
                          <p className="text-sm mt-1">Click "Add Form" to attach a custom form</p>
                        </div>
                      )}
                          </div>
                        </div>
                      )}

                      {/* Tasks Section */}
                      {initialEventTab === 'tasks' && (
                        <TasksTab
                          booking={eventDetailsBooking}
                          tasks={eventTasks}
                          onTasksUpdate={refreshTasks}
                          formatDate={formatDate}
                          onGenerateTasks={handleGenerateTasks}
                        />
                      )}

                      {/* Discounts Section */}
                      {initialEventTab === 'discounts' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg flex items-center gap-2">
                                <Percent className="w-5 h-5 text-neon-purple" />
                                Applied Discounts
                              </h3>
                              {(() => {
                                const totalDiscounts = (eventDetailsBooking?.applied_discounts || []).reduce((sum, d) => sum + (d.amount || 0), 0);
                                const originalAmount = eventDetailsBooking?.original_amount || (eventDetailsBooking?.total_amount || 0) + totalDiscounts;
                                return (
                                  <p className="text-sm text-muted-foreground">
                                    Original: ${originalAmount.toFixed(2)} | 
                                    Discount: <span className="text-neon-purple font-medium">
                                      -${totalDiscounts.toFixed(2)}
                                    </span> | 
                                    Final: <span className="text-green-500 font-medium">${eventDetailsBooking?.total_amount || 0}</span>
                                  </p>
                                );
                              })()}
                            </div>
                          </div>

                          {/* Add Discount Section */}
                          <div className="p-4 rounded-xl bg-muted/20 border border-border">
                            <Label className="text-sm font-medium mb-2 block">Add Discount</Label>
                            <Select 
                              onValueChange={(discountId) => {
                                const discount = discounts.find(d => d.id === discountId);
                                if (discount) addDiscountToBooking(discount);
                              }}
                            >
                              <SelectTrigger className="bg-muted/20 border-glass-border" data-testid="add-discount-select">
                                <SelectValue placeholder="Select a discount to apply..." />
                              </SelectTrigger>
                              <SelectContent>
                                {discounts
                                  .filter(d => !(eventDetailsBooking?.applied_discounts || []).find(ad => ad.id === d.id))
                                  .map(discount => (
                                    <SelectItem key={discount.id} value={discount.id}>
                                      <span className="flex items-center gap-2">
                                        {discount.discount_type === 'percentage' ? (
                                          <Percent className="w-4 h-4 text-neon-purple" />
                                        ) : (
                                          <DollarSign className="w-4 h-4 text-green-500" />
                                        )}
                                        {discount.name} - {discount.discount_type === 'percentage' ? `${discount.value}%` : `$${discount.value}`}
                                      </span>
                                    </SelectItem>
                                  ))}
                                {discounts.filter(d => !(eventDetailsBooking?.applied_discounts || []).find(ad => ad.id === d.id)).length === 0 && (
                                  <SelectItem value="none" disabled>No more discounts available</SelectItem>
                                )}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Applied Discounts List */}
                          <div className="space-y-2">
                            {(eventDetailsBooking?.applied_discounts || []).length > 0 ? (
                              (eventDetailsBooking?.applied_discounts || []).map((discount) => (
                                <div 
                                  key={discount.id}
                                  className="flex items-center justify-between p-4 rounded-xl bg-muted/30 border border-neon-purple/20"
                                  data-testid={`applied-discount-${discount.id}`}
                                >
                                  <div className="flex items-center gap-4">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                      discount.discount_type === 'percentage' ? 'bg-neon-purple/20' : 'bg-green-500/20'
                                    }`}>
                                      {discount.discount_type === 'percentage' ? (
                                        <Percent className="w-5 h-5 text-neon-purple" />
                                      ) : (
                                        <DollarSign className="w-5 h-5 text-green-500" />
                                      )}
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <span className="font-semibold">{discount.name}</span>
                                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                                          discount.auto_applied ? 'bg-green-500/20 text-green-500' : 'bg-blue-500/20 text-blue-400'
                                        }`}>
                                          {discount.auto_applied ? 'Auto-Applied' : 'Manual'}
                                        </span>
                                      </div>
                                      <div className="text-sm text-muted-foreground">
                                        {discount.discount_type === 'percentage' ? `${discount.value}%` : `$${discount.value}`} off
                                        <span className="mx-2">•</span>
                                        <span className="text-neon-purple font-medium">-${discount.amount?.toFixed(2) || '0.00'}</span>
                                      </div>
                                      {discount.tags?.length > 0 && (
                                        <div className="flex gap-1 mt-1">
                                          {discount.tags.map(tag => (
                                            <span key={tag} className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 flex items-center gap-1">
                                              <Tag className="w-3 h-3" />
                                              {tag}
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    onClick={() => removeDiscountFromBooking(discount.id)} 
                                    className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                    data-testid={`remove-discount-btn-${discount.id}`}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              ))
                            ) : (
                              <div className="text-center py-8 text-muted-foreground">
                                <Percent className="w-12 h-12 mx-auto mb-3 opacity-50" />
                                <p>No discounts applied</p>
                                <p className="text-sm">Select a discount above to apply it to this booking</p>
                              </div>
                            )}
                          </div>

                          {/* Discount Summary */}
                          {(eventDetailsBooking?.applied_discounts || []).length > 0 && (
                            <div className="mt-4 p-4 rounded-xl bg-neon-purple/10 border border-neon-purple/30">
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <Percent className="w-4 h-4 text-neon-purple" />
                                Pricing Summary
                              </h4>
                              {(() => {
                                const totalDiscounts = (eventDetailsBooking?.applied_discounts || []).reduce((sum, d) => sum + (d.amount || 0), 0);
                                const originalAmount = eventDetailsBooking?.original_amount || (eventDetailsBooking?.total_amount || 0) + totalDiscounts;
                                return (
                                  <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Original Amount</span>
                                      <span className="font-semibold">${originalAmount.toFixed(2)}</span>
                                    </div>
                                    {(eventDetailsBooking?.applied_discounts || []).map(discount => (
                                      <div key={discount.id} className="flex justify-between text-neon-purple">
                                        <span>{discount.name}</span>
                                        <span>-${discount.amount?.toFixed(2) || '0.00'}</span>
                                      </div>
                                    ))}
                                    <div className="border-t border-neon-purple/30 pt-2 flex justify-between">
                                      <span className="font-semibold">Final Amount</span>
                                      <span className="font-bold text-green-500">${eventDetailsBooking?.total_amount || 0}</span>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Invoices Section */}
                      {initialEventTab === 'invoices' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5 text-neon-blue" />
                                Invoices
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Manage invoices for this event
                              </p>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleGenerateInvoice(eventDetailsBooking)}
                              className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                              data-testid="create-new-invoice-btn"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              New Invoice
                            </Button>
                          </div>

                          {/* Invoices List */}
                          {eventInvoices.length > 0 ? (
                            <div className="space-y-3">
                              {eventInvoices.map((invoice) => (
                                <div 
                                  key={invoice.id}
                                  className="p-4 rounded-xl bg-muted/30 border border-border hover:border-neon-blue/30 transition-colors"
                                  data-testid={`event-invoice-${invoice.id}`}
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 rounded-full bg-neon-blue/20 flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-neon-blue" />
                                      </div>
                                      <div>
                                        <p className="font-semibold">{invoice.invoice_number}</p>
                                        <p className="text-sm text-muted-foreground">Due: {invoice.due_date}</p>
                                      </div>
                                    </div>
                                    <div className="text-right">
                                      <p className="font-bold text-lg">${invoice.amount?.toFixed(2) || '0.00'}</p>
                                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                                        invoice.status === 'paid' ? 'bg-green-500/20 text-green-500' :
                                        invoice.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' :
                                        'bg-red-500/20 text-red-500'
                                      }`}>
                                        {invoice.status}
                                      </span>
                                    </div>
                                  </div>
                                  
                                  {/* Line Items Preview */}
                                  {invoice.line_items?.length > 0 && (
                                    <div className="mb-3 p-2 rounded-lg bg-muted/20">
                                      <p className="text-xs text-muted-foreground mb-1">Items:</p>
                                      {invoice.line_items.slice(0, 3).map((item, idx) => (
                                        <div key={idx} className="flex justify-between text-sm">
                                          <span className="truncate max-w-[200px]">{item.description}</span>
                                          <span className={item.amount < 0 ? 'text-red-500' : ''}>${item.amount?.toFixed(2)}</span>
                                        </div>
                                      ))}
                                      {invoice.line_items.length > 3 && (
                                        <p className="text-xs text-muted-foreground mt-1">+{invoice.line_items.length - 3} more items</p>
                                      )}
                                    </div>
                                  )}

                                  {/* Actions */}
                                  <div className="flex items-center gap-2 pt-2 border-t border-border">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEditEventInvoice(invoice)}
                                      className="flex-1 text-muted-foreground hover:text-foreground"
                                      data-testid={`edit-invoice-btn-${invoice.id}`}
                                    >
                                      <Edit className="w-4 h-4 mr-1" />
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => downloadEventInvoicePdf(invoice)}
                                      className="flex-1 text-muted-foreground hover:text-foreground"
                                      data-testid={`download-invoice-btn-${invoice.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => openEmailEventInvoice(invoice)}
                                      className="flex-1 text-neon-blue hover:text-neon-blue/80"
                                      data-testid={`email-invoice-btn-${invoice.id}`}
                                    >
                                      <MessageSquare className="w-4 h-4 mr-1" />
                                      Email
                                    </Button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-12 text-muted-foreground">
                              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                              <p className="text-lg font-medium">No Invoices Yet</p>
                              <p className="text-sm mb-4">Create an invoice to send to your client</p>
                              <Button 
                                onClick={() => handleGenerateInvoice(eventDetailsBooking)}
                                className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Create Invoice
                              </Button>
                            </div>
                          )}

                          {/* Invoice Summary */}
                          {eventInvoices.length > 0 && (
                            <div className="p-4 rounded-xl bg-neon-blue/10 border border-neon-blue/30">
                              <h4 className="font-medium mb-2 flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-neon-blue" />
                                Invoice Summary
                              </h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Total Invoiced</span>
                                  <span className="font-semibold">
                                    ${eventInvoices.reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Pending</span>
                                  <span className="text-yellow-500 font-medium">
                                    ${eventInvoices.filter(inv => inv.status === 'pending').reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)}
                                  </span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-muted-foreground">Paid</span>
                                  <span className="text-green-500 font-medium">
                                    ${eventInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + (inv.amount || 0), 0).toFixed(2)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Contracts Section */}
                      {initialEventTab === 'contracts' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg flex items-center gap-2">
                                <FileText className="w-5 h-5 text-neon-purple" />
                                Contracts
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                Manage contracts for this event
                              </p>
                            </div>
                            <Button
                              onClick={() => setShowGenerateContractDialog(true)}
                              className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                              data-testid="generate-contract-btn"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Generate Contract
                            </Button>
                          </div>

                          {/* Contracts List */}
                          {eventContracts.length > 0 ? (
                            <div className="space-y-3">
                              {eventContracts.map((contract) => (
                                <div 
                                  key={contract.id}
                                  className={`p-4 rounded-xl border-2 transition-colors ${
                                    contract.status === 'client_signed' ? 'bg-orange-500/10 border-orange-500/30' :
                                    contract.status === 'fully_signed' || contract.status === 'signed' ? 'bg-green-500/10 border-green-500/30' :
                                    'bg-muted/30 border-border'
                                  }`}
                                  data-testid={`event-contract-${contract.id}`}
                                >
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                        contract.status === 'client_signed' ? 'bg-orange-500/20' :
                                        contract.status === 'fully_signed' || contract.status === 'signed' ? 'bg-green-500/20' :
                                        'bg-neon-purple/20'
                                      }`}>
                                        <FileText className={`w-5 h-5 ${
                                          contract.status === 'client_signed' ? 'text-orange-500' :
                                          contract.status === 'fully_signed' || contract.status === 'signed' ? 'text-green-500' :
                                          'text-neon-purple'
                                        }`} />
                                      </div>
                                      <div>
                                        <p className="font-semibold">{contract.template_name || 'Service Contract'}</p>
                                        <p className="text-sm text-muted-foreground">Created: {new Date(contract.created_at).toLocaleDateString()}</p>
                                      </div>
                                    </div>
                                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                      contract.status === 'client_signed' ? 'bg-orange-500/20 text-orange-500' :
                                      contract.status === 'fully_signed' || contract.status === 'signed' ? 'bg-green-500/20 text-green-500' :
                                      'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      {contract.status === 'fully_signed' ? 'Fully Signed' :
                                       contract.status === 'client_signed' ? 'Awaiting Your Signature' :
                                       contract.status === 'signed' ? 'Signed' :
                                       'Pending Client'}
                                    </span>
                                  </div>

                                  {/* Signature Status */}
                                  <div className="grid md:grid-cols-2 gap-3 mb-4">
                                    {/* Client Signature */}
                                    <div className={`p-3 rounded-lg border ${
                                      contract.client_signed_at ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-muted/20'
                                    }`}>
                                      <p className="text-xs text-muted-foreground mb-1">Client Signature</p>
                                      {contract.client_signed_at ? (
                                        <div>
                                          <p className="text-lg italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                                            {contract.client_signer_name || contract.client_signature}
                                          </p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {new Date(contract.client_signed_at).toLocaleString()}
                                          </p>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-muted-foreground italic">Not signed</p>
                                      )}
                                    </div>

                                    {/* Company Signature */}
                                    <div className={`p-3 rounded-lg border ${
                                      contract.company_signed_at ? 'border-green-500/30 bg-green-500/5' : 'border-border bg-muted/20'
                                    }`}>
                                      <p className="text-xs text-muted-foreground mb-1">Company Signature</p>
                                      {contract.company_signed_at ? (
                                        <div>
                                          <p className="text-lg italic" style={{ fontFamily: "'Dancing Script', cursive" }}>
                                            {contract.company_signer_name || contract.company_signature}
                                          </p>
                                          <p className="text-xs text-muted-foreground mt-1">
                                            {new Date(contract.company_signed_at).toLocaleString()}
                                          </p>
                                        </div>
                                      ) : (
                                        <p className="text-sm text-muted-foreground italic">
                                          {contract.client_signed_at ? 'Awaiting your signature' : 'Waiting for client'}
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex items-center gap-2 pt-2 border-t border-border">
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => downloadContractPdf(contract)}
                                      className="flex-1 text-muted-foreground hover:text-foreground"
                                      data-testid={`download-contract-btn-${contract.id}`}
                                    >
                                      <Download className="w-4 h-4 mr-1" />
                                      Download
                                    </Button>
                                    {contract.status === 'client_signed' && (
                                      <Button
                                        size="sm"
                                        onClick={() => openCounterSignDialog(contract)}
                                        className="flex-1 bg-orange-500 hover:bg-orange-600 text-white"
                                        data-testid={`counter-sign-btn-${contract.id}`}
                                      >
                                        <Edit className="w-4 h-4 mr-1" />
                                        Counter-Sign Now
                                      </Button>
                                    )}
                                    {contract.status !== 'fully_signed' && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeleteContract(contract)}
                                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                        data-testid={`delete-contract-btn-${contract.id}`}
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-12 text-muted-foreground">
                              <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                              <p className="text-lg font-medium">No Contracts Yet</p>
                              <p className="text-sm mb-4">Generate a contract to send to your client for signing</p>
                              <Button
                                onClick={() => setShowGenerateContractDialog(true)}
                                className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                                data-testid="generate-contract-empty-btn"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Generate Contract
                              </Button>
                            </div>
                          )}

                          {/* Contract Alert for Pending Signatures */}
                          {eventContracts.some(c => c.status === 'client_signed') && (
                            <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
                              <div className="flex items-center gap-3">
                                <AlertTriangle className="w-6 h-6 text-orange-500" />
                                <div>
                                  <p className="font-medium text-orange-500">Action Required</p>
                                  <p className="text-sm text-muted-foreground">
                                    Client has signed - please counter-sign to finalize the contract
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Payments Section */}
                      {initialEventTab === 'payments' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg">Payment History</h3>
                              <p className="text-sm text-muted-foreground">
                                Total: ${eventDetailsBooking?.total_amount || 0} | 
                                Paid: <span className="text-green-500 font-medium">${eventDetailsBooking?.paid_amount || 0}</span> | 
                                Balance: <span className={`font-medium ${(eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0) > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                                  ${(eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0)}
                                </span>
                              </p>
                            </div>
                            <div className="flex gap-2">
                              {(eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0) > 0 && paymentSettings?.processor && (
                                <Button 
                                  size="sm" 
                                  onClick={async () => {
                                    try {
                                      const balance = (eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0);
                                      
                                      if (paymentSettings.processor === 'stripe' && paymentSettings.stripe_configured) {
                                        const response = await axios.post(`${API}/stripe/create-checkout?booking_id=${eventDetailsBooking.id}`, {
                                          origin_url: window.location.origin
                                        });
                                        window.open(response.data.checkout_url, '_blank');
                                      } else if (paymentSettings.processor === 'square' && paymentSettings.square_configured) {
                                        // Use the client portal payment endpoint which handles Square
                                        const response = await axios.post(`${API}/client-portal/${eventDetailsBooking.client_portal_token}/create-payment`, {
                                          amount: balance,
                                          description: window.location.origin
                                        });
                                        if (response.data.checkout_url) {
                                          window.open(response.data.checkout_url, '_blank');
                                        } else {
                                          toast.error('Failed to create Square checkout');
                                        }
                                      } else {
                                        toast.error(`${paymentSettings.processor} is not properly configured`);
                                      }
                                    } catch (error) {
                                      toast.error(error.response?.data?.detail || 'Failed to create checkout');
                                    }
                                  }} 
                                  className="bg-purple-500 text-white hover:bg-purple-600" 
                                  data-testid="payment-checkout-btn"
                                >
                                  <CreditCard className="w-4 h-4 mr-1" />
                                  {paymentSettings?.processor === 'square' ? 'Pay with Square' : 'Pay with Card'}
                                </Button>
                              )}
                        <Button size="sm" onClick={openAddPaymentDialog} className="bg-green-500 text-white hover:bg-green-600" data-testid="add-payment-btn">
                          <Plus className="w-4 h-4 mr-1" />
                          Add Payment
                        </Button>
                      </div>
                    </div>
                    
                    <div id="print-payments" className="space-y-2">
                      {eventPayments.length > 0 ? (
                        eventPayments.map((payment) => (
                          <div 
                            key={payment.id}
                            className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                            data-testid={`payment-item-${payment.id}`}
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                                <DollarSign className="w-5 h-5 text-green-500" />
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-green-500">${payment.amount}</span>
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                                    {payment.payment_method}
                                  </span>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {payment.payment_date ? formatDate(payment.payment_date) : 'No date'}
                                  {payment.reference_number && ` • Ref: ${payment.reference_number}`}
                                </div>
                                {payment.notes && (
                                  <p className="text-xs text-muted-foreground mt-1">{payment.notes}</p>
                                )}
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => handleDeletePayment(payment.id, payment.amount)} 
                              className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              data-testid={`delete-payment-btn-${payment.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-8 text-muted-foreground">
                          <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
                          <p>No payments recorded yet</p>
                          <p className="text-sm">Click "Add Payment" to record a payment</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Payment Summary */}
                    {eventPayments.length > 0 && (
                      <div className="mt-4 p-4 rounded-xl bg-muted/20 border border-border">
                        <h4 className="font-medium mb-2">Payment Summary</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-muted-foreground">Total Amount</span>
                            <p className="font-semibold">${eventDetailsBooking?.total_amount || 0}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Total Paid</span>
                            <p className="font-semibold text-green-500">${eventDetailsBooking?.paid_amount || 0}</p>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Remaining</span>
                            <p className={`font-semibold ${(eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0) > 0 ? 'text-yellow-500' : 'text-green-500'}`}>
                              ${(eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.paid_amount || 0)}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                        </div>
                      )}

                      {/* Expenses Section (Private - Not visible to clients) */}
                      {initialEventTab === 'expenses' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-lg flex items-center gap-2">
                                Expenses & Payouts
                                <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">Private</span>
                              </h3>
                              <p className="text-sm text-muted-foreground">Track costs for this event (not visible to client)</p>
                            </div>
                            <Button size="sm" onClick={() => openExpenseDialog()} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="add-expense-btn">
                              <Plus className="w-4 h-4 mr-1" />
                              Add Expense
                            </Button>
                          </div>

                          {/* Profit Summary Cards */}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                              <span className="text-sm text-muted-foreground">Revenue</span>
                              <p className="text-2xl font-bold text-green-500">${expenseSummary.total_revenue.toFixed(2)}</p>
                            </div>
                            <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                              <span className="text-sm text-muted-foreground">Total Expenses</span>
                              <p className="text-2xl font-bold text-red-500">${expenseSummary.total_expenses.toFixed(2)}</p>
                            </div>
                            <div className={`p-4 rounded-lg ${expenseSummary.profit >= 0 ? 'bg-neon-blue/10 border-neon-blue/30' : 'bg-red-500/10 border-red-500/30'} border`}>
                              <span className="text-sm text-muted-foreground">Profit</span>
                              <p className={`text-2xl font-bold ${expenseSummary.profit >= 0 ? 'text-neon-blue' : 'text-red-500'}`}>
                                ${expenseSummary.profit.toFixed(2)}
                              </p>
                            </div>
                          </div>

                          {/* Expense List */}
                          {eventExpenses.length > 0 ? (
                            <div className="space-y-2">
                              {eventExpenses.map((expense) => (
                                <div
                                  key={expense.id}
                                  className="flex items-center justify-between p-3 rounded-lg bg-card border border-border hover:border-purple-500/30 transition-colors"
                                  data-testid={`expense-${expense.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                                      expense.category === 'Employee Pay' ? 'bg-blue-500/20 text-blue-400' :
                                      expense.category === 'Equipment Rental' ? 'bg-orange-500/20 text-orange-400' :
                                      expense.category === 'Travel' ? 'bg-green-500/20 text-green-400' :
                                      expense.category === 'Subcontractor' ? 'bg-purple-500/20 text-purple-400' :
                                      'bg-gray-500/20 text-gray-400'
                                    }`}>
                                      <CreditCard className="w-5 h-5" />
                                    </div>
                                    <div>
                                      <p className="font-medium">{expense.description}</p>
                                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                        <span className="px-2 py-0.5 rounded bg-muted text-xs">{expense.category}</span>
                                        {expense.date && <span>{expense.date}</span>}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <span className="font-semibold text-red-400">-${expense.amount.toFixed(2)}</span>
                                    <div className="flex items-center gap-1">
                                      <Button variant="ghost" size="sm" onClick={() => openExpenseDialog(expense)} className="h-8 w-8 p-0">
                                        <Edit className="w-4 h-4" />
                                      </Button>
                                      <Button variant="ghost" size="sm" onClick={() => deleteExpense(expense.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-400">
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="text-center py-12 border border-dashed border-border rounded-lg">
                              <CreditCard className="w-12 h-12 mx-auto mb-3 text-muted-foreground/50" />
                              <p className="text-muted-foreground">No expenses recorded yet</p>
                              <p className="text-sm text-muted-foreground/70">Add employee pay, equipment rentals, and other costs</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Documents Section */}
                      {initialEventTab === 'documents' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg">Event Documents</h3>
                      <div className="flex gap-2">
                        {eventDocuments.length > 0 && (
                          <Button size="sm" variant="outline" onClick={handlePrintDocuments} className="border-border">
                            <Printer className="w-4 h-4 mr-1" />
                            Print List
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          onClick={() => setShowDocUploadDialog(true)} 
                          className="bg-neon-blue text-black hover:bg-neon-blue/80"
                        >
                          <Upload className="w-4 h-4 mr-1" />
                          Upload
                        </Button>
                      </div>
                    </div>
                    
                    {eventDocuments.length > 0 ? (
                      <div className="space-y-3">
                        {eventDocuments.map((doc) => (
                          <div
                            key={doc.id}
                            className="flex items-center justify-between p-4 bg-muted/30 rounded-xl"
                            data-testid={`document-${doc.id}`}
                          >
                            <div className="flex items-center gap-4 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-lg bg-neon-purple/20 flex items-center justify-center flex-shrink-0">
                                <File className="w-5 h-5 text-neon-purple" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium truncate">{doc.original_filename}</p>
                                {doc.note && (
                                  <p className="text-sm text-neon-blue truncate">{doc.note}</p>
                                )}
                                <p className="text-xs text-muted-foreground">
                                  {formatFileSize(doc.file_size)} • 
                                  Uploaded by {doc.uploaded_by_name} • 
                                  {doc.uploaded_by === 'client' ? (
                                    <span className="text-amber-500">Client</span>
                                  ) : (
                                    <span className="text-neon-blue">DJ</span>
                                  )}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => window.open(doc.file_url, '_blank')}
                                className="text-muted-foreground hover:text-foreground"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  const link = document.createElement('a');
                                  link.href = doc.file_url;
                                  link.download = doc.original_filename;
                                  link.click();
                                }}
                                className="text-muted-foreground hover:text-foreground"
                                title="Download"
                              >
                                <Download className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteDocument(doc.id)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-12 bg-muted/20 rounded-xl">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted/30 flex items-center justify-center">
                          <File className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <p className="text-lg font-medium text-muted-foreground">No documents yet</p>
                        <p className="text-sm text-muted-foreground mb-4">Upload documents for this event</p>
                        <Button 
                          onClick={() => setShowDocUploadDialog(true)}
                          className="bg-neon-blue text-black hover:bg-neon-blue/80"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Upload Document
                        </Button>
                      </div>
                    )}
                        </div>
                      )}

                      {/* Messages Section */}
                      {initialEventTab === 'messages' && (
                        <MessagesTab
                          booking={eventDetailsBooking}
                          messages={eventMessages}
                          onMessagesUpdate={refreshMessages}
                        />
                      )}
                      
                      {/* Add-on Requests Section */}
                      {initialEventTab === 'addon-requests' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              <Plus className="w-5 h-5 text-neon-purple" />
                              Add-on Requests
                            </h3>
                          </div>
                          
                          <p className="text-sm text-muted-foreground">
                            Review and approve add-on requests from your client. Approved add-ons will be added to the booking and the client will be notified.
                          </p>

                          {addonRequests.length === 0 ? (
                            <div className="text-center py-12 bg-muted/20 rounded-xl">
                              <Plus className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                              <h3 className="font-semibold text-lg mb-2">No Pending Requests</h3>
                              <p className="text-muted-foreground text-sm">
                                When your client requests add-ons from their portal, they'll appear here for your approval.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {addonRequests.map((request) => (
                                <div 
                                  key={request.id}
                                  className="border border-border rounded-xl p-4 bg-card"
                                  data-testid={`addon-request-item-${request.id}`}
                                >
                                  <div className="flex items-start justify-between gap-4 mb-3">
                                    <div>
                                      <h4 className="font-semibold text-lg">{request.addon_name}</h4>
                                      <p className="text-sm text-muted-foreground">
                                        Requested {new Date(request.requested_at).toLocaleDateString()} at {new Date(request.requested_at).toLocaleTimeString()}
                                      </p>
                                      {request.client_notes && (
                                        <div className="mt-2 p-2 bg-muted/30 rounded-lg">
                                          <p className="text-sm"><span className="font-medium">Client note:</span> {request.client_notes}</p>
                                        </div>
                                      )}
                                    </div>
                                    <div className="text-right">
                                      <p className="text-lg font-bold text-neon-purple">${request.default_price}</p>
                                      <p className="text-xs text-muted-foreground">Default price</p>
                                    </div>
                                  </div>

                                  {processingAddonRequest === request.id ? (
                                    <div className="space-y-3 border-t border-border pt-3">
                                      <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                          <Label>Approved Price ($)</Label>
                                          <Input
                                            type="number"
                                            step="0.01"
                                            value={addonApprovalPrice}
                                            onChange={(e) => setAddonApprovalPrice(e.target.value)}
                                            placeholder={request.default_price.toString()}
                                            className="bg-background"
                                            data-testid="addon-approval-price"
                                          />
                                          <p className="text-xs text-muted-foreground">Leave blank to use default price</p>
                                        </div>
                                        <div className="space-y-2">
                                          <Label>Note to Client (optional)</Label>
                                          <Input
                                            value={addonApprovalNotes}
                                            onChange={(e) => setAddonApprovalNotes(e.target.value)}
                                            placeholder="e.g., Special rate applied"
                                            className="bg-background"
                                            data-testid="addon-approval-notes"
                                          />
                                        </div>
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <Button
                                          variant="outline"
                                          onClick={() => {
                                            setProcessingAddonRequest(null);
                                            setAddonApprovalPrice('');
                                            setAddonApprovalNotes('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          onClick={() => handleDeclineAddonRequest(request)}
                                          data-testid="decline-addon-btn"
                                        >
                                          <X className="w-4 h-4 mr-2" />
                                          Decline
                                        </Button>
                                        <Button
                                          onClick={() => handleApproveAddonRequest(request)}
                                          className="bg-green-600 hover:bg-green-500 text-white"
                                          data-testid="approve-addon-btn"
                                        >
                                          <Check className="w-4 h-4 mr-2" />
                                          Approve
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end">
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          setProcessingAddonRequest(request.id);
                                          setAddonApprovalPrice('');
                                          setAddonApprovalNotes('');
                                        }}
                                        className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                                        data-testid="review-addon-btn"
                                      >
                                        Review Request
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Venue Changes Section */}
                      {initialEventTab === 'venue-changes' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              <MapPin className="w-5 h-5 text-emerald-500" />
                              Venue Change Requests
                            </h3>
                          </div>
                          
                          <p className="text-sm text-muted-foreground">
                            Review and approve venue change requests from your client. Approved changes will update the booking and the client will be notified.
                          </p>

                          {venueChangeRequests.length === 0 ? (
                            <div className="text-center py-12 bg-muted/20 rounded-xl">
                              <MapPin className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                              <h3 className="font-semibold text-lg mb-2">No Pending Requests</h3>
                              <p className="text-muted-foreground text-sm">
                                When your client requests a venue change from their portal, it'll appear here for your approval.
                              </p>
                            </div>
                          ) : (
                            <div className="space-y-4">
                              {venueChangeRequests.map((request) => (
                                <div 
                                  key={request.id}
                                  className="border border-border rounded-xl p-4 bg-card"
                                  data-testid={`venue-change-item-${request.id}`}
                                >
                                  <div className="flex items-start justify-between gap-4 mb-3">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm text-muted-foreground">Current:</span>
                                        <span className="font-medium">{request.current_venue_name}</span>
                                      </div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-sm text-muted-foreground">Requested:</span>
                                        <span className="font-semibold text-emerald-500">{request.new_venue_name}</span>
                                      </div>
                                      {request.new_venue_address && (
                                        <p className="text-sm text-muted-foreground">
                                          <span className="font-medium">Address:</span> {request.new_venue_address}
                                        </p>
                                      )}
                                      <p className="text-xs text-muted-foreground mt-2">
                                        Requested {new Date(request.requested_at).toLocaleDateString()} at {new Date(request.requested_at).toLocaleTimeString()}
                                      </p>
                                      {request.client_notes && (
                                        <div className="mt-2 p-2 bg-muted/30 rounded-lg">
                                          <p className="text-sm"><span className="font-medium">Client note:</span> {request.client_notes}</p>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {processingVenueChange === request.id ? (
                                    <div className="space-y-3 border-t border-border pt-3">
                                      <div className="space-y-2">
                                        <Label>Note to Client (optional)</Label>
                                        <Input
                                          value={venueChangeNotes}
                                          onChange={(e) => setVenueChangeNotes(e.target.value)}
                                          placeholder="e.g., Venue confirmed with our team"
                                          className="bg-background"
                                          data-testid="venue-change-notes"
                                        />
                                      </div>
                                      <div className="flex justify-end gap-2">
                                        <Button
                                          variant="outline"
                                          onClick={() => {
                                            setProcessingVenueChange(null);
                                            setVenueChangeNotes('');
                                          }}
                                        >
                                          Cancel
                                        </Button>
                                        <Button
                                          variant="destructive"
                                          onClick={() => handleDeclineVenueChange(request)}
                                          data-testid="decline-venue-change-btn"
                                        >
                                          <X className="w-4 h-4 mr-2" />
                                          Decline
                                        </Button>
                                        <Button
                                          onClick={() => handleApproveVenueChange(request)}
                                          className="bg-emerald-600 hover:bg-emerald-500 text-white"
                                          data-testid="approve-venue-change-btn"
                                        >
                                          <Check className="w-4 h-4 mr-2" />
                                          Approve
                                        </Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="flex justify-end">
                                      <Button
                                        size="sm"
                                        onClick={() => {
                                          setProcessingVenueChange(request.id);
                                          setVenueChangeNotes('');
                                        }}
                                        className="bg-emerald-500 hover:bg-emerald-400 text-white"
                                        data-testid="review-venue-change-btn"
                                      >
                                        Review Request
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Communications Section */}
                      {initialEventTab === 'communications' && (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-lg flex items-center gap-2">
                              <Mail className="w-5 h-5 text-neon-purple" />
                              Email Communications
                            </h3>
                            <Button 
                              size="sm" 
                              onClick={() => {
                                setEmailRecipient({
                                  email: eventDetailsBooking?.client_email,
                                  name: eventDetailsBooking?.client_name,
                                  contact_id: eventDetailsBooking?.contact_id,
                                  booking_id: eventDetailsBooking?.id
                                });
                                setShowEmailDialog(true);
                              }}
                              className="bg-neon-purple text-white hover:bg-neon-purple/80"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Send Email
                            </Button>
                          </div>
                          
                          <p className="text-sm text-muted-foreground">
                            View and send emails to your client. All emails sent are logged here for reference.
                          </p>
                          
                          <CommunicationsLog 
                            bookingId={eventDetailsBooking?.id}
                            contactId={eventDetailsBooking?.contact_id}
                            refreshTrigger={communicationsRefresh}
                          />
                        </div>
                      )}
                      
                      {/* Activity Notes Section */}
                      {initialEventTab === 'notes' && (
                        <ActivityNotesTab
                          booking={eventDetailsBooking}
                          notes={eventActivityLog}
                          onNotesUpdate={refreshActivityNotes}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Playlist Editor Dialog */}
          <Dialog open={showPlaylistDialog} onOpenChange={setShowPlaylistDialog}>
            <DialogContent className="bg-card border border-border max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="playlist-editor-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <Music className="w-5 h-5 text-primary" />
                  {editingPlaylist ? 'Edit Playlist' : 'Create Playlist'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Playlist Name</Label>
                  <Input
                    value={playlistName}
                    onChange={(e) => setPlaylistName(e.target.value)}
                    placeholder="e.g., Must Play, Do Not Play, First Dance"
                    className="bg-background border-border text-foreground"
                  />
                </div>
                
                {/* Designation Legend */}
                <div className="flex flex-wrap gap-3 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-green-500"></span>
                    <span className="text-muted-foreground">Must Play</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                    <span className="text-muted-foreground">Play if Possible</span>
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full bg-red-500"></span>
                    <span className="text-muted-foreground">Do Not Play</span>
                  </span>
                </div>
                
                {/* Songs List - Grouped by Designation */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground">Songs</Label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={openAddSongModal}
                      className="border-border"
                      data-testid="dj-add-song-top-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Song
                    </Button>
                  </div>
                  <div className="space-y-4">
                    {(() => {
                      const designationOrder = ['must_play', 'play_if_possible', 'do_not_play'];
                      const designationLabels = {
                        'must_play': 'Must Play',
                        'play_if_possible': 'Play if Possible',
                        'do_not_play': 'Do Not Play'
                      };
                      const designationColors = {
                        'must_play': 'bg-green-500',
                        'play_if_possible': 'bg-amber-500',
                        'do_not_play': 'bg-red-500'
                      };
                      
                      // Sort songs by designation, then artist, then title
                      const sortedSongs = [...playlistSongs].map((s, origIdx) => ({ ...s, origIdx }));
                      sortedSongs.sort((a, b) => {
                        const desigA = designationOrder.indexOf(a.designation || 'must_play');
                        const desigB = designationOrder.indexOf(b.designation || 'must_play');
                        if (desigA !== desigB) return desigA - desigB;
                        const artistCmp = (a.artist || '').toLowerCase().localeCompare((b.artist || '').toLowerCase());
                        if (artistCmp !== 0) return artistCmp;
                        return (a.title || '').toLowerCase().localeCompare((b.title || '').toLowerCase());
                      });
                      
                      // Group by designation
                      const groups = {};
                      sortedSongs.forEach(song => {
                        const desig = song.designation || 'must_play';
                        if (!groups[desig]) groups[desig] = [];
                        groups[desig].push(song);
                      });
                      
                      return designationOrder.map(desig => {
                        const songs = groups[desig] || [];
                        if (songs.length === 0) return null;
                        
                        return (
                          <div key={desig} className="space-y-2">
                            {/* Group Header */}
                            <div className="flex items-center gap-2 py-2 border-b border-border">
                              <div className={`w-3 h-3 rounded-full ${designationColors[desig]}`}></div>
                              <span className="text-muted-foreground font-medium text-sm">{designationLabels[desig]}</span>
                              <span className="text-muted-foreground/60 text-xs">({songs.length} songs)</span>
                            </div>
                            
                            {/* Songs in this group */}
                            {songs.map((song) => {
                              const idx = song.origIdx;
                              const isMissing = missingSongs[`${song.title?.toLowerCase()}-${song.artist?.toLowerCase()}`];
                              return (
                                <div key={idx} className="flex gap-2 items-start pl-2">
                                  {/* Designation Indicator */}
                                  <div className={`w-1 rounded-full self-stretch ${designationColors[song.designation || 'must_play']}`}></div>
                                  <div className="flex-1 space-y-1">
                                    <div className="flex gap-2 items-center">
                                      <Input
                                        value={song.title}
                                        onChange={(e) => updatePlaylistSong(idx, 'title', e.target.value)}
                                        placeholder="Song title"
                                        className={`flex-1 bg-background border-border text-foreground h-9 ${isMissing ? 'border-orange-500/50' : ''}`}
                                      />
                                      <Input
                                        value={song.artist}
                                        onChange={(e) => updatePlaylistSong(idx, 'artist', e.target.value)}
                                        placeholder="Artist"
                                        className={`w-32 bg-background border-border text-foreground h-9 ${isMissing ? 'border-orange-500/50' : ''}`}
                                      />
                                      {isMissing && (
                                        <span className="text-orange-400 text-xs whitespace-nowrap flex items-center gap-1" title="Not in your music library">
                                          <AlertCircle className="w-3 h-3" />
                                          Missing
                                        </span>
                                      )}
                                      <select
                                        value={song.designation || 'must_play'}
                                        onChange={(e) => updatePlaylistSong(idx, 'designation', e.target.value)}
                                        className="bg-background border border-border rounded-md px-2 text-foreground text-xs h-9 min-w-[120px]"
                                        data-testid={`dj-song-designation-${idx}`}
                                      >
                                        <option value="must_play">Must Play</option>
                                        <option value="play_if_possible">Play if Possible</option>
                                        <option value="do_not_play">Do Not Play</option>
                                      </select>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removePlaylistSong(idx)}
                                        className="text-red-500 hover:text-red-400 h-9 w-9 p-0"
                                        title="Delete song"
                                        data-testid={`dj-delete-song-${idx}`}
                                      >
                                        <X className="w-4 h-4" />
                                      </Button>
                                    </div>
                                    {/* Comment field */}
                                    <Input
                                      value={song.comment || ''}
                                      onChange={(e) => updatePlaylistSong(idx, 'comment', e.target.value)}
                                      placeholder="Add a note (e.g., 'For first dance', 'Client request')"
                                      className="bg-muted/20 border-border/50 text-muted-foreground text-sm h-8 placeholder:text-muted-foreground/50"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      });
                    })()}
                    
                    <Button variant="outline" onClick={openAddSongModal} className="w-full border-dashed border-border" data-testid="dj-add-song-bottom-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      Add Song
                    </Button>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPlaylistDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button onClick={savePlaylist} className="bg-neon-blue text-black hover:bg-neon-blue/80">
                    <Save className="w-4 h-4 mr-2" />
                    Save Playlist
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Song Modal - DJ Side */}
          <Dialog open={showAddSongModal} onOpenChange={(open) => {
            if (!open) {
              setModalSearchQuery('');
              setModalSearchResults([]);
              setShowModalSearchResults(false);
            }
            setShowAddSongModal(open);
          }}>
            <DialogContent className="bg-card border border-border max-w-md" data-testid="dj-add-song-modal">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg flex items-center gap-2 text-foreground">
                  <Music className="w-5 h-5 text-primary" />
                  Add Song
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {/* Song Search */}
                <div className="space-y-2 relative">
                  <div className="flex items-center justify-between">
                    <Label className="text-foreground flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      Search for a song
                    </Label>
                    {spotifyConfigured && (
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setSearchSource('spotify')}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all flex items-center gap-1 ${
                            searchSource === 'spotify'
                              ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                          </svg>
                          Spotify
                        </button>
                        <button
                          type="button"
                          onClick={() => setSearchSource('musicbrainz')}
                          className={`px-2 py-1 rounded text-xs font-medium transition-all ${
                            searchSource === 'musicbrainz'
                              ? 'bg-amber-500/20 text-amber-400 border border-amber-500/50'
                              : 'text-muted-foreground hover:text-foreground'
                          }`}
                        >
                          MusicBrainz
                        </button>
                      </div>
                    )}
                  </div>
                  <Input
                    value={modalSearchQuery}
                    onChange={(e) => searchSongsForModal(e.target.value)}
                    placeholder={spotifyConfigured && searchSource === 'spotify' ? "Search Spotify..." : "Type to search songs..."}
                    className="bg-background border-border text-foreground"
                    data-testid="dj-add-song-search-input"
                    autoFocus
                  />
                  {/* Search Results Dropdown */}
                  {showModalSearchResults && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-card border border-border rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {modalSearching ? (
                        <p className="p-3 text-muted-foreground text-sm flex items-center gap-2">
                          <span className="w-4 h-4 border-2 border-muted-foreground/30 border-t-muted-foreground rounded-full animate-spin"></span>
                          Searching {spotifyConfigured && searchSource === 'spotify' ? 'Spotify' : 'MusicBrainz'}...
                        </p>
                      ) : modalSearchResults.length > 0 ? (
                        modalSearchResults.map((result, idx) => (
                          <button
                            key={idx}
                            onClick={() => selectSongFromModalSearch(result)}
                            className="w-full text-left p-3 hover:bg-muted/50 transition-colors border-b border-border last:border-b-0 flex items-center gap-3"
                            data-testid={`dj-search-result-${idx}`}
                          >
                            {result.image_url && (
                              <img src={result.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-foreground font-medium text-sm truncate">{result.title}</p>
                              <p className="text-muted-foreground text-xs truncate">{result.artist}</p>
                              {result.album && <p className="text-muted-foreground/60 text-xs truncate">{result.album}</p>}
                            </div>
                          </button>
                        ))
                      ) : modalSearchQuery.length >= 2 ? (
                        <p className="p-3 text-muted-foreground text-sm">No results found</p>
                      ) : null}
                    </div>
                  )}
                  <p className="text-muted-foreground text-xs">
                    {spotifyConfigured ? (
                      searchSource === 'spotify' ? 'Searching Spotify • Or enter manually below' : 'Searching MusicBrainz • Or enter manually below'
                    ) : 'Or enter manually below'}
                  </p>
                </div>

                <div className="border-t border-border pt-4 space-y-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Song Title <span className="text-red-500">*</span></Label>
                    <Input
                      value={newSong.title}
                      onChange={(e) => setNewSong({ ...newSong, title: e.target.value })}
                      placeholder="Enter song title"
                      className="bg-background border-border text-foreground"
                      data-testid="dj-add-song-title-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-foreground">Artist</Label>
                    <Input
                      value={newSong.artist}
                      onChange={(e) => setNewSong({ ...newSong, artist: e.target.value })}
                      placeholder="Enter artist name"
                      className="bg-background border-border text-foreground"
                      data-testid="dj-add-song-artist-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Designation <span className="text-red-500">*</span></Label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setNewSong({ ...newSong, designation: 'must_play' })}
                      className={`p-3 rounded-lg border transition-all ${
                        newSong.designation === 'must_play'
                          ? 'border-green-500 bg-green-500/20 text-green-400'
                          : 'border-border text-muted-foreground hover:border-muted-foreground'
                      }`}
                      data-testid="dj-add-song-must-play-btn"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-green-500"></span>
                        <span className="text-xs font-medium">Must Play</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewSong({ ...newSong, designation: 'play_if_possible' })}
                      className={`p-3 rounded-lg border transition-all ${
                        newSong.designation === 'play_if_possible'
                          ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                          : 'border-border text-muted-foreground hover:border-muted-foreground'
                      }`}
                      data-testid="dj-add-song-play-if-possible-btn"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-amber-500"></span>
                        <span className="text-xs font-medium">Play if Possible</span>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewSong({ ...newSong, designation: 'do_not_play' })}
                      className={`p-3 rounded-lg border transition-all ${
                        newSong.designation === 'do_not_play'
                          ? 'border-red-500 bg-red-500/20 text-red-400'
                          : 'border-border text-muted-foreground hover:border-muted-foreground'
                      }`}
                      data-testid="dj-add-song-do-not-play-btn"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="w-3 h-3 rounded-full bg-red-500"></span>
                        <span className="text-xs font-medium">Do Not Play</span>
                      </div>
                    </button>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Comment (optional)</Label>
                  <Input
                    value={newSong.comment}
                    onChange={(e) => setNewSong({ ...newSong, comment: e.target.value })}
                    placeholder="e.g., 'For first dance', 'Client request'"
                    className="bg-background border-border text-foreground"
                    data-testid="dj-add-song-comment-input"
                  />
                </div>
                
                <DialogFooter className="pt-4">
                  <Button 
                    variant="outline" 
                    onClick={() => setShowAddSongModal(false)}
                    className="border-border"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAddSongFromModal}
                    disabled={!newSong.title.trim() || !newSong.designation}
                    className="bg-neon-blue text-black hover:bg-neon-blue/80"
                    data-testid="dj-add-song-confirm-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Song
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Timeline Event Editor Dialog */}
          <Dialog open={showTimelineDialog} onOpenChange={setShowTimelineDialog}>
            <DialogContent className="bg-card border border-border max-w-lg" data-testid="timeline-editor-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <Clock className="w-5 h-5 text-primary" />
                  {editingTimelineEvent ? 'Edit Event' : 'Add Timeline Event'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Time</Label>
                  <Input
                    type="time"
                    value={timelineEventData.time}
                    onChange={(e) => setTimelineEventData({...timelineEventData, time: e.target.value})}
                    className="bg-background border-border text-foreground"
                    style={{ borderColor: primaryColor + '40' }}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Event Title</Label>
                  <Input
                    value={timelineEventData.title}
                    onChange={(e) => setTimelineEventData({...timelineEventData, title: e.target.value})}
                    placeholder="e.g., Ceremony Begins, First Dance"
                    className="bg-background border-border text-foreground"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Description (Optional)</Label>
                  <textarea
                    value={timelineEventData.description}
                    onChange={(e) => setTimelineEventData({...timelineEventData, description: e.target.value})}
                    placeholder="Additional details..."
                    className="w-full min-h-[80px] p-3 bg-background border border-border rounded-xl text-foreground resize-none"
                  />
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowTimelineDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button onClick={saveTimelineEvent} className="bg-primary text-primary-foreground hover:bg-primary/80">
                    <Save className="w-4 h-4 mr-2" />
                    Save Event
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Spotify Import Dialog */}
          <Dialog open={showSpotifyDialog} onOpenChange={setShowSpotifyDialog}>
            <DialogContent className="bg-card border border-border max-w-lg" data-testid="spotify-import-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <svg className="w-5 h-5 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
                  </svg>
                  Import from Spotify
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Spotify Playlist URL</Label>
                  <div className="flex gap-2">
                    <Input
                      value={spotifyUrl}
                      onChange={(e) => setSpotifyUrl(e.target.value)}
                      placeholder="https://open.spotify.com/playlist/..."
                      className="bg-background border-border text-foreground"
                      data-testid="spotify-url-input"
                    />
                    <Button 
                      onClick={previewSpotifyPlaylist} 
                      disabled={importingSpotify || !spotifyUrl.trim()}
                      variant="outline"
                      className="border-border"
                    >
                      {importingSpotify ? 'Loading...' : 'Preview'}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Paste the share link from any public Spotify playlist
                  </p>
                </div>

                {spotifyPreview && (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      {spotifyPreview.image_url && (
                        <img 
                          src={spotifyPreview.image_url} 
                          alt={spotifyPreview.name}
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground">{spotifyPreview.name}</h4>
                        <p className="text-sm text-muted-foreground">by {spotifyPreview.owner}</p>
                        <p className="text-sm text-green-400 font-medium">{spotifyPreview.total_tracks} tracks</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 pt-2 border-t border-green-500/20">
                      <Label className="text-foreground text-sm">Import to:</Label>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant={spotifyImportMode === 'new' ? 'default' : 'outline'}
                          onClick={() => setSpotifyImportMode('new')}
                          className={spotifyImportMode === 'new' ? 'bg-green-500 text-white hover:bg-green-600' : 'border-border'}
                        >
                          New Playlist
                        </Button>
                        <Button
                          size="sm"
                          variant={spotifyImportMode === 'existing' ? 'default' : 'outline'}
                          onClick={() => setSpotifyImportMode('existing')}
                          disabled={eventPlaylists.length === 0}
                          className={spotifyImportMode === 'existing' ? 'bg-green-500 text-white hover:bg-green-600' : 'border-border'}
                        >
                          Existing Playlist
                        </Button>
                      </div>
                      
                      {spotifyImportMode === 'existing' && eventPlaylists.length > 0 && (
                        <Select value={selectedPlaylistForSpotify} onValueChange={setSelectedPlaylistForSpotify}>
                          <SelectTrigger className="bg-background border-border text-foreground mt-2">
                            <SelectValue placeholder="Select a playlist..." />
                          </SelectTrigger>
                          <SelectContent className="bg-card border-border">
                            {eventPlaylists.map((pl) => (
                              <SelectItem key={pl.id} value={pl.id}>{pl.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowSpotifyDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button 
                    onClick={importSpotifyPlaylist} 
                    disabled={!spotifyPreview || importingSpotify || (spotifyImportMode === 'existing' && !selectedPlaylistForSpotify)}
                    className="bg-green-500 text-white hover:bg-green-600"
                  >
                    {importingSpotify ? 'Importing...' : `Import ${spotifyPreview?.total_tracks || 0} Songs`}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* PDF Import Dialog */}
          <Dialog open={showPdfImportDialog} onOpenChange={(open) => {
            setShowPdfImportDialog(open);
            if (!open) setPdfImportResult(null);
          }}>
            <DialogContent className="bg-card border border-border max-w-lg" data-testid="pdf-import-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <FileText className="w-5 h-5 text-amber-500" />
                  Import Playlist from PDF
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Select PDF File</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    The PDF should have sections labeled "Must Play", "Play if Possible", and/or "Do Not Play" with songs listed as "Artist Name    Song Title"
                  </p>
                  <input
                    ref={pdfInputRef}
                    type="file"
                    accept=".pdf"
                    onChange={handlePdfFileSelect}
                    className="hidden"
                    data-testid="pdf-file-input"
                  />
                  <Button 
                    variant="outline" 
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={importingPdf}
                    className="w-full border-dashed border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                  >
                    {importingPdf ? (
                      <>
                        <span className="w-4 h-4 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin mr-2"></span>
                        Processing PDF...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Choose PDF File
                      </>
                    )}
                  </Button>
                </div>

                {pdfImportResult && (
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 space-y-3">
                    <h4 className="font-semibold text-foreground flex items-center gap-2">
                      <Check className="w-4 h-4 text-green-500" />
                      Songs Found: {pdfImportResult.counts.total}
                    </h4>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div className="bg-green-500/10 rounded-lg p-2 text-center">
                        <span className="block text-green-400 font-bold">{pdfImportResult.counts.must_play}</span>
                        <span className="text-xs text-muted-foreground">Must Play</span>
                      </div>
                      <div className="bg-amber-500/10 rounded-lg p-2 text-center">
                        <span className="block text-amber-400 font-bold">{pdfImportResult.counts.play_if_possible}</span>
                        <span className="text-xs text-muted-foreground">Play if Possible</span>
                      </div>
                      <div className="bg-red-500/10 rounded-lg p-2 text-center">
                        <span className="block text-red-400 font-bold">{pdfImportResult.counts.do_not_play}</span>
                        <span className="text-xs text-muted-foreground">Do Not Play</span>
                      </div>
                    </div>
                    
                    {/* Preview some songs */}
                    <div className="max-h-40 overflow-y-auto space-y-1 pt-2 border-t border-amber-500/20">
                      <p className="text-xs text-muted-foreground mb-1">Preview:</p>
                      {[
                        ...pdfImportResult.songs.must_play.slice(0, 3),
                        ...pdfImportResult.songs.play_if_possible.slice(0, 2),
                        ...pdfImportResult.songs.do_not_play.slice(0, 1)
                      ].map((song, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${
                            song.designation === 'must_play' ? 'bg-green-500' :
                            song.designation === 'play_if_possible' ? 'bg-amber-500' : 'bg-red-500'
                          }`}></span>
                          <span className="truncate">{song.title}</span>
                          <span className="text-muted-foreground truncate">- {song.artist}</span>
                        </div>
                      ))}
                      {pdfImportResult.counts.total > 6 && (
                        <p className="text-xs text-muted-foreground">...and {pdfImportResult.counts.total - 6} more songs</p>
                      )}
                    </div>
                  </div>
                )}

                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPdfImportDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button 
                    onClick={importPdfPlaylist} 
                    disabled={!pdfImportResult || importingPdf}
                    className="bg-amber-500 text-black hover:bg-amber-600"
                  >
                    Import {pdfImportResult?.counts?.total || 0} Songs
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add New Client Dialog */}
          <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
            <DialogContent className="bg-card border border-border max-w-md" data-testid="add-client-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <UserPlus2 className="w-5 h-5 text-primary" />
                  Add New Client
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Client Name *</Label>
                  <Input
                    value={newClientData.name}
                    onChange={(e) => setNewClientData({...newClientData, name: e.target.value})}
                    placeholder="Full name"
                    className="bg-background border-border text-foreground"
                    data-testid="new-client-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Email *</Label>
                  <Input
                    type="email"
                    value={newClientData.email}
                    onChange={(e) => setNewClientData({...newClientData, email: e.target.value})}
                    placeholder="client@example.com"
                    className="bg-background border-border text-foreground"
                    data-testid="new-client-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Phone</Label>
                  <Input
                    value={newClientData.phone}
                    onChange={(e) => setNewClientData({...newClientData, phone: e.target.value})}
                    placeholder="(555) 123-4567"
                    className="bg-background border-border text-foreground"
                    data-testid="new-client-phone-input"
                  />
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddClientDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button onClick={saveNewClient} className="bg-primary text-primary-foreground hover:bg-primary/80">
                    <Save className="w-4 h-4 mr-2" />
                    Add Client & Use
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add New Venue Dialog */}
          <Dialog open={showAddVenueDialog} onOpenChange={setShowAddVenueDialog}>
            <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-venue-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <MapPin className="w-5 h-5 text-primary" />
                  Add New Venue
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Venue Name *</Label>
                    <Input
                      value={newVenueData.name}
                      onChange={(e) => setNewVenueData({...newVenueData, name: e.target.value})}
                      placeholder="Venue name"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Venue Type</Label>
                    <Select value={newVenueData.venue_type} onValueChange={(v) => setNewVenueData({...newVenueData, venue_type: v})}>
                      <SelectTrigger className="bg-background border-border text-foreground" data-testid="new-venue-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        <SelectItem value="Ballroom">Ballroom</SelectItem>
                        <SelectItem value="Outdoor">Outdoor</SelectItem>
                        <SelectItem value="Restaurant">Restaurant</SelectItem>
                        <SelectItem value="Country Club">Country Club</SelectItem>
                        <SelectItem value="Banquet Hall">Banquet Hall</SelectItem>
                        <SelectItem value="Hotel">Hotel</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Venue Image</Label>
                  <div className="flex items-start gap-4">
                    {venueImagePreview ? (
                      <div className="relative">
                        <div className="w-32 h-32 rounded-xl overflow-hidden border border-border bg-muted">
                          <img src={venueImagePreview} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                        <button
                          type="button"
                          onClick={removeVenueImage}
                          className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1.5 hover:bg-red-600 transition-colors shadow-lg"
                          data-testid="remove-venue-image-btn"
                        >
                          <X className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ) : (
                      <label className="w-32 h-32 rounded-xl border-2 border-dashed border-border bg-muted flex flex-col items-center justify-center cursor-pointer hover:border-primary/50 hover:bg-muted/70 transition-all">
                        <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                        <span className="text-xs text-muted-foreground text-center px-2">Click to upload</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleVenueImageUpload}
                          className="hidden"
                          data-testid="venue-image-input"
                        />
                      </label>
                    )}
                    <div className="flex-1 text-xs text-muted-foreground pt-2">
                      <p>Upload a photo of the venue.</p>
                      <p className="mt-1">Max size: 2MB</p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Address</Label>
                  <Input
                    value={newVenueData.address}
                    onChange={(e) => setNewVenueData({...newVenueData, address: e.target.value})}
                    placeholder="123 Main Street"
                    className="bg-background border-border text-foreground"
                    data-testid="new-venue-address-input"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">City</Label>
                    <Input
                      value={newVenueData.city}
                      onChange={(e) => setNewVenueData({...newVenueData, city: e.target.value})}
                      placeholder="City"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-city-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">State</Label>
                    <Input
                      value={newVenueData.state}
                      onChange={(e) => setNewVenueData({...newVenueData, state: e.target.value})}
                      placeholder="State"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-state-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">ZIP</Label>
                    <Input
                      value={newVenueData.zip_code}
                      onChange={(e) => setNewVenueData({...newVenueData, zip_code: e.target.value})}
                      placeholder="ZIP"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-zip-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Capacity</Label>
                    <Input
                      type="number"
                      value={newVenueData.capacity}
                      onChange={(e) => setNewVenueData({...newVenueData, capacity: e.target.value})}
                      placeholder="Guest capacity"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-capacity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Contact Name</Label>
                    <Input
                      value={newVenueData.contact_name}
                      onChange={(e) => setNewVenueData({...newVenueData, contact_name: e.target.value})}
                      placeholder="Venue contact"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-contact-name-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Contact Phone</Label>
                    <Input
                      value={newVenueData.contact_phone}
                      onChange={(e) => setNewVenueData({...newVenueData, contact_phone: e.target.value})}
                      placeholder="Phone number"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-contact-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">Contact Email</Label>
                    <Input
                      type="email"
                      value={newVenueData.contact_email}
                      onChange={(e) => setNewVenueData({...newVenueData, contact_email: e.target.value})}
                      placeholder="Email address"
                      className="bg-background border-border text-foreground"
                      data-testid="new-venue-contact-email-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Parking Info</Label>
                  <Input
                    value={newVenueData.parking_info}
                    onChange={(e) => setNewVenueData({...newVenueData, parking_info: e.target.value})}
                    placeholder="e.g., Valet available, parking garage"
                    className="bg-background border-border text-foreground"
                    data-testid="new-venue-parking-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Setup Notes</Label>
                  <textarea
                    value={newVenueData.setup_notes}
                    onChange={(e) => setNewVenueData({...newVenueData, setup_notes: e.target.value})}
                    placeholder="Equipment access, load-in details, etc."
                    className="w-full min-h-[60px] bg-background border border-border rounded-lg p-3 text-foreground"
                    data-testid="new-venue-setup-notes-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Restrictions</Label>
                  <Input
                    value={newVenueData.restrictions}
                    onChange={(e) => setNewVenueData({...newVenueData, restrictions: e.target.value})}
                    placeholder="e.g., No open flames, noise curfew 11PM"
                    className="bg-background border-border text-foreground"
                    data-testid="new-venue-restrictions-input"
                  />
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowAddVenueDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button onClick={saveNewVenue} className="bg-primary text-primary-foreground hover:bg-primary/80">
                    <Save className="w-4 h-4 mr-2" />
                    Add Venue & Use
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Add Payment Dialog */}
          <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
            <DialogContent className="bg-card border border-border max-w-md" data-testid="add-payment-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <DollarSign className="w-5 h-5 text-green-500" />
                  Add Payment
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-foreground">Amount ($) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPayment.amount}
                    onChange={(e) => setNewPayment({...newPayment, amount: e.target.value})}
                    placeholder="0.00"
                    className="bg-background border-border text-foreground"
                    data-testid="payment-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Payment Method *</Label>
                  <Select 
                    value={newPayment.payment_method} 
                    onValueChange={(v) => setNewPayment({...newPayment, payment_method: v})}
                  >
                    <SelectTrigger className="bg-background border-border text-foreground" data-testid="payment-method-select">
                      <SelectValue placeholder="Select payment method..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {paymentTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Payment Date</Label>
                  <Input
                    type="date"
                    value={newPayment.payment_date}
                    onChange={(e) => setNewPayment({...newPayment, payment_date: e.target.value})}
                    className="bg-background border-border text-foreground"
                    data-testid="payment-date-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Reference Number</Label>
                  <Input
                    value={newPayment.reference_number}
                    onChange={(e) => setNewPayment({...newPayment, reference_number: e.target.value})}
                    placeholder="Check #, transaction ID, etc."
                    className="bg-background border-border text-foreground"
                    data-testid="payment-reference-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-foreground">Notes</Label>
                  <textarea
                    value={newPayment.notes}
                    onChange={(e) => setNewPayment({...newPayment, notes: e.target.value})}
                    placeholder="Optional notes about this payment..."
                    className="w-full min-h-[80px] bg-background border border-border rounded-lg p-3 text-foreground"
                    data-testid="payment-notes-input"
                  />
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowPaymentDialog(false)} className="border-border">
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddPayment} 
                    className="bg-green-500 text-white hover:bg-green-600"
                    data-testid="save-payment-btn"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    Save Payment
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Form Editor Dialog */}
          <Dialog open={showFormDialog} onOpenChange={setShowFormDialog}>
            <DialogContent className="bg-card border border-border max-w-2xl max-h-[85vh] flex flex-col" data-testid="form-editor-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <ClipboardList className="w-5 h-5 text-neon-purple" />
                  {editingForm?.name}
                </DialogTitle>
                {editingForm?.description && (
                  <p className="text-sm text-muted-foreground">{editingForm.description}</p>
                )}
              </DialogHeader>
              
              <div className="flex-1 overflow-y-auto py-4 space-y-6">
                {editingForm?.fields?.map((field, idx) => (
                  <div key={idx} className="space-y-2">
                    {/* Section/Page Break - Display only */}
                    {field.type === 'section' ? (
                      <div className="py-4 my-2 border-t-2 border-dashed" style={{ borderColor: primaryColor + '60' }}>
                        <div className="flex items-center gap-2" style={{ color: primaryColor }}>
                          <span className="font-semibold text-lg">{field.label || 'New Section'}</span>
                        </div>
                        {field.placeholder && (
                          <p className="text-sm text-muted-foreground mt-1">{field.placeholder}</p>
                        )}
                      </div>
                    ) : field.type === 'comment' ? (
                      /* Comment/Note - Display only */
                      <div 
                        className="p-4 rounded-lg border"
                        style={{ 
                          backgroundColor: secondaryColor + '10',
                          borderColor: secondaryColor + '30'
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: secondaryColor }} />
                          <div>
                            <span className="font-medium" style={{ color: secondaryColor }}>{field.label}</span>
                            {field.placeholder && (
                              <p className="text-sm text-muted-foreground mt-1">{field.placeholder}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Regular input fields with label */
                      <>
                        <Label className="text-foreground">
                          {field.label}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                    
                    {field.type === 'text' && (
                      <Input
                        value={formResponses[field.name] || ''}
                        onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        className="bg-background"
                        style={{ borderColor: primaryColor + '40' }}
                      />
                    )}
                    
                    {field.type === 'textarea' && (
                      <textarea
                        value={formResponses[field.name] || ''}
                        onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        className="w-full min-h-[100px] px-3 py-2 rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2"
                        style={{ borderColor: primaryColor + '40', borderWidth: '1px', borderStyle: 'solid' }}
                        rows={4}
                      />
                    )}
                    
                    {field.type === 'select' && (
                      <Select
                        value={formResponses[field.name] || ''}
                        onValueChange={(value) => handleFormFieldChange(field.name, value)}
                      >
                        <SelectTrigger className="bg-background" style={{ borderColor: primaryColor + '40' }}>
                          <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options?.map((option, optIdx) => (
                            <SelectItem key={optIdx} value={option}>{option}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    
                    {field.type === 'checkbox' && (
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={formResponses[field.name] === 'yes' || formResponses[field.name] === true}
                          onCheckedChange={(checked) => handleFormFieldChange(field.name, checked ? 'yes' : 'no')}
                        />
                        <span className="text-sm text-muted-foreground">{field.placeholder || 'Yes'}</span>
                      </div>
                    )}
                    
                    {field.type === 'date' && (
                      <Input
                        type="date"
                        value={formResponses[field.name] || ''}
                        onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                        className="bg-background"
                        style={{ borderColor: primaryColor + '40' }}
                      />
                    )}
                    
                    {field.type === 'time' && (
                      <Input
                        type="time"
                        value={formResponses[field.name] || ''}
                        onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                        className="bg-background"
                        style={{ borderColor: primaryColor + '40' }}
                      />
                    )}
                    
                    {field.type === 'number' && (
                      <Input
                        type="number"
                        value={formResponses[field.name] || ''}
                        onChange={(e) => handleFormFieldChange(field.name, e.target.value)}
                        placeholder={field.placeholder || `Enter ${field.label.toLowerCase()}`}
                        className="bg-background"
                        style={{ borderColor: primaryColor + '40' }}
                      />
                    )}
                    
                    {field.type === 'repeater' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-xs font-medium text-muted-foreground px-1">
                          <span>{field.column1Label || 'Column 1'}</span>
                          <span>{field.column2Label || 'Column 2'}</span>
                        </div>
                        {(formResponses[field.name] || [{ col1: '', col2: '' }]).map((row, rowIdx) => (
                          <div key={rowIdx} className="flex gap-2 items-center">
                            <Input
                              value={row.col1 || ''}
                              onChange={(e) => handleRepeaterChange(field.name, rowIdx, 'col1', e.target.value)}
                              placeholder={field.column1Label}
                              className="flex-1 bg-background"
                              style={{ borderColor: primaryColor + '40' }}
                            />
                            <Input
                              value={row.col2 || ''}
                              onChange={(e) => handleRepeaterChange(field.name, rowIdx, 'col2', e.target.value)}
                              placeholder={field.column2Label}
                              className="flex-1 bg-background"
                              style={{ borderColor: primaryColor + '40' }}
                            />
                            {(formResponses[field.name] || []).length > 1 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeRepeaterRow(field.name, rowIdx)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => addRepeaterRow(field.name, false)}
                          className="w-full border-dashed"
                          style={{ borderColor: primaryColor + '40' }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Row
                        </Button>
                      </div>
                    )}

                    {field.type === 'repeater3' && (
                      <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3 text-xs font-medium text-muted-foreground px-1">
                          <span>{field.column1Label || 'Column 1'}</span>
                          <span>{field.column2Label || 'Column 2'}</span>
                          <span>{field.column3Label || 'Column 3'}</span>
                        </div>
                        {(formResponses[field.name] || [{ col1: '', col2: '', col3: '' }]).map((row, rowIdx) => (
                          <div key={rowIdx} className="flex gap-2 items-center">
                            <Input
                              value={row.col1 || ''}
                              onChange={(e) => handleRepeaterChange(field.name, rowIdx, 'col1', e.target.value)}
                              placeholder={field.column1Label}
                              className="flex-1 bg-background"
                              style={{ borderColor: primaryColor + '40' }}
                            />
                            <Input
                              value={row.col2 || ''}
                              onChange={(e) => handleRepeaterChange(field.name, rowIdx, 'col2', e.target.value)}
                              placeholder={field.column2Label}
                              className="flex-1 bg-background"
                              style={{ borderColor: primaryColor + '40' }}
                            />
                            <Input
                              value={row.col3 || ''}
                              onChange={(e) => handleRepeaterChange(field.name, rowIdx, 'col3', e.target.value)}
                              placeholder={field.column3Label}
                              className="flex-1 bg-background"
                              style={{ borderColor: primaryColor + '40' }}
                            />
                            {(formResponses[field.name] || []).length > 1 && (
                              <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                onClick={() => removeRepeaterRow(field.name, rowIdx)}
                                className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => addRepeaterRow(field.name, true)}
                          className="w-full border-dashed"
                          style={{ borderColor: primaryColor + '40' }}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Add Row
                        </Button>
                      </div>
                    )}

                    {field.type === 'columns2' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{field.column1Label || 'Column 1'}</Label>
                          {renderColumnInput(field, 'col1', field.column1Type || 'text', field.column1Options, (formResponses[field.name] || {}).col1)}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{field.column2Label || 'Column 2'}</Label>
                          {renderColumnInput(field, 'col2', field.column2Type || 'text', field.column2Options, (formResponses[field.name] || {}).col2)}
                        </div>
                      </div>
                    )}

                    {field.type === 'columns3' && (
                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{field.column1Label || 'Column 1'}</Label>
                          {renderColumnInput(field, 'col1', field.column1Type || 'text', field.column1Options, (formResponses[field.name] || {}).col1)}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{field.column2Label || 'Column 2'}</Label>
                          {renderColumnInput(field, 'col2', field.column2Type || 'text', field.column2Options, (formResponses[field.name] || {}).col2)}
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs text-muted-foreground">{field.column3Label || 'Column 3'}</Label>
                          {renderColumnInput(field, 'col3', field.column3Type || 'text', field.column3Options, (formResponses[field.name] || {}).col3)}
                        </div>
                      </div>
                    )}
                    
                    {field.helpText && (
                      <p className="text-xs text-muted-foreground">{field.helpText}</p>
                    )}
                      </>
                    )}
                  </div>
                ))}
              </div>
              
              <DialogFooter className="border-t border-border pt-4">
                <Button
                  variant="outline"
                  onClick={() => setShowFormDialog(false)}
                  className="border-border"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveFormResponse}
                  disabled={savingForm}
                  className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                  data-testid="save-form-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingForm ? 'Saving...' : 'Save Form'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Document Upload Dialog */}
          <Dialog open={showDocUploadDialog} onOpenChange={setShowDocUploadDialog}>
            <DialogContent className="bg-card border border-border max-w-md" data-testid="doc-upload-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2 text-foreground">
                  <Upload className="w-5 h-5 text-neon-blue" />
                  Upload Document
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* File Input */}
                <div className="space-y-2">
                  <Label>Select File</Label>
                  <div 
                    className="border-2 border-dashed border-border rounded-xl p-6 text-center cursor-pointer hover:border-neon-blue/50 transition-colors"
                    onClick={() => docFileInputRef.current?.click()}
                  >
                    <input
                      ref={docFileInputRef}
                      type="file"
                      onChange={handleDocFileSelect}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt"
                    />
                    {selectedDocFile ? (
                      <div className="space-y-2">
                        <File className="w-10 h-10 mx-auto text-neon-blue" />
                        <p className="font-medium text-foreground">{selectedDocFile.name}</p>
                        <p className="text-sm text-muted-foreground">{formatFileSize(selectedDocFile.size)}</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <Upload className="w-10 h-10 mx-auto text-muted-foreground" />
                        <p className="text-muted-foreground">Click to select a file</p>
                        <p className="text-xs text-muted-foreground">PDF, DOC, XLS, Images (max 10MB)</p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Note */}
                <div className="space-y-2">
                  <Label htmlFor="doc-note">Note (optional)</Label>
                  <Input
                    id="doc-note"
                    value={docNote}
                    onChange={(e) => setDocNote(e.target.value)}
                    placeholder="What is this document?"
                    className="bg-background border-border"
                  />
                  <p className="text-xs text-muted-foreground">Add a description to help identify this document</p>
                </div>
              </div>
              
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowDocUploadDialog(false);
                    setSelectedDocFile(null);
                    setDocNote('');
                  }}
                  className="border-border"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUploadDocument}
                  disabled={uploadingDoc || !selectedDocFile}
                  className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                  data-testid="submit-doc-upload-btn"
                >
                  {uploadingDoc ? 'Uploading...' : 'Upload Document'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Date Availability Warning Dialog */}
          <Dialog open={showAvailabilityWarning} onOpenChange={setShowAvailabilityWarning}>
            <DialogContent className="bg-card border border-border max-w-md" data-testid="availability-warning-dialog">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl text-foreground flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Date Not Available
                </DialogTitle>
                <DialogDescription className="text-muted-foreground pt-2">
                  {availabilityWarningData && (
                    <>
                      <p className="mb-2">
                        The date <span className="font-semibold text-foreground">{availabilityWarningData.date}</span> is not available.
                      </p>
                      {availabilityWarningData.reason && (
                        <p className="text-yellow-400 text-sm mb-3">{availabilityWarningData.reason}</p>
                      )}
                      <p>Would you like to continue anyway?</p>
                    </>
                  )}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter className="mt-4 flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowAvailabilityWarning(false);
                    setAvailabilityWarningData(null);
                  }}
                  className="border-border"
                  data-testid="availability-cancel-btn"
                >
                  Choose Different Date
                </Button>
                <Button
                  onClick={() => {
                    setShowAvailabilityWarning(false);
                    setAvailabilityWarningData(null);
                    handleCreate(true); // Force create
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700"
                  data-testid="availability-continue-btn"
                >
                  Continue Anyway
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Expense Dialog */}
          <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-500" />
                  {editingExpense ? 'Edit Expense' : 'Add Expense'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Category</Label>
                  <Select
                    value={expenseData.category}
                    onValueChange={(value) => setExpenseData({ ...expenseData, category: value })}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPENSE_CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    value={expenseData.description}
                    onChange={(e) => setExpenseData({ ...expenseData, description: e.target.value })}
                    placeholder="e.g., DJ Assistant - John Smith"
                    className="bg-muted border-border"
                    data-testid="expense-description-input"
                  />
                </div>
                <div>
                  <Label>Amount *</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      type="number"
                      value={expenseData.amount}
                      onChange={(e) => setExpenseData({ ...expenseData, amount: e.target.value })}
                      placeholder="0.00"
                      className="bg-muted border-border pl-7"
                      step="0.01"
                      min="0"
                      data-testid="expense-amount-input"
                    />
                  </div>
                </div>
                <div>
                  <Label>Date (Optional)</Label>
                  <Input
                    type="date"
                    value={expenseData.date}
                    onChange={(e) => setExpenseData({ ...expenseData, date: e.target.value })}
                    className="bg-muted border-border"
                    data-testid="expense-date-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowExpenseDialog(false)}>Cancel</Button>
                <Button onClick={saveExpense} className="bg-purple-600 hover:bg-purple-700" data-testid="save-expense-btn">
                  {editingExpense ? 'Update' : 'Add'} Expense
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Add Form Dialog */}
          <Dialog open={showAddFormDialog} onOpenChange={setShowAddFormDialog}>
            <DialogContent className="bg-card border-border max-w-md">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-neon-blue" />
                  Add Form to Booking
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <p className="text-sm text-muted-foreground">
                  Select a form to manually attach to this booking. The client will be able to fill it out in their portal.
                </p>
                {getAvailableFormsToAdd().length > 0 ? (
                  <div className="space-y-2">
                    <Label>Select Form</Label>
                    <Select
                      value={selectedFormToAdd}
                      onValueChange={setSelectedFormToAdd}
                    >
                      <SelectTrigger className="bg-muted border-border">
                        <SelectValue placeholder="Choose a form..." />
                      </SelectTrigger>
                      <SelectContent>
                        {getAvailableFormsToAdd().map((form) => (
                          <SelectItem key={form.id} value={form.id}>
                            {form.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>All available forms are already attached to this booking.</p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setShowAddFormDialog(false); setSelectedFormToAdd(''); }}>
                  Cancel
                </Button>
                <Button 
                  onClick={addManualForm} 
                  disabled={!selectedFormToAdd}
                  className="bg-neon-blue hover:bg-neon-blue/80 text-black"
                  data-testid="confirm-add-form-btn"
                >
                  Add Form
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Cancellation Confirmation Dialog */}
          {/* Cancel Booking Dialog */}
          <CancelBookingDialog
            open={showCancelDialog}
            onOpenChange={setShowCancelDialog}
            booking={cancellingBooking}
            cancellationReason={cancellationReason}
            onReasonChange={setCancellationReason}
            onConfirm={confirmCancellation}
            onCancel={() => {
              setShowCancelDialog(false);
              setCancellingBooking(null);
              setCancellationReason('');
            }}
          />

          {/* Invoice Preview Dialog */}
          <Dialog open={showInvoicePreview} onOpenChange={setShowInvoicePreview}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-glass-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-neon-blue" />
                  Invoice Preview - Review & Edit Before Sending
                </DialogTitle>
              </DialogHeader>
              
              {invoicePreviewData && (
                <div className="space-y-6">
                  {/* Client Info */}
                  <div className="grid md:grid-cols-2 gap-4 p-4 rounded-xl bg-muted/20 border border-border">
                    <div>
                      <Label className="text-xs text-muted-foreground">Client</Label>
                      <p className="font-semibold">{invoicePreviewData.client_name}</p>
                      <p className="text-sm text-muted-foreground">{invoicePreviewData.client_email}</p>
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Event</Label>
                      <p className="font-semibold">{invoicePreviewData.event_type}</p>
                      <p className="text-sm text-muted-foreground">{invoicePreviewData.event_date} at {invoicePreviewData.venue}</p>
                    </div>
                  </div>

                  {/* Template Info */}
                  {invoiceTemplate && (
                    <div className="p-3 rounded-lg bg-neon-blue/10 border border-neon-blue/30">
                      <p className="text-sm">
                        <span className="text-muted-foreground">Using template:</span>{' '}
                        <span className="font-medium text-neon-blue">{invoiceTemplate.name}</span>
                      </p>
                    </div>
                  )}

                  {/* Editable Fields */}
                  <div className="space-y-4">
                    <div>
                      <Label>Invoice Description</Label>
                      <Input
                        value={editableInvoice.description}
                        onChange={(e) => setEditableInvoice(prev => ({ ...prev, description: e.target.value }))}
                        className="bg-muted/20 border-glass-border"
                        data-testid="invoice-description"
                      />
                    </div>
                    
                    <div>
                      <Label>Due Date</Label>
                      <Input
                        type="date"
                        value={editableInvoice.due_date}
                        onChange={(e) => setEditableInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                        className="bg-muted/20 border-glass-border"
                        data-testid="invoice-due-date"
                      />
                    </div>

                    {/* Line Items */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label>Line Items</Label>
                        <Button size="sm" variant="outline" onClick={addLineItem} className="border-glass-border">
                          <Plus className="w-4 h-4 mr-1" />
                          Add Item
                        </Button>
                      </div>
                      <div className="space-y-2">
                        {editableInvoice.line_items.map((item, index) => (
                          <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                            <div className="flex-1">
                              <Input
                                value={item.description}
                                onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                placeholder="Description"
                                className="bg-background/50 border-glass-border"
                              />
                            </div>
                            <div className="w-32">
                              <Input
                                type="number"
                                value={item.amount}
                                onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                                placeholder="Amount"
                                className="bg-background/50 border-glass-border text-right"
                              />
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              onClick={() => removeLineItem(index)}
                              className="text-red-500 hover:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <Label>Notes (Saved to this invoice only, not the template)</Label>
                      <Textarea
                        value={editableInvoice.notes}
                        onChange={(e) => setEditableInvoice(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Add any custom notes for this client..."
                        className="bg-muted/20 border-glass-border min-h-[100px]"
                        data-testid="invoice-notes"
                      />
                    </div>
                  </div>

                  {/* Total */}
                  <div className="p-4 rounded-xl bg-neon-purple/10 border border-neon-purple/30">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold">Total Amount</span>
                      <span className="text-2xl font-bold text-neon-purple">${editableInvoice.amount?.toFixed(2) || '0.00'}</span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setShowInvoicePreview(false)} className="border-glass-border">
                      Cancel
                    </Button>
                    <Button onClick={handleSendInvoice} className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="send-invoice-btn">
                      <FileText className="w-4 h-4 mr-2" />
                      Create Invoice
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Edit Invoice Dialog */}
          <Dialog open={showEditInvoiceDialog} onOpenChange={setShowEditInvoiceDialog}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card border-glass-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-5 h-5 text-neon-blue" />
                  Edit Invoice - {editableInvoice.invoice_number}
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                {/* Invoice Description */}
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editableInvoice.description}
                    onChange={(e) => setEditableInvoice(prev => ({ ...prev, description: e.target.value }))}
                    className="bg-muted/20 border-glass-border"
                  />
                </div>
                
                {/* Due Date */}
                <div>
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={editableInvoice.due_date}
                    onChange={(e) => setEditableInvoice(prev => ({ ...prev, due_date: e.target.value }))}
                    className="bg-muted/20 border-glass-border"
                  />
                </div>

                {/* Line Items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label>Line Items</Label>
                    <Button size="sm" variant="outline" onClick={addLineItem} className="border-glass-border">
                      <Plus className="w-4 h-4 mr-1" />
                      Add Item
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {editableInvoice.line_items?.map((item, index) => (
                      <div key={index} className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border">
                        <div className="flex-1">
                          <Input
                            value={item.description}
                            onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                            placeholder="Description"
                            className="bg-background/50 border-glass-border"
                          />
                        </div>
                        <div className="w-32">
                          <Input
                            type="number"
                            value={item.amount}
                            onChange={(e) => updateLineItem(index, 'amount', e.target.value)}
                            placeholder="Amount"
                            className="bg-background/50 border-glass-border text-right"
                          />
                        </div>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => removeLineItem(index)}
                          className="text-red-500 hover:text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={editableInvoice.notes || ''}
                    onChange={(e) => setEditableInvoice(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Add notes..."
                    className="bg-muted/20 border-glass-border min-h-[80px]"
                  />
                </div>

                {/* Total */}
                <div className="p-4 rounded-xl bg-neon-blue/10 border border-neon-blue/30">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Amount</span>
                    <span className="text-2xl font-bold text-neon-blue">${editableInvoice.amount?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowEditInvoiceDialog(false)} className="border-glass-border">
                    Cancel
                  </Button>
                  <Button onClick={saveEventInvoice} className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="save-edited-invoice-btn">
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Email Invoice Dialog */}
          <Dialog open={showEmailInvoiceDialog} onOpenChange={setShowEmailInvoiceDialog}>
            <DialogContent className="max-w-md bg-card border-glass-border">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-neon-blue" />
                  Email Invoice
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Send invoice {selectedEventInvoice?.invoice_number} to:
                </p>
                
                <div>
                  <Label>Email Address</Label>
                  <Input
                    type="email"
                    value={invoiceEmailAddress}
                    onChange={(e) => setInvoiceEmailAddress(e.target.value)}
                    placeholder="Enter email address"
                    className="bg-muted/20 border-glass-border"
                    data-testid="invoice-email-address"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowEmailInvoiceDialog(false)} className="border-glass-border">
                    Cancel
                  </Button>
                  <Button 
                    onClick={sendEventInvoiceEmail} 
                    disabled={sendingInvoiceEmail || !invoiceEmailAddress.trim()}
                    className="bg-neon-blue hover:bg-neon-blue/80 text-black" 
                    data-testid="send-invoice-email-btn"
                  >
                    {sendingInvoiceEmail ? (
                      <>
                        <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin mr-2"></div>
                        Sending...
                      </>
                    ) : (
                      <>
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Send Invoice
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Counter-Sign Contract Dialog */}
          {/* Counter Sign Dialog */}
          <CounterSignDialog
            open={showCounterSignDialog}
            onOpenChange={setShowCounterSignDialog}
            contract={selectedContractForSigning}
            signerName={counterSignerName}
            onSignerNameChange={setCounterSignerName}
            onConfirm={handleCounterSign}
            signing={counterSigning}
          />

          {/* Email Compose Dialog */}
          <EmailComposeDialog
            open={showEmailDialog}
            onOpenChange={setShowEmailDialog}
            recipient={emailRecipient}
            variables={{
              client_name: emailRecipient?.name || '',
              client_email: emailRecipient?.email || '',
              company_name: user?.contact_name || '',
              event_date: eventDetailsBooking?.event_date ? formatDate(eventDetailsBooking.event_date) : '',
              event_time: eventDetailsBooking?.start_time || '',
              event_type: eventDetailsBooking?.event_type || '',
              venue_name: eventDetailsBooking?.venue_name || '',
              venue_address: eventDetailsBooking?.venue_address || '',
              total_amount: eventDetailsBooking?.total_amount?.toFixed(2) || '0.00',
              amount_paid: eventDetailsBooking?.amount_paid?.toFixed(2) || '0.00',
              balance_due: ((eventDetailsBooking?.total_amount || 0) - (eventDetailsBooking?.amount_paid || 0)).toFixed(2)
            }}
            onEmailSent={() => setCommunicationsRefresh(prev => prev + 1)}
          />

          {/* Generate Contract Dialog */}
          <Dialog open={showGenerateContractDialog} onOpenChange={setShowGenerateContractDialog}>
            <DialogContent className="glass-card border-glass-border sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl">Generate Contract</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Select Contract Template</Label>
                  {contractTemplates.length > 0 ? (
                    <Select
                      value={selectedContractTemplate}
                      onValueChange={setSelectedContractTemplate}
                    >
                      <SelectTrigger className="bg-background border-border" data-testid="contract-template-select">
                        <SelectValue placeholder="Choose a template..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {contractTemplates.map((template) => (
                          <SelectItem key={template.id} value={template.id}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground">
                      <p className="text-sm">No contract templates found.</p>
                      <p className="text-xs mt-1">Create templates in Configuration → Contract Templates</p>
                    </div>
                  )}
                </div>
                
                {eventDetailsBooking && (
                  <div className="p-3 rounded-lg bg-muted/30 border border-border">
                    <p className="text-sm text-muted-foreground mb-1">Creating contract for:</p>
                    <p className="font-medium">{eventDetailsBooking.client_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {eventDetailsBooking.event_type} • {eventDetailsBooking.event_date}
                    </p>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowGenerateContractDialog(false);
                    setSelectedContractTemplate('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleGenerateContract}
                  disabled={!selectedContractTemplate || generatingContract}
                  className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                  data-testid="confirm-generate-contract-btn"
                >
                  {generatingContract ? 'Generating...' : 'Generate Contract'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Remove Form Confirmation Dialog */}
          <AlertDialog open={showRemoveFormDialog} onOpenChange={setShowRemoveFormDialog}>
            <AlertDialogContent className="glass-card border-glass-border">
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  {formHasData && <AlertTriangle className="w-5 h-5 text-amber-500" />}
                  Remove Form from Booking?
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {formHasData ? (
                    <div className="space-y-2">
                      <p className="text-amber-500 font-medium">
                        ⚠️ Warning: This form has submitted data!
                      </p>
                      <p>
                        Removing "{formToRemove?.name}" will also delete any responses that have been submitted. This action cannot be undone.
                      </p>
                    </div>
                  ) : (
                    <p>
                      Are you sure you want to remove "{formToRemove?.name}" from this booking?
                    </p>
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={confirmRemoveForm}
                  className={formHasData ? "bg-red-600 hover:bg-red-700 text-white" : "bg-neon-blue hover:bg-neon-blue/80 text-black"}
                >
                  {formHasData ? 'Delete Form & Data' : 'Remove Form'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
    </Layout>
  );
}